// Internationalization system
// UI translations for all supported native languages

export interface Translations {
  // Navigation
  nav: {
    dialogues: string;
    grammar: string;
    practice: string;
    alphabet: string;
  };
  // Language selector
  languageSelector: {
    title: string;
    iSpeak: string;
    iWantToLearn: string;
    done: string;
    back: string;
    moreComingSoon: string;
  };
  // Topic selector
  topics: {
    title: string;
    subtitle: string;
    tagline: string;
    heroTitle: string;
    heroSubtitle: string;
    createCustom: string;
    chooseTopic: string;
    available: string;
    words: string;
    expressions: string;
    difficulty: {
      beginner: string;
      intermediate: string;
      advanced: string;
    };
    dialogNodes: string;
    regenerateWarning: string;
    deleteTitle: string;
    deleteDescription: string;
    regenerateTitle: string;
    regenerateDescription: string;
    regenerate: string;
    edit: string;
    recent: string;
    categories: string;
    byCategory: string;
    collapse: string;
  };
  // Grammar section
  grammar: {
    title: string;
    subtitle: string;
    noRulesTitle: string;
    noRulesDescription: string;
    rulesCount: string;
    rulesSingular: string;
    tapToExpand: string;
    examples: string;
    howToThink: string;
    commonMistake: string;
  };
  // Alphabet section
  alphabet: {
    title: string;
    subtitle: string;
    comingSoonTitle: string;
    comingSoonDescription: string;
    examples: string;
    all: string;
  };
  // Custom topic generator
  generator: {
    title: string;
    placeholder: string;
    proficiency: string;
    generate: string;
    generating: string;
    loadTopic: string;
    back: string;
    askAI: string;
    responses: string;
    // Model tiers
    modelTierFast: string;
    modelTierFastDesc: string;
    modelTierBalanced: string;
    modelTierBalancedDesc: string;
    modelTierPremium: string;
    modelTierPremiumDesc: string;
    loadingModels: string;
    llmConfigWarning: string;
    // Topic step
    topicTitle: string;
    topicHintMic: string;
    topicHintKeyboard: string;
    exampleTapToUse: string;
    enterText: string;
    tapToStop: string;
    processing: string;
    tapAndTalk: string;
    recording: string;
    useVoice: string;
    attach: string;
    continue: string;
    apiKeyRequired: string;
    apiKeyRequiredDesc: string;
    // Difficulty step
    difficultyTitle: string;
    difficultySubtitle: string;
    styleBeginner: string;
    styleBeginnerDesc: string;
    styleLocal: string;
    styleLocalDesc: string;
    styleMix: string;
    styleMixDesc: string;
    styleCustomPlaceholder: string;
    // Richness step
    richnessTitle: string;
    richnessSubtitle: string;
    richnessQuick: string;
    richnessQuickDesc: string;
    richnessStandard: string;
    richnessStandardDesc: string;
    richnessComprehensive: string;
    richnessComprehensiveDesc: string;
    // Model step
    modelTitle: string;
    modelSubtitle: string;
    cachedAvailable: string;
    loadCached: string;
    previewPrompt: string;
    fullPrompt: string;
    copied: string;
    systemPrompt: string;
    userPrompt: string;
    // Generating step
    gettingReady: string;
    connectingToAI: string;
    done: string;
    letsGo: string;
    goBack: string;
    playWhileWaiting: string;
    play: string;
    or: string;
    whileYouWait: string;
    exitGame: string;
    // Sample scenarios for inspiration
    sampleScenarios: string[];
  };
  // PWA
  pwa: {
    installPrompt: string;
    install: string;
  };
  // Settings
  settings: {
    title: string;
    theme: string;
    themeLight: string;
    themeDark: string;
    themeSystem: string;
    resetAll: string;
    resetConfirm: string;
    close: string;
    // Speech recognition
    speechRecognition: string;
    sttAzure: string;
    sttAzureDesc: string;
    sttBrowser: string;
    sttBrowserDesc: string;
    // Speech speed
    speechSpeed: string;
    // Analytics
    analytics: string;
    shareUsageData: string;
    analyticsDescription: string;
    // Known languages
    knownLanguages: string;
    knownLanguagesDescription: string;
    noneSelected: string;
    // Learning settings
    learning: string;
    showPronunciationHints: string;
    showPronunciationHintsDescription: string;
  };
  // Learning view
  learning: {
    back: string;
    dialogues: string;
    vocabulary: string;
    grammar: string;
    practice: string;
    cards: string;
    listen: string;
    showTranslation: string;
    hideTranslation: string;
    // Vocabulary section
    pronunciation: string;
    sayItLike: string;
    allForms: string;
    example: string;
    inContext: string;
    literalMeaning: string;
    wordBreakdown: string;
    fromDialog: string;
    searchVocabulary: string;
    foundResults: string;
    progress: string;
    mastered: string;
    words: string;
    expressions: string;
    topical: string;
    wordsDescription: string;
    expressionsDescription: string;
    topicalDescription: string;
    noWordsMatch: string;
    noExpressionsMatch: string;
    noTopicalMatch: string;
    forms: string;
    buildingBlocks: string;
    showBuildingBlocks: string;
    buildingBlocksDescription: string;
    hideByDefault: string;
    // Phrases section
    longPressHint: string;
    hideAll: string;
    showAll: string;
    tapToReveal: string;
    // Practice mode
    practiceMode: string;
    practiceDescription: string;
    howItWorks: string;
    practiceStep1: string;
    practiceStep2: string;
    practiceStep3: string;
    startPractice: string;
    greatJob: string;
    keepPracticing: string;
    correctResponses: string;
    yourConversation: string;
    tryAgain: string;
    newPath: string;
    turn: string;
    score: string;
    chooseResponse: string;
    continueConversation: string;
    seeResults: string;
    // Dialog tree
    conversationStarts: string;
    you: string;
    them: string;
    end: string;
    alsoSay: string;
    playing: string;
    askAI: string;
    response: string;
    responses: string;
    or: string;
    systemResponse: string;
    yourOptions: string;
    tapToExpand: string;
    startAgain: string;
    pickYourAnswer: string;
    pickYourAnswerHint: string;
    tapToAnswer: string;
    pickTheirAnswer: string;
    tapToStart: string;
    transcribing: string;
    useVoice: string;
    // Custom phrase
    saySomethingElse: string;
    said: string;
    whatToSay: string;
    typeYourResponse: string;
    customPhraseHint: string;
    // Fix response
    fixThisResponse: string;
    whatsWrong: string;
    fixResponseHint: string;
    // Practice tab modes
    practiceTab: string;
    practiceWords: string;
    practiceSpelling: string;
    practiceKaraoke: string;
    practiceWordsDesc: string;
    practiceSpellingDesc: string;
    practiceKaraokeDesc: string;
    introduceWord: string;
    composeWord: string;
    describeImage: string;
    pickWordForSentence: string;
    iNeedAHint: string;
    iDontKnow: string;
    tapHoldSayWord: string;
    tapHoldSayPhrase: string;
    weHeard: string;
    skipWord: string;
    nextWord: string;
    practiceComplete: string;
    wordsCorrectCount: string;
    practiceAgain: string;
    done: string;
    correct: string;
    // Module selector & word state
    chooseModule: string;
    moduleHint: string;
    wordsCount: string;
    markKnown: string;
    markTough: string;
    markLiked: string;
    knownWordsHidden: string;
    // Global practice empty state
    noWordsToReview: string;
    noWordsToReviewDesc: string;
    generatingMore: string;
  };
  // Common
  common: {
    loading: string;
    error: string;
    retry: string;
    cancel: string;
    save: string;
    delete: string;
    edit: string;
    close: string;
    send: string;
    copy: string;
  };
  // Mini-games (loading screen entertainment)
  games: {
    score: string;
    best: string;
    hi: string;
    tapOn: string;
    getReady: string;
    tapToStart: string;
    tapToJump: string;
    gameOver: string;
    tapToRestart: string;
    // Speed level names
    speedSlow: string;
    speedSteady: string;
    speedNormal: string;
    speedQuick: string;
    speedFast: string;
    speedBlazing: string;
    speedOnFire: string;
    speedRocket: string;
    speedLegendary: string;
    // Cheers
    cheers: string[];
  };
  // Errors
  errors: {
    microphoneAccessDenied: string;
    recognitionError: string;
    recordingFailed: string;
    noSpeechDetected: string;
    llmConfigRequired: string;
  };
  // Onboarding
  onboarding: {
    // Welcome screen
    welcomeTitle: string;
    welcomeSubtitle: string;
    getStarted: string;
    // Feature slides
    featureTopicsPain: string;
    featureTopicsSolution: string;
    featureTopicsDesc: string;
    featureVoicePain: string;
    featureVoiceSolution: string;
    featureVoiceDesc: string;
    featureAiPain: string;
    featureAiSolution: string;
    featureAiDesc: string;
    continue: string;
    // Input: Native Language
    inputNativeLangTitle: string;
    inputNativeLangSubtitle: string;
    // Input: Target Language
    inputTargetLangTitle: string;
    inputTargetLangSubtitle: string;
    // Input: Name
    inputNameTitle: string;
    inputNameSubtitle: string;
    inputNamePlaceholder: string;
    // Input: Languages
    inputLangTitle: string;
    inputLangSubtitle: string;
    inputLangSelect: string;
    inputLangFindMore: string;
    inputLangSearch: string;
    skipStep: string;
    // Input: Style
    inputStyleTitle: string;
    inputStyleSubtitle: string;
    speakingStyleBeginner: string;
    speakingStyleBeginnerDesc: string;
    speakingStyleLocal: string;
    speakingStyleLocalDesc: string;
    speakingStyleMix: string;
    speakingStyleMixDesc: string;
    styleYourOption: string;
    styleTypePlaceholder: string;
    // Input: Gender
    inputGenderTitle: string;
    inputGenderSubtitle: string;
    genderMale: string;
    genderFemale: string;
    genderOther: string;
    genderPreferNot: string;
    // Navigation
    back: string;
    skipAll: string;
    // Ready screen
    startLearning: string;
    youreAllSet: string;
    youreAllSetWithName: string;
    startFirstConversation: string;
    analyticsOptIn: string;
    // First conversation transition screen
    firstConversationTitle: string;
    firstConversationSubtitle: string;
    firstConversationButton: string;
    // Preview UI elements
    previewTopicTitle: string;
    previewTopicSubtitle: string;
    previewExample: string;
    previewExampleText: string;
    previewTypeOwn: string;
    previewVoiceText: string;
    previewDialogTranslation: string;
  };
  // Known languages (for onboarding language picker)
  knownLanguages: {
    spanish: string;
    french: string;
    german: string;
    italian: string;
    portuguese: string;
    russian: string;
    chinese: string;
    japanese: string;
    korean: string;
    arabic: string;
    hindi: string;
    dutch: string;
    polish: string;
    swedish: string;
    turkish: string;
    greek: string;
    czech: string;
    romanian: string;
    hungarian: string;
    finnish: string;
    norwegian: string;
    danish: string;
    ukrainian: string;
    thai: string;
    vietnamese: string;
    indonesian: string;
    malay: string;
    tagalog: string;
    hebrew: string;
    persian: string;
    bengali: string;
    punjabi: string;
    tamil: string;
    urdu: string;
    swahili: string;
    afrikaans: string;
    catalan: string;
    croatian: string;
    serbian: string;
    slovak: string;
    slovenian: string;
    bulgarian: string;
    lithuanian: string;
    latvian: string;
    estonian: string;
    icelandic: string;
    welsh: string;
    irish: string;
    scottishGaelic: string;
    basque: string;
    maltese: string;
    albanian: string;
    macedonian: string;
    georgian: string;
    armenian: string;
    kazakh: string;
    uzbek: string;
    mongolian: string;
    nepali: string;
    sinhala: string;
    burmese: string;
    khmer: string;
    lao: string;
  };
  // Coach marks walkthrough
  coachMarks: {
    startNodeTitle: string;
    startNodeBody: string;
    speakerTitle: string;
    speakerBody: string;
    answerTitle: string;
    answerBody: string;
    tabsTitle: string;
    tabsBody: string;
    skip: string;
    next: string;
    done: string;
  };
  // Password gate
  passwordGate: {
    enterPassword: string;
    placeholder: string;
  };
  // Dialogue-level feedback
  dialogueFeedback: {
    title: string;
    bad: string;
    meh: string;
    good: string;
    thanks: string;
    tellUsWhy: string;
    commentPlaceholder: string;
    skip: string;
    send: string;
  };
  // Dialog interactions
  dialog: {
    listening: string;
    stopRecording: string;
    practice: string;
    vocabulary: string;
    loadingVocabulary: string;
  };
  // Language names (translated)
  languageNames: {
    "en-US": string;
    "en-GB": string;
    "es-ES": string;
    "es-MX": string;
    "pt-PT": string;
    "pt-BR": string;
    "fr-FR": string;
    "de-DE": string;
    "de-AT": string;
    "de-CH": string;
    "it-IT": string;
    "ja-JP": string;
    "zh-CN": string;
    "ko-KR": string;
    "he-IL": string;
    "ru-RU": string;
    "pl-PL": string;
    "uk-UA": string;
    "fi-FI": string;
    "et-EE": string;
    "lv-LV": string;
    "el-GR": string;
  };
}

// English (default)
const en: Translations = {
  nav: {
    dialogues: "Dialogues",
    grammar: "Grammar",
    practice: "Practice",
    alphabet: "Alphabet",
  },
  languageSelector: {
    title: "Languages",
    iSpeak: "I speak",
    iWantToLearn: "I want to learn",
    done: "Done",
    back: "Back",
    moreComingSoon: "More languages coming soon!",
  },
  topics: {
    title: "Dialogue Topics",
    subtitle: "Choose a conversation to practice",
    tagline: "Learn to say what you need",
    heroTitle: "Be ready for any conversation",
    heroSubtitle: "Finally say what you're thinking",
    createCustom: "New conversation",
    chooseTopic: "Choose a Topic",
    available: "available",
    words: "words",
    expressions: "expressions",
    difficulty: {
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
    },
    dialogNodes: "turns",
    regenerateWarning: "Made for a different language — tap to recreate",
    deleteTitle: "Delete Topic",
    deleteDescription: "Are you sure you want to delete this topic? This action cannot be undone.",
    regenerateTitle: "Regenerate Dialogue",
    regenerateDescription: "This will create a new dialogue for this topic. The current dialogue will be replaced.",
    regenerate: "Regenerate",
    edit: "Edit",
    recent: "Recent",
    categories: "Categories",
    byCategory: "Dialogues by category",
    collapse: "Collapse",
  },
  grammar: {
    title: "Grammar Rules",
    subtitle: "{language} basics",
    noRulesTitle: "No Grammar Rules Yet",
    noRulesDescription: "Create a custom topic in {language} to see grammar rules here. Grammar is extracted from the topics you generate.",
    rulesCount: "{count} grammar rules",
    rulesSingular: "{count} grammar rule",
    tapToExpand: "Tap to expand and learn the patterns.",
    examples: "Examples from Dialog",
    howToThink: "How to Think About It",
    commonMistake: "Common Mistake",
  },
  alphabet: {
    title: "Sounds & Letters",
    subtitle: "{language} pronunciation",
    comingSoonTitle: "Coming Soon",
    comingSoonDescription: "Alphabet and pronunciation guide for {language} is not yet available.",
    examples: "Examples",
    all: "All",
  },
  generator: {
    title: "What do you want to talk about?",
    placeholder: "Describe a situation...\n\nExamples:\n• Ordering coffee at a café\n• Asking for directions\n• Making a doctor's appointment",
    proficiency: "How do you want to sound?",
    generate: "Let's go",
    generating: "Getting things ready...",
    loadTopic: "Load",
    back: "Back",
    askAI: "Describe your scenario",
    responses: "responses",
    modelTierFast: "Fast",
    modelTierFastDesc: "Quick & affordable",
    modelTierBalanced: "Balanced",
    modelTierBalancedDesc: "Best value",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Highest quality",
    loadingModels: "Loading models...",
    llmConfigWarning: "Configure your LLM provider in settings to enable AI generation",
    // Topic step
    topicTitle: "What would you like to talk about?",
    topicHintMic: "Tap the mic and speak your topic",
    topicHintKeyboard: "Type a scenario or topic",
    exampleTapToUse: "Example — tap to use",
    enterText: "Enter text",
    tapToStop: "Tap to stop",
    processing: "Processing...",
    tapAndTalk: "Tap and talk",
    recording: "Recording...",
    useVoice: "Use voice",
    attach: "Attach",
    continue: "Continue",
    apiKeyRequired: "API key required",
    apiKeyRequiredDesc: "Configure your LLM provider in settings to enable AI generation",
    // Difficulty step
    difficultyTitle: "How do you want to sound?",
    difficultySubtitle: "Choose your speaking style",
    styleBeginner: "Beginner",
    styleBeginnerDesc: "Simple words and phrases — sound like a beginner",
    styleLocal: "Like a local",
    styleLocalDesc: "Natural speech, slang, idioms — sound like a native",
    styleMix: "Mix it up",
    styleMixDesc: "Pick one or more",
    styleCustomPlaceholder: "Or type your own...",
    // Richness step
    richnessTitle: "How much content?",
    richnessSubtitle: "Choose the amount of vocabulary and dialog",
    richnessQuick: "Quick",
    richnessQuickDesc: "Short dialog, essential vocabulary",
    richnessStandard: "Standard",
    richnessStandardDesc: "Medium dialog, good vocabulary coverage",
    richnessComprehensive: "Comprehensive",
    richnessComprehensiveDesc: "Long dialog, extensive vocabulary",
    // Model step
    modelTitle: "Choose quality level",
    modelSubtitle: "Better quality costs more",
    cachedAvailable: "Cached response available — instant load!",
    loadCached: "Load Cached",
    previewPrompt: "Preview Prompt",
    fullPrompt: "Full Prompt",
    copied: "Copied!",
    systemPrompt: "System Prompt",
    userPrompt: "User Prompt",
    // Generating step
    gettingReady: "Getting things ready...",
    connectingToAI: "Connecting to AI...",
    done: "Done!",
    letsGo: "Let's go",
    goBack: "Go Back",
    playWhileWaiting: "Play {game1} or {game2} while you wait!",
    play: "Play",
    or: "or",
    whileYouWait: "while you wait",
    exitGame: "Exit game",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "I ordered tires online at a tire shop and they've been delivered. I need to call and make an appointment to get them installed on my car this week.",
      "I'm at a specialty coffee shop and want to order a flat white with oat milk and a pastry. I also want to ask if they have any single-origin beans to buy.",
      "I'm at the train station and need to get to my destination. I want to know which platform, how much a return ticket costs, and if I can pay by card.",
      "I'm at a go-kart centre with friends. We want to book a 20-minute session for 4 people and ask about any group discounts.",
      "My phone screen is cracked and I'm at a repair shop. I want to ask how long it takes to fix, how much it costs, and if they have a warranty.",
      "I'm at the pharmacy and need something for a bad headache and also some allergy medicine. I want to ask which one is strongest and if I need a prescription.",
      "I'm checking into an Airbnb and the key isn't where the host said it would be. I need to call them and figure out how to get in.",
      "I'm at a seafood restaurant by the beach and want to order grilled fish, but I want to know what's fresh today and ask for recommendations.",
      "I'm at a surf school and want to book a beginner lesson for tomorrow morning. I need to ask about wetsuit rental and what time to arrive.",
      "My rental car has a flat tire on the highway. I need to call roadside assistance and explain where I am and what happened.",
    ],
  },
  pwa: {
    installPrompt: "Install SaySay for a better experience",
    install: "Install",
  },
  settings: {
    title: "Settings",
    theme: "Appearance",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "Auto",
    resetAll: "Reset All Local Data",
    resetConfirm: "This will clear all saved settings, topics, and preferences. Are you sure?",
    close: "Close",
    speechRecognition: "Speech Recognition",
    sttAzure: "Azure",
    sttAzureDesc: "More accurate",
    sttBrowser: "Browser",
    sttBrowserDesc: "Works offline",
    speechSpeed: "Speech Speed",
    analytics: "Analytics",
    shareUsageData: "Share usage data",
    analyticsDescription: "Help improve SaySay by sharing anonymous usage data",
    knownLanguages: "Known Languages",
    knownLanguagesDescription: "Select languages you already know. This helps us provide better pronunciation tips using sounds from these languages.",
    noneSelected: "None selected",
    learning: "Learning",
    showPronunciationHints: "Show pronunciation hints",
    showPronunciationHintsDescription: "Display how to say phrases using sounds you know",
  },
  learning: {
    back: "Back",
    dialogues: "Dialogue",
    vocabulary: "Vocabulary",
    grammar: "Grammar",
    practice: "Practice",
    cards: "Cards",
    listen: "Listen",
    showTranslation: "Show translation",
    hideTranslation: "Hide translation",
    pronunciation: "Pronunciation",
    sayItLike: "Say it like",
    allForms: "All Forms",
    example: "Example",
    inContext: "In Context",
    literalMeaning: "Literal Meaning",
    wordBreakdown: "Word Breakdown",
    fromDialog: "From Dialog",
    searchVocabulary: "Search vocabulary...",
    foundResults: "Found {count} results for \"{query}\"",
    progress: "Progress",
    mastered: "{count} / {total} mastered",
    words: "Words",
    expressions: "Expressions",
    topical: "Nice to Know",
    wordsDescription: "Core vocabulary with all forms",
    expressionsDescription: "Phrases and idioms",
    topicalDescription: "Related vocabulary",
    noWordsMatch: "No words match your search",
    noExpressionsMatch: "No expressions match your search",
    noTopicalMatch: "No related vocabulary matches your search",
    forms: "Forms",
    buildingBlocks: "Building Blocks",
    showBuildingBlocks: "Show building blocks",
    buildingBlocksDescription: "Common words you need to construct this dialogue (articles, prepositions, etc.)",
    hideByDefault: "I already know these — hide by default",
    longPressHint: "Hold any word to look it up",
    hideAll: "Hide all",
    showAll: "Show all",
    tapToReveal: "Tap to reveal translation",
    practiceMode: "Practice Mode",
    practiceDescription: "Navigate through real conversation scenarios. Choose responses and see how the dialog unfolds.",
    howItWorks: "How it works:",
    practiceStep1: "Read the conversation prompt",
    practiceStep2: "Choose the best response from the options",
    practiceStep3: "Continue until you reach the end of the conversation",
    startPractice: "Start Practice",
    greatJob: "Great job!",
    keepPracticing: "Keep practicing!",
    correctResponses: "{correct} of {total} correct responses",
    yourConversation: "Your conversation:",
    tryAgain: "Try Again",
    newPath: "New Path",
    turn: "Turn {count}",
    score: "Score: {correct}/{total}",
    chooseResponse: "Choose your response:",
    continueConversation: "Continue Conversation",
    seeResults: "See Results",
    conversationStarts: "Conversation starts",
    you: "You",
    them: "Them",
    end: "End",
    alsoSay: "Also say",
    playing: "Playing...",
    askAI: "Get Help",
    response: "response",
    responses: "responses",
    or: "or",
    systemResponse: "Reply",
    yourOptions: "Your options",
    tapToExpand: "Tap for options · Hold words to translate",
    startAgain: "Start again",
    pickYourAnswer: "Pick your answer",
    pickYourAnswerHint: "(tap one of the options below)",
    tapToAnswer: "Tap on the answer below to continue",
    pickTheirAnswer: "Pick their answer",
    tapToStart: "Tap to start",
    transcribing: "Transcribing...",
    useVoice: "Use voice",
    saySomethingElse: "I want to say something else",
    said: "said",
    whatToSay: "What do you want to say?",
    typeYourResponse: "Type your response...",
    customPhraseHint: "Type in your language and we'll translate it naturally",
    fixThisResponse: "Fix this response",
    whatsWrong: "What should it say instead?",
    fixResponseHint: "Describe the correction or provide the correct phrase",
    // Practice tab modes
    practiceTab: "Practice",
    practiceWords: "Words",
    practiceSpelling: "Spelling",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Quiz with images",
    practiceSpellingDesc: "Say words aloud",
    practiceKaraokeDesc: "Speak phrases word by word",
    introduceWord: "New word",
    composeWord: "Compose a word",
    describeImage: "Describe what's on a picture",
    pickWordForSentence: "Pick a word for the sentence",
    iNeedAHint: "I need a Hint",
    iDontKnow: "I don't know this word",
    tapHoldSayWord: "Tap, hold and say the word",
    tapHoldSayPhrase: "Tap, hold and say the phrase",
    weHeard: "We heard:",
    skipWord: "Skip",
    nextWord: "Next",
    practiceComplete: "Practice Complete!",
    wordsCorrectCount: "{correct} of {total} correct",
    practiceAgain: "Practice Again",
    done: "Done",
    correct: "Correct!",
    chooseModule: "Choose a module",
    moduleHint: "Practice in smaller groups for better retention",
    wordsCount: "words",
    markKnown: "I know this",
    markTough: "Tough one",
    markLiked: "Like",
    knownWordsHidden: "{count} known words hidden",
    noWordsToReview: "No words to review yet",
    noWordsToReviewDesc: "Generate some dialogues to build your vocabulary",
    generatingMore: "Generating...",
  },
  common: {
    loading: "Loading...",
    error: "An error occurred",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    send: "Send",
    copy: "Copy",
  },
  games: {
    score: "Score",
    best: "Best",
    hi: "HI",
    tapOn: "Tap on",
    getReady: "Get ready...",
    tapToStart: "Tap to start!",
    tapToJump: "Tap to jump!",
    gameOver: "GAME OVER",
    tapToRestart: "Tap to restart",
    speedSlow: "Slow",
    speedSteady: "Steady",
    speedNormal: "Normal",
    speedQuick: "Quick",
    speedFast: "Fast",
    speedBlazing: "Blazing",
    speedOnFire: "On Fire!",
    speedRocket: "Rocket",
    speedLegendary: "Legendary",
    cheers: ["Nice!", "Great!", "Amazing!", "Wow!", "Superb!", "Got it!", "Yes!", "Boom!", "Perfect!", "Awesome!"],
  },
  errors: {
    microphoneAccessDenied: "Microphone access denied",
    recognitionError: "Speech recognition error",
    recordingFailed: "Failed to start recording",
    noSpeechDetected: "No speech detected. Please try again.",
    llmConfigRequired: "Configure your LLM API key in settings to use this feature.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Welcome to SaySay",
    welcomeSubtitle: "Be ready for any conversation",
    getStarted: "Get Started",
    // Feature slides
    featureTopicsPain: "Generic textbooks don't fit your life",
    featureTopicsSolution: "Practice what you actually need",
    featureTopicsDesc: "Describe any situation — ordering coffee, seeing a doctor, chatting with neighbors — and get a dialogue tailored to you.",
    featureVoicePain: "How do you actually say that?",
    featureVoiceSolution: "Hear every word spoken",
    featureVoiceDesc: "Tap any phrase to hear native pronunciation. Use your voice to respond hands-free.",
    featureAiPain: "One-size-fits-all doesn't work",
    featureAiSolution: "AI builds your vocabulary",
    featureAiDesc: "Every dialogue comes with words and phrases you'll actually use. Learn vocabulary that fits your life.",
    continue: "Continue",
    inputNativeLangTitle: "What language do you speak?",
    inputNativeLangSubtitle: "We'll teach you in this language",
    inputTargetLangTitle: "What do you want to learn?",
    inputTargetLangSubtitle: "Pick the language you'd like to practice",
    // Input: Name
    inputNameTitle: "What should we call you?",
    inputNameSubtitle: "You can change this later",
    inputNamePlaceholder: "Your name",
    // Input: Languages
    inputLangTitle: "Do you know any other languages?",
    inputLangSubtitle: "We'll use similarities to help you learn faster",
    inputLangSelect: "Select any languages you know beyond English",
    inputLangFindMore: "Find more",
    inputLangSearch: "Search for a language...",
    skipStep: "Skip this step",
    // Input: Style
    inputStyleTitle: "How do you want to sound?",
    inputStyleSubtitle: "You can change this later",
    speakingStyleBeginner: "Beginner",
    speakingStyleBeginnerDesc: "Simple words, basic phrases — clearly a learner",
    speakingStyleLocal: "Like a local",
    speakingStyleLocalDesc: "Natural expressions, slang, idioms — blend in",
    speakingStyleMix: "Mix it up",
    speakingStyleMixDesc: "Pick one or more styles",
    styleYourOption: "Your option...",
    styleTypePlaceholder: "Type your style...",
    // Input: Gender
    inputGenderTitle: "Who are you in dialogues?",
    inputGenderSubtitle: "Optional — you can change this later",
    genderMale: "Male",
    genderFemale: "Female",
    genderOther: "Other / Non-binary",
    genderPreferNot: "Prefer not to say",
    // Navigation
    back: "Back",
    skipAll: "Skip all",
    // Ready screen
    startLearning: "Start Learning",
    youreAllSet: "You're all set!",
    youreAllSetWithName: "You're all set, {name}!",
    startFirstConversation: "Start your first conversation and see how natural language learning can be.",
    analyticsOptIn: "Help improve SaySay by sharing anonymous usage data (no personal information collected)",
    // First conversation transition screen
    firstConversationTitle: "Great! Now let's practice your first conversation.",
    firstConversationSubtitle: "Pick a topic and we'll generate a real dialogue for you to practice with.",
    firstConversationButton: "Let's go!",
    // Preview UI elements
    previewTopicTitle: "What would you like to talk about?",
    previewTopicSubtitle: "Type a scenario or topic",
    previewExample: "Example — tap to use",
    previewExampleText: "Ordering coffee and asking about local recommendations",
    previewTypeOwn: "Or type your own...",
    previewVoiceText: "I would like a coffee...",
    previewDialogTranslation: "Good morning! What would you like?",
  },
  knownLanguages: {
    spanish: "Spanish",
    french: "French",
    german: "German",
    italian: "Italian",
    portuguese: "Portuguese",
    russian: "Russian",
    chinese: "Chinese",
    japanese: "Japanese",
    korean: "Korean",
    arabic: "Arabic",
    hindi: "Hindi",
    dutch: "Dutch",
    polish: "Polish",
    swedish: "Swedish",
    turkish: "Turkish",
    greek: "Greek",
    czech: "Czech",
    romanian: "Romanian",
    hungarian: "Hungarian",
    finnish: "Finnish",
    norwegian: "Norwegian",
    danish: "Danish",
    ukrainian: "Ukrainian",
    thai: "Thai",
    vietnamese: "Vietnamese",
    indonesian: "Indonesian",
    malay: "Malay",
    tagalog: "Tagalog",
    hebrew: "Hebrew",
    persian: "Persian",
    bengali: "Bengali",
    punjabi: "Punjabi",
    tamil: "Tamil",
    urdu: "Urdu",
    swahili: "Swahili",
    afrikaans: "Afrikaans",
    catalan: "Catalan",
    croatian: "Croatian",
    serbian: "Serbian",
    slovak: "Slovak",
    slovenian: "Slovenian",
    bulgarian: "Bulgarian",
    lithuanian: "Lithuanian",
    latvian: "Latvian",
    estonian: "Estonian",
    icelandic: "Icelandic",
    welsh: "Welsh",
    irish: "Irish",
    scottishGaelic: "Scottish Gaelic",
    basque: "Basque",
    maltese: "Maltese",
    albanian: "Albanian",
    macedonian: "Macedonian",
    georgian: "Georgian",
    armenian: "Armenian",
    kazakh: "Kazakh",
    uzbek: "Uzbek",
    mongolian: "Mongolian",
    nepali: "Nepali",
    sinhala: "Sinhala",
    burmese: "Burmese",
    khmer: "Khmer",
    lao: "Lao",
  },
  coachMarks: {
    startNodeTitle: "Start the conversation",
    startNodeBody: "Tap this message bubble to begin the dialogue.",
    speakerTitle: "Listen to pronunciation",
    speakerBody: "Tap the speaker icon to hear the phrase spoken aloud.",
    answerTitle: "Choose your response",
    answerBody: "Tap one of the options to pick what you want to say.",
    tabsTitle: "Explore more",
    tabsBody: "Switch between the dialogue, practice exercises, and vocabulary.",
    skip: "Skip",
    next: "Next",
    done: "Done",
  },
  passwordGate: {
    enterPassword: "Enter password to continue",
    placeholder: "Password",
  },
  dialogueFeedback: {
    title: "What do you think of this dialogue?",
    bad: "Bad",
    meh: "Meh",
    good: "Good",
    thanks: "Thanks for your feedback!",
    tellUsWhy: "Thanks! Could you tell us why?",
    commentPlaceholder: "What could be better?",
    skip: "Skip",
    send: "Send",
  },
  dialog: {
    listening: "Listening to you...",
    stopRecording: "Stop recording",
    practice: "Practice",
    vocabulary: "Vocabulary",
    loadingVocabulary: "Loading vocabulary...",
  },
  languageNames: {
    "en-US": "English (US)",
    "en-GB": "English (UK)",
    "es-ES": "Spanish (Spain)",
    "es-MX": "Spanish (Mexico)",
    "pt-PT": "Portuguese (Portugal)",
    "pt-BR": "Portuguese (Brazil)",
    "fr-FR": "French",
    "de-DE": "German",
    "de-AT": "German (Austria)",
    "de-CH": "German (Switzerland)",
    "it-IT": "Italian",
    "ja-JP": "Japanese",
    "zh-CN": "Chinese",
    "ko-KR": "Korean",
    "he-IL": "Hebrew",
    "ru-RU": "Russian",
    "pl-PL": "Polish",
    "uk-UA": "Ukrainian",
    "fi-FI": "Finnish",
    "et-EE": "Estonian",
    "lv-LV": "Latvian",
    "el-GR": "Greek",
  },
};

// Spanish
const es: Translations = {
  nav: {
    dialogues: "Diálogos",
    grammar: "Gramática",
    practice: "Práctica",
    alphabet: "Alfabeto",
  },
  languageSelector: {
    title: "Idiomas",
    iSpeak: "Yo hablo",
    iWantToLearn: "Quiero aprender",
    done: "Listo",
    back: "Atrás",
    moreComingSoon: "¡Más idiomas próximamente!",
  },
  topics: {
    title: "Temas de Diálogo",
    subtitle: "Elige una conversación para practicar",
    tagline: "Aprende a decir lo que necesitas",
    heroTitle: "Prepárate para cualquier conversación",
    heroSubtitle: "Deja de asentir — empieza a hablar",
    createCustom: "Nueva conversación",
    chooseTopic: "Elige un Tema",
    available: "disponibles",
    words: "palabras",
    expressions: "expresiones",
    difficulty: {
      beginner: "Principiante",
      intermediate: "Intermedio",
      advanced: "Avanzado",
    },
    dialogNodes: "turnos",
    regenerateWarning: "Creado para otro idioma — toca para recrear",
    deleteTitle: "Eliminar Tema",
    deleteDescription: "¿Estás seguro de que quieres eliminar este tema? Esta acción no se puede deshacer.",
    regenerateTitle: "Regenerar diálogo",
    regenerateDescription: "Se creará un nuevo diálogo para este tema. El diálogo actual será reemplazado.",
    regenerate: "Regenerar",
    edit: "Editar",
    recent: "Recientes",
    categories: "Categorías",
    byCategory: "Diálogos por categoría",
    collapse: "Contraer",
  },
  grammar: {
    title: "Reglas Gramaticales",
    subtitle: "Cómo funciona el {language}",
    noRulesTitle: "Sin Reglas Aún",
    noRulesDescription: "Crea un tema personalizado en {language} para ver las reglas gramaticales aquí.",
    rulesCount: "{count} reglas gramaticales",
    rulesSingular: "{count} regla gramatical",
    tapToExpand: "Toca para expandir y aprender los patrones.",
    examples: "Ejemplos del Diálogo",
    howToThink: "Cómo Pensarlo",
    commonMistake: "Error Común",
  },
  alphabet: {
    title: "Alfabeto y Sonidos",
    subtitle: "Pronunciación del {language}",
    comingSoonTitle: "Próximamente",
    comingSoonDescription: "La guía de alfabeto y pronunciación para {language} aún no está disponible.",
    examples: "Ejemplos",
    all: "Todo",
  },
  generator: {
    title: "¿De qué quieres hablar?",
    placeholder: "Describe una situación...\n\nEjemplos:\n• Pedir un café en una cafetería\n• Preguntar direcciones\n• Hacer una cita con el médico",
    proficiency: "¿Cómo quieres sonar?",
    generate: "Vamos",
    generating: "Preparando todo...",
    loadTopic: "Cargar",
    back: "Volver",
    askAI: "Describe tu situación",
    responses: "respuestas",
    modelTierFast: "Rápido",
    modelTierFastDesc: "Rápido y económico",
    modelTierBalanced: "Equilibrado",
    modelTierBalancedDesc: "Mejor relación",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Máxima calidad",
    loadingModels: "Cargando modelos...",
    llmConfigWarning: "Configura tu proveedor de LLM en la configuración para habilitar la generación con IA",
    topicTitle: "¿De qué te gustaría hablar?",
    topicHintMic: "Toca el micrófono y di tu tema",
    topicHintKeyboard: "Escribe un escenario o tema",
    exampleTapToUse: "Ejemplo — toca para usar",
    enterText: "Escribir",
    tapToStop: "Toca para parar",
    processing: "Procesando...",
    tapAndTalk: "Toca y habla",
    recording: "Grabando...",
    useVoice: "Usar voz",
    attach: "Adjuntar",
    continue: "Continuar",
    apiKeyRequired: "Se requiere clave API",
    apiKeyRequiredDesc: "Configura tu proveedor de LLM en ajustes para habilitar la generación con IA",
    difficultyTitle: "¿Cómo quieres sonar?",
    difficultySubtitle: "Elige tu estilo de habla",
    styleBeginner: "Principiante",
    styleBeginnerDesc: "Palabras sencillas — como principiante",
    styleLocal: "Como un local",
    styleLocalDesc: "Habla natural, jerga, modismos — como un nativo",
    styleMix: "Mezclar",
    styleMixDesc: "Elige uno o más",
    styleCustomPlaceholder: "O escribe el tuyo...",
    richnessTitle: "¿Cuánto contenido?",
    richnessSubtitle: "Elige la cantidad de vocabulario y diálogo",
    richnessQuick: "Rápido",
    richnessQuickDesc: "Diálogo corto, vocabulario esencial",
    richnessStandard: "Estándar",
    richnessStandardDesc: "Diálogo medio, buena cobertura",
    richnessComprehensive: "Completo",
    richnessComprehensiveDesc: "Diálogo largo, vocabulario extenso",
    modelTitle: "Elige nivel de calidad",
    modelSubtitle: "Mayor calidad = mejor contenido, mayor costo",
    cachedAvailable: "Respuesta en caché disponible — ¡carga instantánea!",
    loadCached: "Cargar caché",
    previewPrompt: "Vista previa del prompt",
    fullPrompt: "Prompt completo",
    copied: "¡Copiado!",
    systemPrompt: "Prompt del sistema",
    userPrompt: "Prompt del usuario",
    gettingReady: "Preparando todo...",
    connectingToAI: "Conectando con IA...",
    done: "¡Listo!",
    letsGo: "Vamos",
    goBack: "Volver",
    playWhileWaiting: "¡Juega {game1} o {game2} mientras esperas!",
    play: "Juega",
    or: "o",
    whileYouWait: "mientras esperas",
    exitGame: "Salir del juego",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Pedí neumáticos en línea en una tienda y ya llegaron. Necesito llamar para hacer una cita y que me los instalen esta semana.",
      "Estoy en una cafetería de especialidad y quiero pedir un flat white con leche de avena y un pastelito. También quiero preguntar si tienen granos de origen único.",
      "Estoy en la estación de tren y necesito llegar a mi destino. Quiero saber en qué andén, cuánto cuesta un boleto de ida y vuelta, y si puedo pagar con tarjeta.",
      "Estoy en una pista de karts con amigos. Queremos reservar una sesión de 20 minutos para 4 personas y preguntar por descuentos de grupo.",
      "La pantalla de mi teléfono está rota y estoy en una tienda de reparación. Quiero preguntar cuánto tiempo tarda, cuánto cuesta y si tienen garantía.",
      "Estoy en la farmacia y necesito algo para un dolor de cabeza fuerte y también medicina para alergias. Quiero saber cuál es más fuerte y si necesito receta.",
      "Estoy llegando a un Airbnb y la llave no está donde el anfitrión dijo. Necesito llamarle para ver cómo entrar.",
      "Estoy en un restaurante de mariscos en la playa y quiero pedir pescado a la parrilla, pero quiero saber qué hay fresco hoy y pedir recomendaciones.",
      "Estoy en una escuela de surf y quiero reservar una clase para principiantes mañana por la mañana. Necesito preguntar sobre el alquiler del traje y a qué hora llegar.",
      "Mi coche de alquiler tiene una llanta ponchada en la autopista. Necesito llamar a asistencia en carretera y explicar dónde estoy y qué pasó.",
    ],
  },
  pwa: {
    installPrompt: "Instala SaySay para una mejor experiencia",
    install: "Instalar",
  },
  settings: {
    title: "Configuración",
    theme: "Apariencia",
    themeLight: "Claro",
    themeDark: "Oscuro",
    themeSystem: "Auto",
    resetAll: "Borrar Todos los Datos",
    resetConfirm: "Esto eliminará todas las configuraciones, temas y preferencias guardadas. ¿Estás seguro?",
    close: "Cerrar",
    speechRecognition: "Reconocimiento de Voz",
    sttAzure: "Azure",
    sttAzureDesc: "Basado en la nube, alta calidad",
    sttBrowser: "Navegador",
    sttBrowserDesc: "Integrado, funciona sin conexión",
    speechSpeed: "Velocidad de voz",
    analytics: "Análisis",
    shareUsageData: "Compartir datos de uso",
    analyticsDescription: "Ayuda a mejorar SaySay compartiendo datos de uso anónimos",
    knownLanguages: "Idiomas conocidos",
    knownLanguagesDescription: "Selecciona los idiomas que ya conoces. Esto nos ayuda a proporcionar mejores consejos de pronunciación usando sonidos de estos idiomas.",
    noneSelected: "Ninguno seleccionado",
    learning: "Aprendizaje",
    showPronunciationHints: "Mostrar sugerencias de pronunciación",
    showPronunciationHintsDescription: "Muestra cómo decir frases usando sonidos que ya conoces",
  },
  learning: {
    back: "Volver",
    dialogues: "Diálogos",
    vocabulary: "Vocabulario",
    grammar: "Gramática",
    practice: "Práctica",
    cards: "Tarjetas",
    listen: "Escuchar",
    showTranslation: "Mostrar traducción",
    hideTranslation: "Ocultar traducción",
    pronunciation: "Pronunciación",
    sayItLike: "Dilo como",
    allForms: "Todas las Formas",
    example: "Ejemplo",
    inContext: "En Contexto",
    literalMeaning: "Significado Literal",
    wordBreakdown: "Desglose de Palabras",
    fromDialog: "Del Diálogo",
    searchVocabulary: "Buscar vocabulario...",
    foundResults: "Encontrados {count} resultados para \"{query}\"",
    progress: "Progreso",
    mastered: "{count} / {total} dominados",
    words: "Palabras",
    expressions: "Expresiones",
    topical: "Bueno Saber",
    wordsDescription: "Vocabulario esencial con todas sus formas",
    expressionsDescription: "Frases y modismos",
    topicalDescription: "Vocabulario relacionado",
    noWordsMatch: "No hay palabras que coincidan",
    noExpressionsMatch: "No hay expresiones que coincidan",
    noTopicalMatch: "No hay vocabulario relacionado que coincida",
    forms: "Formas",
    buildingBlocks: "Piezas básicas",
    showBuildingBlocks: "Mostrar piezas básicas",
    buildingBlocksDescription: "Palabras comunes necesarias para este diálogo (artículos, preposiciones, etc.)",
    hideByDefault: "Ya las conozco — ocultar por defecto",
    longPressHint: "Mantén pulsada cualquier palabra para buscarla",
    hideAll: "Ocultar todo",
    showAll: "Mostrar todo",
    tapToReveal: "Toca para ver la traducción",
    practiceMode: "Modo Práctica",
    practiceDescription: "Navega por escenarios de conversación reales. Elige respuestas y ve cómo se desarrolla el diálogo.",
    howItWorks: "Cómo funciona:",
    practiceStep1: "Lee el mensaje de conversación",
    practiceStep2: "Elige la mejor respuesta de las opciones",
    practiceStep3: "Continúa hasta llegar al final de la conversación",
    startPractice: "Comenzar Práctica",
    greatJob: "¡Excelente!",
    keepPracticing: "¡Sigue practicando!",
    correctResponses: "{correct} de {total} respuestas correctas",
    yourConversation: "Tu conversación:",
    tryAgain: "Intentar de Nuevo",
    newPath: "Nuevo Camino",
    turn: "Turno {count}",
    score: "Puntuación: {correct}/{total}",
    chooseResponse: "Elige tu respuesta:",
    continueConversation: "Continuar Conversación",
    seeResults: "Ver Resultados",
    conversationStarts: "Comienza la conversación",
    you: "Tú",
    them: "Ellos",
    end: "Fin",
    alsoSay: "También di",
    playing: "Reproduciendo...",
    askAI: "Ayuda",
    response: "respuesta",
    responses: "respuestas",
    or: "o",
    systemResponse: "Sistema/Respuesta",
    yourOptions: "Tus opciones",
    tapToExpand: "Toca para expandir. Mantén pulsada una palabra para traducir.",
    startAgain: "Empezar de nuevo",
    pickYourAnswer: "Elige tu respuesta",
    pickYourAnswerHint: "(toca una de las opciones de abajo)",
    tapToAnswer: "Toca la respuesta de abajo para continuar",
    pickTheirAnswer: "Elige su respuesta",
    tapToStart: "Toca para empezar",
    transcribing: "Transcribiendo...",
    useVoice: "Usar voz",
    saySomethingElse: "Quiero decir otra cosa",
    said: "dijo",
    whatToSay: "¿Qué quieres decir?",
    typeYourResponse: "Escribe tu respuesta...",
    customPhraseHint: "Escribe en tu idioma y lo traduciremos naturalmente",
    fixThisResponse: "Corregir esta respuesta",
    whatsWrong: "¿Qué debería decir en su lugar?",
    fixResponseHint: "Describe la corrección o proporciona la frase correcta",
    // Practice tab modes
    practiceTab: "Práctica",
    practiceWords: "Palabras",
    practiceSpelling: "Ortografía",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Cuestionario con imágenes",
    practiceSpellingDesc: "Di las palabras en voz alta",
    practiceKaraokeDesc: "Di frases palabra por palabra",
    introduceWord: "Nueva palabra",
    composeWord: "Componer una palabra",
    describeImage: "Describe lo que hay en una imagen",
    pickWordForSentence: "Elige una palabra para la oración",
    iNeedAHint: "Necesito una pista",
    iDontKnow: "No conozco esta palabra",
    tapHoldSayWord: "Toca, mantén y di la palabra",
    tapHoldSayPhrase: "Toca, mantén y di la frase",
    weHeard: "Escuchamos:",
    skipWord: "Saltar",
    nextWord: "Siguiente",
    practiceComplete: "¡Práctica completa!",
    wordsCorrectCount: "{correct} de {total} correctas",
    practiceAgain: "Practicar de nuevo",
    done: "Listo",
    correct: "¡Correcto!",
    chooseModule: "Elige un módulo",
    moduleHint: "Practica en grupos pequeños para mejor retención",
    wordsCount: "palabras",
    markKnown: "Ya lo sé",
    markTough: "Difícil",
    markLiked: "Me gusta",
    knownWordsHidden: "{count} palabras conocidas ocultas",
    noWordsToReview: "Aún no hay palabras para repasar",
    noWordsToReviewDesc: "Genera algunos diálogos para ampliar tu vocabulario",
    generatingMore: "Generando...",
  },
  common: {
    loading: "Cargando...",
    error: "Ocurrió un error",
    retry: "Reintentar",
    cancel: "Cancelar",
    save: "Guardar",
    delete: "Eliminar",
    edit: "Editar",
    close: "Cerrar",
    send: "Enviar",
    copy: "Copiar",
  },
  games: {
    score: "Puntos",
    best: "Mejor",
    hi: "RÉC",
    tapOn: "Toca en",
    getReady: "Prepárate...",
    tapToStart: "¡Toca para empezar!",
    tapToJump: "¡Toca para saltar!",
    gameOver: "FIN DEL JUEGO",
    tapToRestart: "Toca para reiniciar",
    speedSlow: "Lento",
    speedSteady: "Firme",
    speedNormal: "Normal",
    speedQuick: "Rápido",
    speedFast: "Veloz",
    speedBlazing: "Ardiente",
    speedOnFire: "¡En llamas!",
    speedRocket: "Cohete",
    speedLegendary: "Legendario",
    cheers: ["¡Bien!", "¡Genial!", "¡Increíble!", "¡Guau!", "¡Magnífico!", "¡Lo tienes!", "¡Sí!", "¡Boom!", "¡Perfecto!", "¡Asombroso!"],
  },
  errors: {
    microphoneAccessDenied: "Acceso al micrófono denegado",
    recognitionError: "Error de reconocimiento de voz",
    recordingFailed: "No se pudo iniciar la grabación",
    noSpeechDetected: "No se detectó voz. Por favor intenta de nuevo.",
    llmConfigRequired: "Configura tu clave de API de LLM en la configuración para usar esta función.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Bienvenido a SaySay",
    welcomeSubtitle: "Prepárate para cualquier conversación",
    getStarted: "Empezar",
    // Feature slides
    featureTopicsPain: "Los libros de texto no van contigo",
    featureTopicsSolution: "Practica lo que de verdad necesitas",
    featureTopicsDesc: "Describe cualquier situación — pedir un café, ir al médico, charlar con vecinos — y consigue un diálogo a tu medida.",
    featureVoicePain: "¿Cómo se dice eso realmente?",
    featureVoiceSolution: "Escucha cada palabra",
    featureVoiceDesc: "Toca cualquier frase para escuchar la pronunciación nativa. Responde con tu voz sin usar las manos.",
    featureAiPain: "Lo genérico no funciona",
    featureAiSolution: "La IA construye tu vocabulario",
    featureAiDesc: "Cada diálogo viene con palabras y expresiones que usarás de verdad. Aprende vocabulario que encaja en tu vida.",
    continue: "Continuar",
    inputNativeLangTitle: "¿Qué idioma hablas?",
    inputNativeLangSubtitle: "Te enseñaremos en este idioma",
    inputTargetLangTitle: "¿Qué idioma quieres aprender?",
    inputTargetLangSubtitle: "Elige el idioma que quieres practicar",
    // Input: Name
    inputNameTitle: "¿Cómo te llamamos?",
    inputNameSubtitle: "Puedes cambiarlo después",
    inputNamePlaceholder: "Tu nombre",
    // Input: Languages
    inputLangTitle: "¿Hablas otros idiomas?",
    inputLangSubtitle: "Usaremos las similitudes para que aprendas más rápido",
    inputLangSelect: "Selecciona los idiomas que conoces además del español",
    inputLangFindMore: "Buscar más",
    inputLangSearch: "Buscar idioma...",
    skipStep: "Saltar este paso",
    // Input: Style
    inputStyleTitle: "¿Cómo quieres sonar?",
    inputStyleSubtitle: "Puedes cambiarlo después",
    speakingStyleBeginner: "Principiante",
    speakingStyleBeginnerDesc: "Palabras simples, frases básicas — como alguien que está aprendiendo",
    speakingStyleLocal: "Como un nativo",
    speakingStyleLocalDesc: "Expresiones naturales, jerga, modismos — que no se note que eres extranjero",
    speakingStyleMix: "Mezclar estilos",
    speakingStyleMixDesc: "Elige uno o más estilos",
    styleYourOption: "Tu propia opción...",
    styleTypePlaceholder: "Escribe tu estilo...",
    // Input: Gender
    inputGenderTitle: "¿Quién eres en los diálogos?",
    inputGenderSubtitle: "Opcional — puedes cambiarlo después",
    genderMale: "Hombre",
    genderFemale: "Mujer",
    genderOther: "Otro / No binario",
    genderPreferNot: "Prefiero no decirlo",
    // Navigation
    back: "Atrás",
    skipAll: "Saltar todo",
    // Ready screen
    startLearning: "Empezar a Aprender",
    youreAllSet: "¡Todo listo!",
    youreAllSetWithName: "¡Todo listo, {name}!",
    startFirstConversation: "Empieza tu primera conversación y descubre lo natural que puede ser aprender un idioma.",
    analyticsOptIn: "Ayuda a mejorar SaySay compartiendo datos de uso anónimos (no se recopila información personal)",
    // First conversation transition screen
    firstConversationTitle: "¡Genial! Ahora practiquemos tu primera conversación.",
    firstConversationSubtitle: "Elige un tema y generaremos un diálogo real para que practiques.",
    firstConversationButton: "¡Vamos!",
    // Preview UI elements
    previewTopicTitle: "¿De qué te gustaría hablar?",
    previewTopicSubtitle: "Escribe un escenario o tema",
    previewExample: "Ejemplo — toca para usar",
    previewExampleText: "Pedir un café y preguntar por recomendaciones locales",
    previewTypeOwn: "O escribe el tuyo...",
    previewVoiceText: "Me gustaría un café...",
    previewDialogTranslation: "¡Buenos días! ¿Qué desea?",
  },
  knownLanguages: {
    spanish: "Español",
    french: "Francés",
    german: "Alemán",
    italian: "Italiano",
    portuguese: "Portugués",
    russian: "Ruso",
    chinese: "Chino",
    japanese: "Japonés",
    korean: "Coreano",
    arabic: "Árabe",
    hindi: "Hindi",
    dutch: "Neerlandés",
    polish: "Polaco",
    swedish: "Sueco",
    turkish: "Turco",
    greek: "Griego",
    czech: "Checo",
    romanian: "Rumano",
    hungarian: "Húngaro",
    finnish: "Finlandés",
    norwegian: "Noruego",
    danish: "Danés",
    ukrainian: "Ucraniano",
    thai: "Tailandés",
    vietnamese: "Vietnamita",
    indonesian: "Indonesio",
    malay: "Malayo",
    tagalog: "Tagalo",
    hebrew: "Hebreo",
    persian: "Persa",
    bengali: "Bengalí",
    punjabi: "Panyabí",
    tamil: "Tamil",
    urdu: "Urdu",
    swahili: "Suajili",
    afrikaans: "Afrikáans",
    catalan: "Catalán",
    croatian: "Croata",
    serbian: "Serbio",
    slovak: "Eslovaco",
    slovenian: "Esloveno",
    bulgarian: "Búlgaro",
    lithuanian: "Lituano",
    latvian: "Letón",
    estonian: "Estonio",
    icelandic: "Islandés",
    welsh: "Galés",
    irish: "Irlandés",
    scottishGaelic: "Gaélico escocés",
    basque: "Euskera",
    maltese: "Maltés",
    albanian: "Albanés",
    macedonian: "Macedonio",
    georgian: "Georgiano",
    armenian: "Armenio",
    kazakh: "Kazajo",
    uzbek: "Uzbeko",
    mongolian: "Mongol",
    nepali: "Nepalí",
    sinhala: "Cingalés",
    burmese: "Birmano",
    khmer: "Jemer",
    lao: "Lao",
  },
  coachMarks: {
    startNodeTitle: "Iniciar la conversación",
    startNodeBody: "Toca esta burbuja de mensaje para comenzar el diálogo.",
    speakerTitle: "Escuchar la pronunciación",
    speakerBody: "Toca el icono del altavoz para escuchar la frase en voz alta.",
    answerTitle: "Elige tu respuesta",
    answerBody: "Toca una de las opciones para elegir lo que quieres decir.",
    tabsTitle: "Explorar más",
    tabsBody: "Cambia entre el diálogo, ejercicios de práctica y vocabulario.",
    skip: "Omitir",
    next: "Siguiente",
    done: "Listo",
  },
  passwordGate: {
    enterPassword: "Ingresa la contraseña para continuar",
    placeholder: "Contraseña",
  },
  dialogueFeedback: {
    title: "¿Qué te parece este diálogo?",
    bad: "Malo",
    meh: "Regular",
    good: "Bueno",
    thanks: "¡Gracias por tu opinión!",
    tellUsWhy: "¡Gracias! ¿Podrías decirnos por qué?",
    commentPlaceholder: "¿Qué podría mejorar?",
    skip: "Omitir",
    send: "Enviar",
  },
  dialog: {
    listening: "Escuchándote...",
    stopRecording: "Detener grabación",
    practice: "Practicar",
    vocabulary: "Vocabulario",
    loadingVocabulary: "Cargando vocabulario...",
  },
  languageNames: {
    "en-US": "Inglés (EE.UU.)",
    "en-GB": "Inglés (Reino Unido)",
    "es-ES": "Español (España)",
    "es-MX": "Español (México)",
    "pt-PT": "Portugués (Portugal)",
    "pt-BR": "Portugués (Brasil)",
    "fr-FR": "Francés",
    "de-DE": "Alemán",
    "de-AT": "Alemán (Austria)",
    "de-CH": "Alemán (Suiza)",
    "it-IT": "Italiano",
    "ja-JP": "Japonés",
    "zh-CN": "Chino",
    "ko-KR": "Coreano",
    "he-IL": "Hebreo",
    "ru-RU": "Ruso",
    "pl-PL": "Polaco",
    "uk-UA": "Ucraniano",
    "fi-FI": "Finlandés",
    "et-EE": "Estonio",
    "lv-LV": "Letón",
    "el-GR": "Griego",
  },
};

// Portuguese
const pt: Translations = {
  nav: {
    dialogues: "Diálogos",
    grammar: "Gramática",
    practice: "Prática",
    alphabet: "Alfabeto",
  },
  languageSelector: {
    title: "Idiomas",
    iSpeak: "Eu falo",
    iWantToLearn: "Quero aprender",
    done: "Pronto",
    back: "Voltar",
    moreComingSoon: "Mais idiomas em breve!",
  },
  topics: {
    title: "Temas de Diálogo",
    subtitle: "Escolha uma conversa para praticar",
    tagline: "Aprenda a dizer o que você precisa",
    heroTitle: "Esteja pronto para qualquer conversa",
    heroSubtitle: "Chega de acenar — comece a falar",
    createCustom: "Nova conversa",
    chooseTopic: "Escolha um Tema",
    available: "disponíveis",
    words: "palavras",
    expressions: "expressões",
    difficulty: {
      beginner: "Iniciante",
      intermediate: "Intermediário",
      advanced: "Avançado",
    },
    dialogNodes: "turnos",
    regenerateWarning: "Criado para outro idioma — toque para recriar",
    deleteTitle: "Excluir Tema",
    deleteDescription: "Tem certeza de que deseja excluir este tema? Esta ação não pode ser desfeita.",
    regenerateTitle: "Regenerar diálogo",
    regenerateDescription: "Será criado um novo diálogo para este tema. O diálogo atual será substituído.",
    regenerate: "Regenerar",
    edit: "Editar",
    recent: "Recentes",
    categories: "Categorias",
    byCategory: "Diálogos por categoria",
    collapse: "Recolher",
  },
  grammar: {
    title: "Regras Gramaticais",
    subtitle: "Como funciona o {language}",
    noRulesTitle: "Sem Regras Ainda",
    noRulesDescription: "Crie um tema personalizado em {language} para ver as regras gramaticais aqui.",
    rulesCount: "{count} regras gramaticais",
    rulesSingular: "{count} regra gramatical",
    tapToExpand: "Toque para expandir e aprender os padrões.",
    examples: "Exemplos do Diálogo",
    howToThink: "Como Pensar Nisso",
    commonMistake: "Erro Comum",
  },
  alphabet: {
    title: "Alfabeto e Sons",
    subtitle: "Pronúncia do {language}",
    comingSoonTitle: "Em Breve",
    comingSoonDescription: "O guia de alfabeto e pronúncia para {language} ainda não está disponível.",
    examples: "Exemplos",
    all: "Tudo",
  },
  generator: {
    title: "Sobre o que você quer conversar?",
    placeholder: "Descreva uma situação...\n\nExemplos:\n• Pedir um café numa cafetaria\n• Perguntar direções\n• Marcar consulta médica",
    proficiency: "Como você quer soar?",
    generate: "Vamos lá",
    generating: "Preparando tudo...",
    loadTopic: "Carregar",
    back: "Voltar",
    askAI: "Descreva sua situação",
    responses: "respostas",
    modelTierFast: "Rápido",
    modelTierFastDesc: "Rápido e econômico",
    modelTierBalanced: "Balanceado",
    modelTierBalancedDesc: "Melhor custo-benefício",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Melhor qualidade",
    loadingModels: "Carregando modelos...",
    llmConfigWarning: "Configure seu provedor de IA nas configurações para ativar a geração de IA",
    topicTitle: "Sobre o que gostaria de falar?",
    topicHintMic: "Toque no microfone e diga seu tema",
    topicHintKeyboard: "Digite um cenário ou tema",
    exampleTapToUse: "Exemplo — toque para usar",
    enterText: "Escrever",
    tapToStop: "Toque para parar",
    processing: "Processando...",
    tapAndTalk: "Toque e fale",
    recording: "Gravando...",
    useVoice: "Usar voz",
    attach: "Anexar",
    continue: "Continuar",
    apiKeyRequired: "Chave API necessária",
    apiKeyRequiredDesc: "Configure seu provedor de IA nas configurações para ativar a geração de IA",
    difficultyTitle: "Como você quer soar?",
    difficultySubtitle: "Escolha seu estilo de fala",
    styleBeginner: "Iniciante",
    styleBeginnerDesc: "Palavras simples — como iniciante",
    styleLocal: "Como um local",
    styleLocalDesc: "Fala natural, gírias, expressões — como nativo",
    styleMix: "Misturar",
    styleMixDesc: "Escolha um ou mais",
    styleCustomPlaceholder: "Ou escreva o seu...",
    richnessTitle: "Quanto conteúdo?",
    richnessSubtitle: "Escolha a quantidade de vocabulário e diálogo",
    richnessQuick: "Rápido",
    richnessQuickDesc: "Diálogo curto, vocabulário essencial",
    richnessStandard: "Padrão",
    richnessStandardDesc: "Diálogo médio, boa cobertura de vocabulário",
    richnessComprehensive: "Completo",
    richnessComprehensiveDesc: "Diálogo longo, vocabulário extenso",
    modelTitle: "Escolha o nível de qualidade",
    modelSubtitle: "Maior qualidade = melhor conteúdo, maior custo",
    cachedAvailable: "Resposta em cache disponível — carregamento instantâneo!",
    loadCached: "Carregar cache",
    previewPrompt: "Pré-visualizar prompt",
    fullPrompt: "Prompt completo",
    copied: "Copiado!",
    systemPrompt: "Prompt do sistema",
    userPrompt: "Prompt do usuário",
    gettingReady: "Preparando tudo...",
    connectingToAI: "Conectando à IA...",
    done: "Pronto!",
    letsGo: "Vamos lá",
    goBack: "Voltar",
    playWhileWaiting: "Joga {game1} ou {game2} enquanto esperas!",
    play: "Joga",
    or: "ou",
    whileYouWait: "enquanto esperas",
    exitGame: "Sair do jogo",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Encomendei pneus online numa loja e já chegaram. Preciso ligar para marcar uma hora para os instalarem no meu carro esta semana.",
      "Estou numa cafeteria de especialidade e quero pedir um flat white com leite de aveia e um pastel. Também quero perguntar se têm grãos de origem única.",
      "Estou na estação de comboio e preciso de ir ao meu destino. Quero saber qual a plataforma, quanto custa um bilhete de ida e volta e se posso pagar com cartão.",
      "Estou num kartódromo com amigos. Queremos reservar uma sessão de 20 minutos para 4 pessoas e perguntar sobre descontos de grupo.",
      "O ecrã do meu telemóvel está partido e estou numa loja de reparações. Quero perguntar quanto tempo demora, quanto custa e se têm garantia.",
      "Estou na farmácia e preciso de algo para uma dor de cabeça forte e também de um anti-histamínico. Quero saber qual é o mais forte e se preciso de receita.",
      "Estou a fazer check-in num Airbnb e a chave não está onde o anfitrião disse. Preciso de ligar para descobrir como entrar.",
      "Estou num restaurante de marisco à beira-mar e quero pedir peixe grelhado, mas quero saber o que está fresco hoje e pedir recomendações.",
      "Estou numa escola de surf e quero reservar uma aula para iniciantes amanhã de manhã. Preciso de perguntar sobre o aluguer do fato e a que horas chegar.",
      "O meu carro alugado tem um pneu furado na autoestrada. Preciso de ligar para a assistência na estrada e explicar onde estou e o que aconteceu.",
    ],
  },
  pwa: {
    installPrompt: "Instale o SaySay para uma melhor experiência",
    install: "Instalar",
  },
  settings: {
    title: "Configurações",
    theme: "Aparência",
    themeLight: "Claro",
    themeDark: "Escuro",
    themeSystem: "Auto",
    resetAll: "Apagar Todos os Dados",
    resetConfirm: "Isto irá apagar todas as configurações, temas e preferências guardadas. Tem a certeza?",
    close: "Fechar",
    speechRecognition: "Reconhecimento de Fala",
    sttAzure: "Azure",
    sttAzureDesc: "Baseado em nuvem, alta qualidade",
    sttBrowser: "Navegador",
    sttBrowserDesc: "Integrado, funciona offline",
    speechSpeed: "Velocidade da fala",
    analytics: "Análise",
    shareUsageData: "Compartilhar dados de uso",
    analyticsDescription: "Ajude a melhorar o SaySay compartilhando dados de uso anônimos",
    knownLanguages: "Idiomas conhecidos",
    knownLanguagesDescription: "Selecione os idiomas que você já conhece. Isso nos ajuda a fornecer melhores dicas de pronúncia usando sons desses idiomas.",
    noneSelected: "Nenhum selecionado",
    learning: "Aprendizagem",
    showPronunciationHints: "Mostrar dicas de pronúncia",
    showPronunciationHintsDescription: "Exibe como dizer frases usando sons que você já conhece",
  },
  learning: {
    back: "Voltar",
    dialogues: "Diálogos",
    vocabulary: "Vocabulário",
    grammar: "Gramática",
    practice: "Prática",
    cards: "Cartões",
    listen: "Ouvir",
    showTranslation: "Mostrar tradução",
    hideTranslation: "Ocultar tradução",
    pronunciation: "Pronúncia",
    sayItLike: "Diga como",
    allForms: "Todas as Formas",
    example: "Exemplo",
    inContext: "Em Contexto",
    literalMeaning: "Significado Literal",
    wordBreakdown: "Decomposição da Palavra",
    fromDialog: "Do Diálogo",
    searchVocabulary: "Pesquisar vocabulário...",
    foundResults: "Encontrados {count} resultados para \"{query}\"",
    progress: "Progresso",
    mastered: "{count} / {total} dominados",
    words: "Palavras",
    expressions: "Expressões",
    topical: "Bom Saber",
    wordsDescription: "Vocabulário essencial com todas as formas",
    expressionsDescription: "Frases e expressões idiomáticas",
    topicalDescription: "Vocabulário relacionado",
    noWordsMatch: "Nenhuma palavra corresponde à pesquisa",
    noExpressionsMatch: "Nenhuma expressão corresponde à pesquisa",
    noTopicalMatch: "Nenhum vocabulário relacionado corresponde à pesquisa",
    forms: "Formas",
    buildingBlocks: "Blocos básicos",
    showBuildingBlocks: "Mostrar blocos básicos",
    buildingBlocksDescription: "Palavras comuns necessárias para este diálogo (artigos, preposições, etc.)",
    hideByDefault: "Já conheço — ocultar por padrão",
    longPressHint: "Pressione longamente qualquer palavra para pesquisar",
    hideAll: "Ocultar tudo",
    showAll: "Mostrar tudo",
    tapToReveal: "Toque para revelar a tradução",
    practiceMode: "Modo Prática",
    practiceDescription: "Navegue por cenários de conversação reais. Escolha respostas e veja como o diálogo se desenrola.",
    howItWorks: "Como funciona:",
    practiceStep1: "Leia o prompt de conversação",
    practiceStep2: "Escolha a melhor resposta das opções",
    practiceStep3: "Continue até chegar ao fim da conversação",
    startPractice: "Começar Prática",
    greatJob: "Excelente!",
    keepPracticing: "Continue a praticar!",
    correctResponses: "{correct} de {total} respostas corretas",
    yourConversation: "A sua conversação:",
    tryAgain: "Tentar Novamente",
    newPath: "Novo Caminho",
    turn: "Turno {count}",
    score: "Pontuação: {correct}/{total}",
    chooseResponse: "Escolha a sua resposta:",
    continueConversation: "Continuar Conversação",
    seeResults: "Ver Resultados",
    conversationStarts: "A conversa começa",
    you: "Você",
    them: "Eles",
    end: "Fim",
    alsoSay: "Também diga",
    playing: "A reproduzir...",
    askAI: "Ajuda",
    response: "resposta",
    responses: "respostas",
    or: "ou",
    systemResponse: "Sistema/Resposta",
    yourOptions: "As suas opções",
    tapToExpand: "Toque para expandir. Pressione longamente uma palavra para traduzir.",
    startAgain: "Recomeçar",
    pickYourAnswer: "Escolha a sua resposta",
    pickYourAnswerHint: "(toque numa das opções abaixo)",
    tapToAnswer: "Toque na resposta abaixo para continuar",
    pickTheirAnswer: "Escolha a resposta dele",
    tapToStart: "Toque para começar",
    transcribing: "Transcrevendo...",
    useVoice: "Usar voz",
    saySomethingElse: "Quero dizer outra coisa",
    said: "disse",
    whatToSay: "O que você quer dizer?",
    typeYourResponse: "Digite sua resposta...",
    customPhraseHint: "Digite no seu idioma e traduziremos naturalmente",
    fixThisResponse: "Corrigir esta resposta",
    whatsWrong: "O que deveria dizer em vez disso?",
    fixResponseHint: "Descreva a correção ou forneça a frase correta",
    // Practice tab modes
    practiceTab: "Prática",
    practiceWords: "Palavras",
    practiceSpelling: "Ortografia",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Quiz com imagens",
    practiceSpellingDesc: "Diga as palavras em voz alta",
    practiceKaraokeDesc: "Fale frases palavra por palavra",
    introduceWord: "Nova palavra",
    composeWord: "Compor uma palavra",
    describeImage: "Descreva o que está na imagem",
    pickWordForSentence: "Escolha uma palavra para a frase",
    iNeedAHint: "Preciso de uma dica",
    iDontKnow: "Não conheço esta palavra",
    tapHoldSayWord: "Toque, segure e diga a palavra",
    tapHoldSayPhrase: "Toque, segure e diga a frase",
    weHeard: "Ouvimos:",
    skipWord: "Pular",
    nextWord: "Próxima",
    practiceComplete: "Prática concluída!",
    wordsCorrectCount: "{correct} de {total} corretas",
    practiceAgain: "Praticar novamente",
    done: "Concluído",
    correct: "Correto!",
    chooseModule: "Escolha um módulo",
    moduleHint: "Pratique em grupos menores para melhor retenção",
    wordsCount: "palavras",
    markKnown: "Já sei",
    markTough: "Difícil",
    markLiked: "Gostei",
    knownWordsHidden: "{count} palavras conhecidas ocultas",
    noWordsToReview: "Ainda não há palavras para rever",
    noWordsToReviewDesc: "Gere alguns diálogos para construir o seu vocabulário",
    generatingMore: "A gerar...",
  },
  common: {
    loading: "A carregar...",
    error: "Ocorreu um erro",
    retry: "Tentar novamente",
    cancel: "Cancelar",
    save: "Guardar",
    delete: "Eliminar",
    edit: "Editar",
    close: "Fechar",
    send: "Enviar",
    copy: "Copiar",
  },
  games: {
    score: "Pontos",
    best: "Melhor",
    hi: "REC",
    tapOn: "Toque em",
    getReady: "Prepare-se...",
    tapToStart: "Toque para começar!",
    tapToJump: "Toque para pular!",
    gameOver: "FIM DE JOGO",
    tapToRestart: "Toque para recomeçar",
    speedSlow: "Devagar",
    speedSteady: "Estável",
    speedNormal: "Normal",
    speedQuick: "Rápido",
    speedFast: "Veloz",
    speedBlazing: "Ardente",
    speedOnFire: "Em chamas!",
    speedRocket: "Foguete",
    speedLegendary: "Lendário",
    cheers: ["Boa!", "Ótimo!", "Incrível!", "Uau!", "Magnífico!", "Acertou!", "Sim!", "Boom!", "Perfeito!", "Demais!"],
  },
  errors: {
    microphoneAccessDenied: "Acesso ao microfone negado",
    recognitionError: "Erro no reconhecimento de fala",
    recordingFailed: "Falha ao iniciar a gravação",
    noSpeechDetected: "Nenhuma fala detectada. Tente novamente.",
    llmConfigRequired: "Configure sua chave de API de IA nas configurações para usar este recurso.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Bem-vindo ao SaySay",
    welcomeSubtitle: "Prepare-se para qualquer conversa",
    getStarted: "Começar",
    // Feature slides
    featureTopicsPain: "Livros genéricos não combinam com sua vida",
    featureTopicsSolution: "Pratique o que você realmente precisa",
    featureTopicsDesc: "Descreva qualquer situação — pedir um café, ir ao médico, conversar com vizinhos — e receba um diálogo feito para você.",
    featureVoicePain: "Como é que se diz isso mesmo?",
    featureVoiceSolution: "Ouça cada palavra",
    featureVoiceDesc: "Toque em qualquer frase para ouvir a pronúncia nativa. Use sua voz para responder sem usar as mãos.",
    featureAiPain: "O que funciona para todos não funciona para você",
    featureAiSolution: "A IA constrói seu vocabulário",
    featureAiDesc: "Cada diálogo vem com palavras e expressões que você vai usar de verdade. Aprenda vocabulário que faz sentido na sua vida.",
    continue: "Continuar",
    inputNativeLangTitle: "Que língua fala?",
    inputNativeLangSubtitle: "Vamos ensinar-lhe neste idioma",
    inputTargetLangTitle: "O que quer aprender?",
    inputTargetLangSubtitle: "Escolha o idioma que quer praticar",
    // Input: Name
    inputNameTitle: "Como podemos te chamar?",
    inputNameSubtitle: "Você pode mudar depois",
    inputNamePlaceholder: "Seu nome",
    // Input: Languages
    inputLangTitle: "Você fala outros idiomas?",
    inputLangSubtitle: "Vamos usar as semelhanças para você aprender mais rápido",
    inputLangSelect: "Selecione os idiomas que você conhece além do português",
    inputLangFindMore: "Encontrar mais",
    inputLangSearch: "Buscar idioma...",
    skipStep: "Pular esta etapa",
    // Input: Style
    inputStyleTitle: "Como você quer soar?",
    inputStyleSubtitle: "Você pode mudar depois",
    speakingStyleBeginner: "Iniciante",
    speakingStyleBeginnerDesc: "Palavras simples, frases básicas — como alguém que está aprendendo",
    speakingStyleLocal: "Como um nativo",
    speakingStyleLocalDesc: "Expressões naturais, gírias, expressões — para se misturar",
    speakingStyleMix: "Misturar estilos",
    speakingStyleMixDesc: "Escolha um ou mais estilos",
    styleYourOption: "Sua opção...",
    styleTypePlaceholder: "Digite seu estilo...",
    // Input: Gender
    inputGenderTitle: "Quem você é nos diálogos?",
    inputGenderSubtitle: "Opcional — você pode mudar depois",
    genderMale: "Homem",
    genderFemale: "Mulher",
    genderOther: "Outro / Não-binário",
    genderPreferNot: "Prefiro não dizer",
    // Navigation
    back: "Voltar",
    skipAll: "Pular tudo",
    // Ready screen
    startLearning: "Começar a Aprender",
    youreAllSet: "Tudo pronto!",
    youreAllSetWithName: "Tudo pronto, {name}!",
    startFirstConversation: "Comece sua primeira conversa e veja como aprender idiomas pode ser natural.",
    analyticsOptIn: "Ajude a melhorar o SaySay compartilhando dados de uso anônimos (nenhuma informação pessoal é coletada)",
    // First conversation transition screen
    firstConversationTitle: "Ótimo! Agora vamos praticar a tua primeira conversa.",
    firstConversationSubtitle: "Escolhe um tema e vamos gerar um diálogo real para praticares.",
    firstConversationButton: "Vamos lá!",
    // Preview UI elements
    previewTopicTitle: "Sobre o que gostaria de falar?",
    previewTopicSubtitle: "Digite um cenário ou tema",
    previewExample: "Exemplo — toque para usar",
    previewExampleText: "Pedir um café e perguntar sobre recomendações locais",
    previewTypeOwn: "Ou digite o seu...",
    previewVoiceText: "Eu gostaria de um café...",
    previewDialogTranslation: "Bom dia! O que deseja?",
  },
  knownLanguages: {
    spanish: "Espanhol",
    french: "Francês",
    german: "Alemão",
    italian: "Italiano",
    portuguese: "Português",
    russian: "Russo",
    chinese: "Chinês",
    japanese: "Japonês",
    korean: "Coreano",
    arabic: "Árabe",
    hindi: "Hindi",
    dutch: "Holandês",
    polish: "Polonês",
    swedish: "Sueco",
    turkish: "Turco",
    greek: "Grego",
    czech: "Tcheco",
    romanian: "Romeno",
    hungarian: "Húngaro",
    finnish: "Finlandês",
    norwegian: "Norueguês",
    danish: "Dinamarquês",
    ukrainian: "Ucraniano",
    thai: "Tailandês",
    vietnamese: "Vietnamita",
    indonesian: "Indonésio",
    malay: "Malaio",
    tagalog: "Tagalo",
    hebrew: "Hebraico",
    persian: "Persa",
    bengali: "Bengali",
    punjabi: "Punjabi",
    tamil: "Tâmil",
    urdu: "Urdu",
    swahili: "Suaíli",
    afrikaans: "Africâner",
    catalan: "Catalão",
    croatian: "Croata",
    serbian: "Sérvio",
    slovak: "Eslovaco",
    slovenian: "Esloveno",
    bulgarian: "Búlgaro",
    lithuanian: "Lituano",
    latvian: "Letão",
    estonian: "Estoniano",
    icelandic: "Islandês",
    welsh: "Galês",
    irish: "Irlandês",
    scottishGaelic: "Gaélico escocês",
    basque: "Basco",
    maltese: "Maltês",
    albanian: "Albanês",
    macedonian: "Macedônio",
    georgian: "Georgiano",
    armenian: "Armênio",
    kazakh: "Cazaque",
    uzbek: "Uzbeque",
    mongolian: "Mongol",
    nepali: "Nepalês",
    sinhala: "Cingalês",
    burmese: "Birmanês",
    khmer: "Khmer",
    lao: "Laosiano",
  },
  coachMarks: {
    startNodeTitle: "Iniciar a conversa",
    startNodeBody: "Toque nesta bolha de mensagem para começar o diálogo.",
    speakerTitle: "Ouvir a pronúncia",
    speakerBody: "Toque no ícone do alto-falante para ouvir a frase em voz alta.",
    answerTitle: "Escolha sua resposta",
    answerBody: "Toque em uma das opções para escolher o que quer dizer.",
    tabsTitle: "Explorar mais",
    tabsBody: "Alterne entre o diálogo, exercícios de prática e vocabulário.",
    skip: "Pular",
    next: "Próximo",
    done: "Concluído",
  },
  passwordGate: {
    enterPassword: "Digite a senha para continuar",
    placeholder: "Senha",
  },
  dialogueFeedback: {
    title: "O que achou deste diálogo?",
    bad: "Ruim",
    meh: "Mais ou menos",
    good: "Bom",
    thanks: "Obrigado pelo seu feedback!",
    tellUsWhy: "Obrigado! Poderia nos dizer por quê?",
    commentPlaceholder: "O que poderia melhorar?",
    skip: "Pular",
    send: "Enviar",
  },
  dialog: {
    listening: "Ouvindo você...",
    stopRecording: "Parar gravação",
    practice: "Praticar",
    vocabulary: "Vocabulário",
    loadingVocabulary: "A carregar vocabulário...",
  },
  languageNames: {
    "en-US": "Inglês (EUA)",
    "en-GB": "Inglês (Reino Unido)",
    "es-ES": "Espanhol (Espanha)",
    "es-MX": "Espanhol (México)",
    "pt-PT": "Português (Portugal)",
    "pt-BR": "Português (Brasil)",
    "fr-FR": "Francês",
    "de-DE": "Alemão",
    "de-AT": "Alemão (Áustria)",
    "de-CH": "Alemão (Suíça)",
    "it-IT": "Italiano",
    "ja-JP": "Japonês",
    "zh-CN": "Chinês",
    "ko-KR": "Coreano",
    "he-IL": "Hebraico",
    "ru-RU": "Russo",
    "pl-PL": "Polonês",
    "uk-UA": "Ucraniano",
    "fi-FI": "Finlandês",
    "et-EE": "Estoniano",
    "lv-LV": "Letão",
    "el-GR": "Grego",
  },
};

// French
const fr: Translations = {
  nav: {
    dialogues: "Dialogues",
    grammar: "Grammaire",
    practice: "Pratique",
    alphabet: "Alphabet",
  },
  languageSelector: {
    title: "Langues",
    iSpeak: "Je parle",
    iWantToLearn: "Je veux apprendre",
    done: "Terminé",
    back: "Retour",
    moreComingSoon: "Plus de langues bientôt!",
  },
  topics: {
    title: "Sujets de Dialogue",
    subtitle: "Choisissez une conversation à pratiquer",
    tagline: "Apprenez à dire ce dont vous avez besoin",
    heroTitle: "Soyez prêt pour toute conversation",
    heroSubtitle: "Arrête d'acquiescer — commence à parler",
    createCustom: "Nouvelle conversation",
    chooseTopic: "Choisir un Sujet",
    available: "disponibles",
    words: "mots",
    expressions: "expressions",
    difficulty: {
      beginner: "Débutant",
      intermediate: "Intermédiaire",
      advanced: "Avancé",
    },
    dialogNodes: "échanges",
    regenerateWarning: "Créé pour une autre langue — touchez pour recréer",
    deleteTitle: "Supprimer le Sujet",
    deleteDescription: "Êtes-vous sûr de vouloir supprimer ce sujet ? Cette action ne peut pas être annulée.",
    regenerateTitle: "Régénérer le dialogue",
    regenerateDescription: "Un nouveau dialogue sera créé pour ce sujet. Le dialogue actuel sera remplacé.",
    regenerate: "Régénérer",
    edit: "Modifier",
    recent: "Récents",
    categories: "Catégories",
    byCategory: "Dialogues par catégorie",
    collapse: "Réduire",
  },
  grammar: {
    title: "Règles de Grammaire",
    subtitle: "Comment fonctionne le {language}",
    noRulesTitle: "Pas Encore de Règles",
    noRulesDescription: "Créez un sujet personnalisé en {language} pour voir les règles ici.",
    rulesCount: "{count} règles de grammaire",
    rulesSingular: "{count} règle de grammaire",
    tapToExpand: "Appuyez pour développer et apprendre.",
    examples: "Exemples du Dialogue",
    howToThink: "Comment y Penser",
    commonMistake: "Erreur Courante",
  },
  alphabet: {
    title: "Alphabet et Sons",
    subtitle: "Prononciation du {language}",
    comingSoonTitle: "Bientôt Disponible",
    comingSoonDescription: "Le guide de l'alphabet pour {language} n'est pas encore disponible.",
    examples: "Exemples",
    all: "Tout",
  },
  generator: {
    title: "De quoi voulez-vous parler ?",
    placeholder: "Décrivez une situation...\n\nExemples:\n• Commander un café\n• Demander son chemin\n• Prendre rendez-vous chez le médecin",
    proficiency: "Comment voulez-vous sonner ?",
    generate: "C'est parti",
    generating: "On prépare tout...",
    loadTopic: "Charger",
    back: "Retour",
    askAI: "Décrivez votre situation",
    responses: "réponses",
    modelTierFast: "Rapide",
    modelTierFastDesc: "Rapide et abordable",
    modelTierBalanced: "Équilibré",
    modelTierBalancedDesc: "Meilleur rapport qualité-prix",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Qualité maximale",
    loadingModels: "Chargement des modèles...",
    llmConfigWarning: "Configurez votre fournisseur LLM dans les paramètres pour activer la génération IA",
    topicTitle: "De quoi aimeriez-vous parler ?",
    topicHintMic: "Appuyez sur le micro et dites votre sujet",
    topicHintKeyboard: "Tapez un scénario ou un sujet",
    exampleTapToUse: "Exemple — appuyez pour utiliser",
    enterText: "Saisir texte",
    tapToStop: "Appuyez pour arrêter",
    processing: "Traitement...",
    tapAndTalk: "Appuyez et parlez",
    recording: "Enregistrement...",
    useVoice: "Utiliser la voix",
    attach: "Joindre",
    continue: "Continuer",
    apiKeyRequired: "Clé API requise",
    apiKeyRequiredDesc: "Configurez votre fournisseur LLM dans les paramètres pour activer la génération IA",
    difficultyTitle: "Comment voulez-vous sonner ?",
    difficultySubtitle: "Choisissez votre style de parole",
    styleBeginner: "Débutant",
    styleBeginnerDesc: "Mots simples — comme un débutant",
    styleLocal: "Comme un local",
    styleLocalDesc: "Parler naturel, argot, expressions — comme un natif",
    styleMix: "Mélanger",
    styleMixDesc: "Choisissez un ou plusieurs",
    styleCustomPlaceholder: "Ou tapez le vôtre...",
    richnessTitle: "Combien de contenu ?",
    richnessSubtitle: "Choisissez la quantité de vocabulaire et de dialogue",
    richnessQuick: "Rapide",
    richnessQuickDesc: "Dialogue court, vocabulaire essentiel",
    richnessStandard: "Standard",
    richnessStandardDesc: "Dialogue moyen, bonne couverture de vocabulaire",
    richnessComprehensive: "Complet",
    richnessComprehensiveDesc: "Long dialogue, vocabulaire étendu",
    modelTitle: "Choisissez le niveau de qualité",
    modelSubtitle: "Meilleure qualité = meilleur contenu, coût plus élevé",
    cachedAvailable: "Réponse en cache disponible — chargement instantané !",
    loadCached: "Charger le cache",
    previewPrompt: "Aperçu du prompt",
    fullPrompt: "Prompt complet",
    copied: "Copié !",
    systemPrompt: "Prompt système",
    userPrompt: "Prompt utilisateur",
    gettingReady: "On prépare tout...",
    connectingToAI: "Connexion à l'IA...",
    done: "Terminé !",
    letsGo: "C'est parti",
    goBack: "Retour",
    playWhileWaiting: "Joue à {game1} ou {game2} en attendant !",
    play: "Joue à",
    or: "ou",
    whileYouWait: "en attendant",
    exitGame: "Quitter le jeu",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "J'ai commandé des pneus en ligne dans un garage et ils sont arrivés. Je dois appeler pour prendre rendez-vous et les faire installer cette semaine.",
      "Je suis dans un café de spécialité et je veux commander un flat white au lait d'avoine et une pâtisserie. Je veux aussi demander s'ils ont des grains d'origine unique.",
      "Je suis à la gare et je dois me rendre à destination. Je veux savoir quel quai, combien coûte un aller-retour et si je peux payer par carte.",
      "Je suis dans un centre de karting avec des amis. On veut réserver une session de 20 minutes pour 4 personnes et demander les réductions de groupe.",
      "L'écran de mon téléphone est cassé et je suis dans un magasin de réparation. Je veux demander combien de temps ça prend, combien ça coûte et s'ils ont une garantie.",
      "Je suis à la pharmacie et j'ai besoin de quelque chose pour un mal de tête et aussi d'un antihistaminique. Je veux savoir lequel est le plus fort et si j'ai besoin d'une ordonnance.",
      "J'arrive dans un Airbnb et la clé n'est pas là où l'hôte l'a indiqué. Je dois appeler pour savoir comment entrer.",
      "Je suis dans un restaurant de fruits de mer au bord de la plage et je veux commander du poisson grillé, mais je veux savoir ce qui est frais aujourd'hui et demander des recommandations.",
      "Je suis dans une école de surf et je veux réserver un cours pour débutant demain matin. Je dois demander pour la location de combinaison et l'heure d'arrivée.",
      "Ma voiture de location a un pneu crevé sur l'autoroute. Je dois appeler l'assistance routière et expliquer où je suis et ce qui s'est passé.",
    ],
  },
  pwa: {
    installPrompt: "Installez SaySay pour une meilleure expérience",
    install: "Installer",
  },
  settings: {
    title: "Paramètres",
    theme: "Apparence",
    themeLight: "Clair",
    themeDark: "Sombre",
    themeSystem: "Auto",
    resetAll: "Effacer Toutes les Données",
    resetConfirm: "Cela effacera tous les paramètres et sujets sauvegardés. Êtes-vous sûr?",
    close: "Fermer",
    speechRecognition: "Reconnaissance Vocale",
    sttAzure: "Azure",
    sttAzureDesc: "Basé sur le cloud, haute qualité",
    sttBrowser: "Navigateur",
    sttBrowserDesc: "Intégré, fonctionne hors ligne",
    speechSpeed: "Vitesse de parole",
    analytics: "Analytique",
    shareUsageData: "Partager les données d'utilisation",
    analyticsDescription: "Aidez à améliorer SaySay en partageant des données d'utilisation anonymes",
    knownLanguages: "Langues connues",
    knownLanguagesDescription: "Sélectionnez les langues que vous connaissez déjà. Cela nous aide à fournir de meilleurs conseils de prononciation en utilisant des sons de ces langues.",
    noneSelected: "Aucune sélection",
    learning: "Apprentissage",
    showPronunciationHints: "Afficher les astuces de prononciation",
    showPronunciationHintsDescription: "Affiche comment prononcer les phrases en utilisant des sons que vous connaissez",
  },
  learning: {
    back: "Retour",
    dialogues: "Dialogues",
    vocabulary: "Vocabulaire",
    grammar: "Grammaire",
    practice: "Pratique",
    cards: "Cartes",
    listen: "Écouter",
    showTranslation: "Afficher la traduction",
    hideTranslation: "Masquer la traduction",
    pronunciation: "Prononciation",
    sayItLike: "Dites-le comme",
    allForms: "Toutes les Formes",
    example: "Exemple",
    inContext: "En Contexte",
    literalMeaning: "Sens Littéral",
    wordBreakdown: "Décomposition du Mot",
    fromDialog: "Du Dialogue",
    searchVocabulary: "Rechercher vocabulaire...",
    foundResults: "{count} résultats trouvés pour \"{query}\"",
    progress: "Progression",
    mastered: "{count} / {total} maîtrisés",
    words: "Mots",
    expressions: "Expressions",
    topical: "Bon à Savoir",
    wordsDescription: "Vocabulaire essentiel avec toutes les formes",
    expressionsDescription: "Expressions et idiomes",
    topicalDescription: "Vocabulaire associé",
    noWordsMatch: "Aucun mot ne correspond à votre recherche",
    noExpressionsMatch: "Aucune expression ne correspond à votre recherche",
    noTopicalMatch: "Aucun vocabulaire associé ne correspond",
    forms: "Formes",
    buildingBlocks: "Briques de base",
    showBuildingBlocks: "Afficher les briques de base",
    buildingBlocksDescription: "Mots courants nécessaires pour ce dialogue (articles, prépositions, etc.)",
    hideByDefault: "Je les connais déjà — masquer par défaut",
    longPressHint: "Appuyez longuement sur un mot pour le rechercher",
    hideAll: "Tout masquer",
    showAll: "Tout afficher",
    tapToReveal: "Appuyez pour révéler la traduction",
    practiceMode: "Mode Pratique",
    practiceDescription: "Naviguez dans des scénarios de conversation réels. Choisissez des réponses et voyez comment le dialogue se déroule.",
    howItWorks: "Comment ça marche:",
    practiceStep1: "Lisez le prompt de conversation",
    practiceStep2: "Choisissez la meilleure réponse parmi les options",
    practiceStep3: "Continuez jusqu'à la fin de la conversation",
    startPractice: "Commencer la Pratique",
    greatJob: "Excellent!",
    keepPracticing: "Continuez à pratiquer!",
    correctResponses: "{correct} sur {total} réponses correctes",
    yourConversation: "Votre conversation:",
    tryAgain: "Réessayer",
    newPath: "Nouveau Chemin",
    turn: "Tour {count}",
    score: "Score: {correct}/{total}",
    chooseResponse: "Choisissez votre réponse:",
    continueConversation: "Continuer la Conversation",
    seeResults: "Voir les Résultats",
    conversationStarts: "La conversation commence",
    you: "Vous",
    them: "Eux",
    end: "Fin",
    alsoSay: "Dites aussi",
    playing: "Lecture...",
    askAI: "Aide",
    response: "réponse",
    responses: "réponses",
    or: "ou",
    systemResponse: "Système/Réponse",
    yourOptions: "Vos options",
    tapToExpand: "Appuyez pour développer. Appuyez longuement sur un mot pour traduire.",
    startAgain: "Recommencer",
    pickYourAnswer: "Choisissez votre réponse",
    pickYourAnswerHint: "(appuyez sur une des réponses ci-dessous)",
    tapToAnswer: "Appuyez sur la réponse ci-dessous pour continuer",
    pickTheirAnswer: "Choisissez leur réponse",
    tapToStart: "Appuyez pour commencer",
    transcribing: "Transcription...",
    useVoice: "Utiliser la voix",
    saySomethingElse: "Je veux dire autre chose",
    said: "a dit",
    whatToSay: "Que voulez-vous dire?",
    typeYourResponse: "Tapez votre réponse...",
    customPhraseHint: "Tapez dans votre langue et nous traduirons naturellement",
    fixThisResponse: "Corriger cette réponse",
    whatsWrong: "Que devrait-elle dire à la place?",
    fixResponseHint: "Décrivez la correction ou fournissez la phrase correcte",
    // Practice tab modes
    practiceTab: "Pratique",
    practiceWords: "Mots",
    practiceSpelling: "Orthographe",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Quiz avec images",
    practiceSpellingDesc: "Prononcez les mots à voix haute",
    practiceKaraokeDesc: "Prononcez des phrases mot par mot",
    introduceWord: "Nouveau mot",
    composeWord: "Composer un mot",
    describeImage: "Décrivez ce qui est sur l'image",
    pickWordForSentence: "Choisissez un mot pour la phrase",
    iNeedAHint: "J'ai besoin d'un indice",
    iDontKnow: "Je ne connais pas ce mot",
    tapHoldSayWord: "Appuyez, maintenez et dites le mot",
    tapHoldSayPhrase: "Appuyez, maintenez et dites la phrase",
    weHeard: "Nous avons entendu :",
    skipWord: "Passer",
    nextWord: "Suivant",
    practiceComplete: "Pratique terminée !",
    wordsCorrectCount: "{correct} sur {total} correctes",
    practiceAgain: "Repratiquer",
    done: "Terminé",
    correct: "Correct !",
    chooseModule: "Choisir un module",
    moduleHint: "Pratiquez en petits groupes pour mieux retenir",
    wordsCount: "mots",
    markKnown: "Je connais",
    markTough: "Difficile",
    markLiked: "J'aime",
    knownWordsHidden: "{count} mots connus masqués",
    noWordsToReview: "Pas encore de mots à réviser",
    noWordsToReviewDesc: "Générez des dialogues pour enrichir votre vocabulaire",
    generatingMore: "Génération...",
  },
  common: {
    loading: "Chargement...",
    error: "Une erreur est survenue",
    retry: "Réessayer",
    cancel: "Annuler",
    save: "Enregistrer",
    delete: "Supprimer",
    edit: "Modifier",
    close: "Fermer",
    send: "Envoyer",
    copy: "Copier",
  },
  games: {
    score: "Score",
    best: "Meilleur",
    hi: "REC",
    tapOn: "Appuyez sur",
    getReady: "Préparez-vous...",
    tapToStart: "Appuyez pour commencer !",
    tapToJump: "Appuyez pour sauter !",
    gameOver: "FIN DE PARTIE",
    tapToRestart: "Appuyez pour recommencer",
    speedSlow: "Lent",
    speedSteady: "Régulier",
    speedNormal: "Normal",
    speedQuick: "Rapide",
    speedFast: "Véloce",
    speedBlazing: "Fulgurant",
    speedOnFire: "En feu !",
    speedRocket: "Fusée",
    speedLegendary: "Légendaire",
    cheers: ["Bien !", "Génial !", "Incroyable !", "Ouah !", "Superbe !", "Bravo !", "Oui !", "Boom !", "Parfait !", "Formidable !"],
  },
  errors: {
    microphoneAccessDenied: "Accès au microphone refusé",
    recognitionError: "Erreur de reconnaissance vocale",
    recordingFailed: "Échec du démarrage de l'enregistrement",
    noSpeechDetected: "Aucune parole détectée. Veuillez réessayer.",
    llmConfigRequired: "Configurez votre clé API LLM dans les paramètres pour utiliser cette fonctionnalité.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Bienvenue sur SaySay",
    welcomeSubtitle: "Soyez prêt pour toute conversation",
    getStarted: "Commencer",
    // Feature slides
    featureTopicsPain: "Les manuels génériques ne collent pas à votre vie",
    featureTopicsSolution: "Pratiquez ce dont vous avez vraiment besoin",
    featureTopicsDesc: "Décrivez n'importe quelle situation — commander un café, voir un médecin, discuter avec les voisins — et recevez un dialogue sur mesure.",
    featureVoicePain: "Mais ça se dit comment, au juste ?",
    featureVoiceSolution: "Écoutez chaque mot",
    featureVoiceDesc: "Appuyez sur n'importe quelle phrase pour entendre la prononciation native. Utilisez votre voix pour répondre en mains libres.",
    featureAiPain: "Le tout-venant ne marche pas",
    featureAiSolution: "L'IA construit votre vocabulaire",
    featureAiDesc: "Chaque dialogue vient avec des mots et expressions que vous utiliserez vraiment. Apprenez du vocabulaire adapté à votre vie.",
    continue: "Continuer",
    inputNativeLangTitle: "Quelle langue parlez-vous ?",
    inputNativeLangSubtitle: "Nous vous enseignerons dans cette langue",
    inputTargetLangTitle: "Que voulez-vous apprendre ?",
    inputTargetLangSubtitle: "Choisissez la langue que vous souhaitez pratiquer",
    // Input: Name
    inputNameTitle: "Comment vous appeler ?",
    inputNameSubtitle: "Vous pourrez changer plus tard",
    inputNamePlaceholder: "Votre prénom",
    // Input: Languages
    inputLangTitle: "Parlez-vous d'autres langues ?",
    inputLangSubtitle: "Nous utiliserons les similitudes pour vous aider à apprendre plus vite",
    inputLangSelect: "Sélectionnez les langues que vous connaissez en plus du français",
    inputLangFindMore: "En trouver d'autres",
    inputLangSearch: "Rechercher une langue...",
    skipStep: "Passer cette étape",
    // Input: Style
    inputStyleTitle: "Comment voulez-vous parler ?",
    inputStyleSubtitle: "Vous pourrez changer plus tard",
    speakingStyleBeginner: "Débutant",
    speakingStyleBeginnerDesc: "Mots simples, phrases de base — clairement quelqu'un qui apprend",
    speakingStyleLocal: "Comme un natif",
    speakingStyleLocalDesc: "Expressions naturelles, argot, idiomes — passer pour un local",
    speakingStyleMix: "Mixer les styles",
    speakingStyleMixDesc: "Choisissez un ou plusieurs styles",
    styleYourOption: "Votre option...",
    styleTypePlaceholder: "Tapez votre style...",
    // Input: Gender
    inputGenderTitle: "Qui êtes-vous dans les dialogues ?",
    inputGenderSubtitle: "Facultatif — vous pourrez changer plus tard",
    genderMale: "Homme",
    genderFemale: "Femme",
    genderOther: "Autre / Non-binaire",
    genderPreferNot: "Je préfère ne pas dire",
    // Navigation
    back: "Retour",
    skipAll: "Tout passer",
    // Ready screen
    startLearning: "Commencer à Apprendre",
    youreAllSet: "Vous êtes prêt !",
    youreAllSetWithName: "Vous êtes prêt, {name} !",
    startFirstConversation: "Lancez votre première conversation et découvrez à quel point apprendre une langue peut être naturel.",
    analyticsOptIn: "Aidez à améliorer SaySay en partageant des données d'utilisation anonymes (aucune info personnelle collectée)",
    // First conversation transition screen
    firstConversationTitle: "Super ! Maintenant, pratiquons ta première conversation.",
    firstConversationSubtitle: "Choisis un sujet et on te génère un vrai dialogue pour t'entraîner.",
    firstConversationButton: "C'est parti !",
    // Preview UI elements
    previewTopicTitle: "De quoi aimeriez-vous parler ?",
    previewTopicSubtitle: "Tapez un scénario ou un sujet",
    previewExample: "Exemple — appuyez pour utiliser",
    previewExampleText: "Commander un café et demander des recommandations locales",
    previewTypeOwn: "Ou tapez le vôtre...",
    previewVoiceText: "Je voudrais un café...",
    previewDialogTranslation: "Bonjour ! Que désirez-vous ?",
  },
  knownLanguages: {
    spanish: "Espagnol",
    french: "Français",
    german: "Allemand",
    italian: "Italien",
    portuguese: "Portugais",
    russian: "Russe",
    chinese: "Chinois",
    japanese: "Japonais",
    korean: "Coréen",
    arabic: "Arabe",
    hindi: "Hindi",
    dutch: "Néerlandais",
    polish: "Polonais",
    swedish: "Suédois",
    turkish: "Turc",
    greek: "Grec",
    czech: "Tchèque",
    romanian: "Roumain",
    hungarian: "Hongrois",
    finnish: "Finnois",
    norwegian: "Norvégien",
    danish: "Danois",
    ukrainian: "Ukrainien",
    thai: "Thaï",
    vietnamese: "Vietnamien",
    indonesian: "Indonésien",
    malay: "Malais",
    tagalog: "Tagalog",
    hebrew: "Hébreu",
    persian: "Persan",
    bengali: "Bengali",
    punjabi: "Pendjabi",
    tamil: "Tamoul",
    urdu: "Ourdou",
    swahili: "Swahili",
    afrikaans: "Afrikaans",
    catalan: "Catalan",
    croatian: "Croate",
    serbian: "Serbe",
    slovak: "Slovaque",
    slovenian: "Slovène",
    bulgarian: "Bulgare",
    lithuanian: "Lituanien",
    latvian: "Letton",
    estonian: "Estonien",
    icelandic: "Islandais",
    welsh: "Gallois",
    irish: "Irlandais",
    scottishGaelic: "Gaélique écossais",
    basque: "Basque",
    maltese: "Maltais",
    albanian: "Albanais",
    macedonian: "Macédonien",
    georgian: "Géorgien",
    armenian: "Arménien",
    kazakh: "Kazakh",
    uzbek: "Ouzbek",
    mongolian: "Mongol",
    nepali: "Népalais",
    sinhala: "Cingalais",
    burmese: "Birman",
    khmer: "Khmer",
    lao: "Lao",
  },
  coachMarks: {
    startNodeTitle: "Commencer la conversation",
    startNodeBody: "Appuyez sur cette bulle de message pour lancer le dialogue.",
    speakerTitle: "Écouter la prononciation",
    speakerBody: "Appuyez sur l'icône du haut-parleur pour entendre la phrase à voix haute.",
    answerTitle: "Choisissez votre réponse",
    answerBody: "Appuyez sur une des options pour choisir ce que vous voulez dire.",
    tabsTitle: "Explorer plus",
    tabsBody: "Basculez entre le dialogue, les exercices de pratique et le vocabulaire.",
    skip: "Passer",
    next: "Suivant",
    done: "Terminé",
  },
  passwordGate: {
    enterPassword: "Entrez le mot de passe pour continuer",
    placeholder: "Mot de passe",
  },
  dialogueFeedback: {
    title: "Que pensez-vous de ce dialogue ?",
    bad: "Mauvais",
    meh: "Bof",
    good: "Bien",
    thanks: "Merci pour votre avis !",
    tellUsWhy: "Merci ! Pourriez-vous nous dire pourquoi ?",
    commentPlaceholder: "Qu'est-ce qui pourrait être amélioré ?",
    skip: "Passer",
    send: "Envoyer",
  },
  dialog: {
    listening: "Je vous écoute...",
    stopRecording: "Arrêter l'enregistrement",
    practice: "Pratique",
    vocabulary: "Vocabulaire",
    loadingVocabulary: "Chargement du vocabulaire...",
  },
  languageNames: {
    "en-US": "Anglais (États-Unis)",
    "en-GB": "Anglais (Royaume-Uni)",
    "es-ES": "Espagnol (Espagne)",
    "es-MX": "Espagnol (Mexique)",
    "pt-PT": "Portugais (Portugal)",
    "pt-BR": "Portugais (Brésil)",
    "fr-FR": "Français",
    "de-DE": "Allemand",
    "de-AT": "Allemand (Autriche)",
    "de-CH": "Allemand (Suisse)",
    "it-IT": "Italien",
    "ja-JP": "Japonais",
    "zh-CN": "Chinois",
    "ko-KR": "Coréen",
    "he-IL": "Hébreu",
    "ru-RU": "Russe",
    "pl-PL": "Polonais",
    "uk-UA": "Ukrainien",
    "fi-FI": "Finnois",
    "et-EE": "Estonien",
    "lv-LV": "Letton",
    "el-GR": "Grec",
  },
};

// German
const de: Translations = {
  nav: {
    dialogues: "Dialoge",
    grammar: "Grammatik",
    practice: "Übung",
    alphabet: "Alphabet",
  },
  languageSelector: {
    title: "Sprachen",
    iSpeak: "Ich spreche",
    iWantToLearn: "Ich möchte lernen",
    done: "Fertig",
    back: "Zurück",
    moreComingSoon: "Mehr Sprachen kommen bald!",
  },
  topics: {
    title: "Dialogthemen",
    subtitle: "Wähle ein Gespräch zum Üben",
    tagline: "Lerne zu sagen, was du brauchst",
    heroTitle: "Sei bereit für jedes Gespräch",
    heroSubtitle: "Schluss mit Nicken — fang an zu reden",
    createCustom: "Neues Gespräch",
    chooseTopic: "Wähle ein Thema",
    available: "verfügbar",
    words: "Wörter",
    expressions: "Ausdrücke",
    difficulty: {
      beginner: "Anfänger",
      intermediate: "Mittelstufe",
      advanced: "Fortgeschritten",
    },
    dialogNodes: "Züge",
    regenerateWarning: "Für andere Sprache erstellt — tippen zum Neuerstellen",
    deleteTitle: "Thema Löschen",
    deleteDescription: "Sind Sie sicher, dass Sie dieses Thema löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
    regenerateTitle: "Dialog neu generieren",
    regenerateDescription: "Es wird ein neuer Dialog für dieses Thema erstellt. Der aktuelle Dialog wird ersetzt.",
    regenerate: "Neu generieren",
    edit: "Bearbeiten",
    recent: "Neueste",
    categories: "Kategorien",
    byCategory: "Dialoge nach Kategorie",
    collapse: "Einklappen",
  },
  grammar: {
    title: "Grammatikregeln",
    subtitle: "Wie {language} funktioniert",
    noRulesTitle: "Noch Keine Regeln",
    noRulesDescription: "Erstelle ein Thema auf {language}, um hier Grammatikregeln zu sehen.",
    rulesCount: "{count} Grammatikregeln",
    rulesSingular: "{count} Grammatikregel",
    tapToExpand: "Tippen zum Erweitern und Lernen.",
    examples: "Beispiele aus dem Dialog",
    howToThink: "So Denkt Man Darüber",
    commonMistake: "Häufiger Fehler",
  },
  alphabet: {
    title: "Alphabet & Laute",
    subtitle: "{language} Aussprache",
    comingSoonTitle: "Demnächst",
    comingSoonDescription: "Der Alphabet-Leitfaden für {language} ist noch nicht verfügbar.",
    examples: "Beispiele",
    all: "Alles",
  },
  generator: {
    title: "Worüber möchtest du sprechen?",
    placeholder: "Beschreibe eine Situation...\n\nBeispiele:\n• Kaffee bestellen\n• Nach dem Weg fragen\n• Arzttermin vereinbaren",
    proficiency: "Wie willst du klingen?",
    generate: "Los geht's",
    generating: "Wird alles vorbereitet...",
    loadTopic: "Laden",
    back: "Zurück",
    askAI: "Beschreibe deine Situation",
    responses: "Antworten",
    modelTierFast: "Schnell",
    modelTierFastDesc: "Schnell und günstig",
    modelTierBalanced: "Ausgewogen",
    modelTierBalancedDesc: "Bestes Preis-Leistungs-Verhältnis",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Höchste Qualität",
    loadingModels: "Modelle werden geladen...",
    llmConfigWarning: "Konfigurieren Sie Ihren LLM-Anbieter in den Einstellungen, um die KI-Generierung zu aktivieren",
    topicTitle: "Worüber möchtest du sprechen?",
    topicHintMic: "Tippe auf das Mikrofon und sprich dein Thema",
    topicHintKeyboard: "Schreibe ein Szenario oder Thema",
    exampleTapToUse: "Beispiel — tippen zum Verwenden",
    enterText: "Text eingeben",
    tapToStop: "Tippen zum Stoppen",
    processing: "Verarbeitung...",
    tapAndTalk: "Tippen und sprechen",
    recording: "Aufnahme...",
    useVoice: "Sprache nutzen",
    attach: "Anhängen",
    continue: "Weiter",
    apiKeyRequired: "API-Schlüssel erforderlich",
    apiKeyRequiredDesc: "Konfiguriere deinen LLM-Anbieter in den Einstellungen, um KI-Generierung zu aktivieren",
    difficultyTitle: "Wie willst du klingen?",
    difficultySubtitle: "Wähle deinen Sprechstil",
    styleBeginner: "Anfänger",
    styleBeginnerDesc: "Einfache Wörter — wie ein Anfänger",
    styleLocal: "Wie ein Einheimischer",
    styleLocalDesc: "Natürliche Sprache, Slang, Redewendungen — wie ein Muttersprachler",
    styleMix: "Mischen",
    styleMixDesc: "Wähle einen oder mehrere",
    styleCustomPlaceholder: "Oder schreibe deinen eigenen...",
    richnessTitle: "Wie viel Inhalt?",
    richnessSubtitle: "Wähle die Menge an Vokabeln und Dialog",
    richnessQuick: "Schnell",
    richnessQuickDesc: "Kurzer Dialog, wesentliches Vokabular",
    richnessStandard: "Standard",
    richnessStandardDesc: "Mittlerer Dialog, gute Vokabelabdeckung",
    richnessComprehensive: "Umfassend",
    richnessComprehensiveDesc: "Langer Dialog, umfangreiches Vokabular",
    modelTitle: "Wähle die Qualitätsstufe",
    modelSubtitle: "Höhere Qualität = besserer Inhalt, höhere Kosten",
    cachedAvailable: "Zwischengespeicherte Antwort verfügbar — sofortiges Laden!",
    loadCached: "Cache laden",
    previewPrompt: "Prompt-Vorschau",
    fullPrompt: "Vollständiger Prompt",
    copied: "Kopiert!",
    systemPrompt: "System-Prompt",
    userPrompt: "Benutzer-Prompt",
    gettingReady: "Alles wird vorbereitet...",
    connectingToAI: "Verbindung zur KI...",
    done: "Fertig!",
    letsGo: "Los geht's",
    goBack: "Zurück",
    playWhileWaiting: "Spiel {game1} oder {game2} während du wartest!",
    play: "Spiel",
    or: "oder",
    whileYouWait: "während du wartest",
    exitGame: "Spiel beenden",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Ich habe online Reifen bei einer Werkstatt bestellt und sie sind angekommen. Ich muss anrufen und einen Termin machen, um sie diese Woche montieren zu lassen.",
      "Ich bin in einem Spezialitätencafé und möchte einen Flat White mit Hafermilch und ein Gebäck bestellen. Ich möchte auch fragen, ob sie Single-Origin-Bohnen haben.",
      "Ich bin am Bahnhof und muss zu meinem Ziel. Ich möchte wissen, welches Gleis, was eine Rückfahrkarte kostet und ob ich mit Karte zahlen kann.",
      "Ich bin mit Freunden bei einer Kartbahn. Wir wollen eine 20-minütige Session für 4 Personen buchen und nach Gruppenrabatten fragen.",
      "Mein Handybildschirm ist kaputt und ich bin in einem Reparaturladen. Ich möchte fragen, wie lange es dauert, wie viel es kostet und ob sie eine Garantie haben.",
      "Ich bin in der Apotheke und brauche etwas gegen starke Kopfschmerzen und auch ein Antihistaminikum. Ich möchte wissen, welches am stärksten ist und ob ich ein Rezept brauche.",
      "Ich checke in einem Airbnb ein und der Schlüssel ist nicht dort, wo der Gastgeber gesagt hat. Ich muss anrufen, um herauszufinden, wie ich reinkomme.",
      "Ich bin in einem Fischrestaurant am Strand und möchte gegrillten Fisch bestellen, aber ich möchte wissen, was heute frisch ist und um Empfehlungen bitten.",
      "Ich bin bei einer Surfschule und möchte für morgen früh eine Anfängerstunde buchen. Ich muss nach dem Neoprenanzug-Verleih und der Ankunftszeit fragen.",
      "Mein Mietwagen hat auf der Autobahn einen platten Reifen. Ich muss den Pannendienst anrufen und erklären, wo ich bin und was passiert ist.",
    ],
  },
  pwa: {
    installPrompt: "Installiere SaySay für ein besseres Erlebnis",
    install: "Installieren",
  },
  settings: {
    title: "Einstellungen",
    theme: "Erscheinungsbild",
    themeLight: "Hell",
    themeDark: "Dunkel",
    themeSystem: "Auto",
    resetAll: "Alle Daten Löschen",
    resetConfirm: "Dies löscht alle Einstellungen und Themen. Bist du sicher?",
    close: "Schließen",
    speechRecognition: "Spracherkennung",
    sttAzure: "Azure",
    sttAzureDesc: "Cloud-basiert, hohe Qualität",
    sttBrowser: "Browser",
    sttBrowserDesc: "Eingebaut, funktioniert offline",
    speechSpeed: "Sprechgeschwindigkeit",
    analytics: "Analytik",
    shareUsageData: "Nutzungsdaten teilen",
    analyticsDescription: "Helfen Sie SaySay zu verbessern, indem Sie anonyme Nutzungsdaten teilen",
    knownLanguages: "Bekannte Sprachen",
    knownLanguagesDescription: "Wählen Sie Sprachen aus, die Sie bereits kennen. Dies hilft uns, bessere Aussprache-Tipps mit Klängen aus diesen Sprachen zu geben.",
    noneSelected: "Keine ausgewählt",
    learning: "Lernen",
    showPronunciationHints: "Aussprachehinweise anzeigen",
    showPronunciationHintsDescription: "Zeigt an, wie man Phrasen mit bekannten Lauten ausspricht",
  },
  learning: {
    back: "Zurück",
    dialogues: "Dialoge",
    vocabulary: "Vokabeln",
    grammar: "Grammatik",
    practice: "Übung",
    cards: "Cards",
    listen: "Anhören",
    showTranslation: "Übersetzung zeigen",
    hideTranslation: "Übersetzung verbergen",
    pronunciation: "Aussprache",
    sayItLike: "Sag es wie",
    allForms: "Alle Formen",
    example: "Beispiel",
    inContext: "Im Kontext",
    literalMeaning: "Wörtliche Bedeutung",
    wordBreakdown: "Wortzerlegung",
    fromDialog: "Aus dem Dialog",
    searchVocabulary: "Vokabeln suchen...",
    foundResults: "{count} Ergebnisse für \"{query}\" gefunden",
    progress: "Fortschritt",
    mastered: "{count} / {total} gemeistert",
    words: "Wörter",
    expressions: "Ausdrücke",
    topical: "Gut zu Wissen",
    wordsDescription: "Kernvokabular mit allen Formen",
    expressionsDescription: "Redewendungen und Ausdrücke",
    topicalDescription: "Verwandtes Vokabular",
    noWordsMatch: "Keine Wörter entsprechen Ihrer Suche",
    noExpressionsMatch: "Keine Ausdrücke entsprechen Ihrer Suche",
    noTopicalMatch: "Kein verwandter Wortschatz entspricht Ihrer Suche",
    forms: "Formen",
    buildingBlocks: "Bausteine",
    showBuildingBlocks: "Bausteine anzeigen",
    buildingBlocksDescription: "Häufige Wörter für diesen Dialog (Artikel, Präpositionen usw.)",
    hideByDefault: "Kenne ich schon — standardmäßig ausblenden",
    longPressHint: "Lange auf ein Wort drücken zum Nachschlagen",
    hideAll: "Alle verbergen",
    showAll: "Alle zeigen",
    tapToReveal: "Tippen zum Anzeigen der Übersetzung",
    practiceMode: "Übungsmodus",
    practiceDescription: "Navigieren Sie durch echte Gesprächsszenarien. Wählen Sie Antworten und sehen Sie, wie sich der Dialog entwickelt.",
    howItWorks: "So funktioniert es:",
    practiceStep1: "Lesen Sie die Gesprächsaufforderung",
    practiceStep2: "Wählen Sie die beste Antwort aus den Optionen",
    practiceStep3: "Fahren Sie fort bis zum Ende des Gesprächs",
    startPractice: "Übung Starten",
    greatJob: "Sehr gut!",
    keepPracticing: "Weiter üben!",
    correctResponses: "{correct} von {total} richtigen Antworten",
    yourConversation: "Ihr Gespräch:",
    tryAgain: "Erneut Versuchen",
    newPath: "Neuer Weg",
    turn: "Zug {count}",
    score: "Punktzahl: {correct}/{total}",
    chooseResponse: "Wählen Sie Ihre Antwort:",
    continueConversation: "Gespräch Fortsetzen",
    seeResults: "Ergebnisse Sehen",
    conversationStarts: "Gespräch beginnt",
    you: "Du",
    them: "Sie",
    end: "Ende",
    alsoSay: "Sag auch",
    playing: "Spielt ab...",
    askAI: "Hilfe",
    response: "Antwort",
    responses: "Antworten",
    or: "oder",
    systemResponse: "System/Antwort",
    yourOptions: "Deine Optionen",
    tapToExpand: "Tippen zum Erweitern. Lange drücken für Übersetzung.",
    startAgain: "Neu beginnen",
    pickYourAnswer: "Wähle deine Antwort",
    pickYourAnswerHint: "(tippe auf eine der Antworten unten)",
    tapToAnswer: "Tippe auf die Antwort unten, um fortzufahren",
    pickTheirAnswer: "Wähle ihre Antwort",
    tapToStart: "Tippen zum Starten",
    transcribing: "Transkribiere...",
    useVoice: "Spracheingabe",
    saySomethingElse: "Ich möchte etwas anderes sagen",
    said: "sagte",
    whatToSay: "Was möchten Sie sagen?",
    typeYourResponse: "Geben Sie Ihre Antwort ein...",
    customPhraseHint: "Tippen Sie in Ihrer Sprache und wir übersetzen es natürlich",
    fixThisResponse: "Diese Antwort korrigieren",
    whatsWrong: "Was sollte es stattdessen sagen?",
    fixResponseHint: "Beschreiben Sie die Korrektur oder geben Sie den richtigen Satz an",
    // Practice tab modes
    practiceTab: "Übung",
    practiceWords: "Wörter",
    practiceSpelling: "Rechtschreibung",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Quiz mit Bildern",
    practiceSpellingDesc: "Wörter laut aussprechen",
    practiceKaraokeDesc: "Sätze Wort für Wort sprechen",
    introduceWord: "Neues Wort",
    composeWord: "Ein Wort zusammensetzen",
    describeImage: "Beschreibe, was auf dem Bild ist",
    pickWordForSentence: "Wähle ein Wort für den Satz",
    iNeedAHint: "Ich brauche einen Hinweis",
    iDontKnow: "Ich kenne dieses Wort nicht",
    tapHoldSayWord: "Tippen, halten und das Wort sagen",
    tapHoldSayPhrase: "Tippen, halten und den Satz sagen",
    weHeard: "Wir haben gehört:",
    skipWord: "Überspringen",
    nextWord: "Weiter",
    practiceComplete: "Übung abgeschlossen!",
    wordsCorrectCount: "{correct} von {total} richtig",
    practiceAgain: "Nochmal üben",
    done: "Fertig",
    correct: "Richtig!",
    chooseModule: "Modul wählen",
    moduleHint: "Übe in kleinen Gruppen für besseres Behalten",
    wordsCount: "Wörter",
    markKnown: "Kenne ich",
    markTough: "Schwierig",
    markLiked: "Gefällt mir",
    knownWordsHidden: "{count} bekannte Wörter ausgeblendet",
    noWordsToReview: "Noch keine Wörter zum Üben",
    noWordsToReviewDesc: "Erstelle einige Dialoge, um deinen Wortschatz aufzubauen",
    generatingMore: "Wird generiert...",
  },
  common: {
    loading: "Lädt...",
    error: "Ein Fehler ist aufgetreten",
    retry: "Erneut versuchen",
    cancel: "Abbrechen",
    save: "Speichern",
    delete: "Löschen",
    edit: "Bearbeiten",
    close: "Schließen",
    send: "Senden",
    copy: "Kopieren",
  },
  games: {
    score: "Punkte",
    best: "Beste",
    hi: "REK",
    tapOn: "Tippe auf",
    getReady: "Mach dich bereit...",
    tapToStart: "Tippe zum Starten!",
    tapToJump: "Tippe zum Springen!",
    gameOver: "SPIEL VORBEI",
    tapToRestart: "Tippe zum Neustart",
    speedSlow: "Langsam",
    speedSteady: "Stetig",
    speedNormal: "Normal",
    speedQuick: "Schnell",
    speedFast: "Flott",
    speedBlazing: "Rasant",
    speedOnFire: "In Flammen!",
    speedRocket: "Rakete",
    speedLegendary: "Legendär",
    cheers: ["Toll!", "Klasse!", "Wahnsinn!", "Wow!", "Super!", "Erwischt!", "Ja!", "Boom!", "Perfekt!", "Genial!"],
  },
  errors: {
    microphoneAccessDenied: "Mikrofonzugriff verweigert",
    recognitionError: "Spracherkennungsfehler",
    recordingFailed: "Aufnahme konnte nicht gestartet werden",
    noSpeechDetected: "Keine Sprache erkannt. Bitte erneut versuchen.",
    llmConfigRequired: "Konfigurieren Sie Ihren LLM-API-Schlüssel in den Einstellungen, um diese Funktion zu nutzen.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Willkommen bei SaySay",
    welcomeSubtitle: "Sei bereit für jedes Gespräch",
    getStarted: "Los geht's",
    // Feature slides
    featureTopicsPain: "Lehrbücher passen nicht zu deinem Leben",
    featureTopicsSolution: "Übe, was du wirklich brauchst",
    featureTopicsDesc: "Beschreibe jede Situation — Kaffee bestellen, zum Arzt gehen, mit Nachbarn plaudern — und bekomme einen Dialog, der zu dir passt.",
    featureVoicePain: "Wie sagt man das eigentlich?",
    featureVoiceSolution: "Höre jedes Wort",
    featureVoiceDesc: "Tippe auf einen Satz, um die muttersprachliche Aussprache zu hören. Antworte freihändig mit deiner Stimme.",
    featureAiPain: "Einheitsgrößen funktionieren nicht",
    featureAiSolution: "KI baut deinen Wortschatz auf",
    featureAiDesc: "Jeder Dialog enthält Wörter und Ausdrücke, die du wirklich verwenden wirst. Lerne Vokabeln, die zu deinem Leben passen.",
    continue: "Weiter",
    inputNativeLangTitle: "Welche Sprache sprichst du?",
    inputNativeLangSubtitle: "Wir unterrichten dich in dieser Sprache",
    inputTargetLangTitle: "Was möchtest du lernen?",
    inputTargetLangSubtitle: "Wähle die Sprache, die du üben möchtest",
    // Input: Name
    inputNameTitle: "Wie sollen wir dich nennen?",
    inputNameSubtitle: "Du kannst es später ändern",
    inputNamePlaceholder: "Dein Name",
    // Input: Languages
    inputLangTitle: "Sprichst du andere Sprachen?",
    inputLangSubtitle: "Wir nutzen Ähnlichkeiten, um dir schneller beim Lernen zu helfen",
    inputLangSelect: "Wähle Sprachen, die du neben Deutsch kennst",
    inputLangFindMore: "Mehr finden",
    inputLangSearch: "Sprache suchen...",
    skipStep: "Diesen Schritt überspringen",
    // Input: Style
    inputStyleTitle: "Wie möchtest du klingen?",
    inputStyleSubtitle: "Du kannst es später ändern",
    speakingStyleBeginner: "Anfänger",
    speakingStyleBeginnerDesc: "Einfache Wörter, grundlegende Sätze — erkennbar ein Lerner",
    speakingStyleLocal: "Wie ein Einheimischer",
    speakingStyleLocalDesc: "Natürliche Ausdrücke, Umgangssprache, Redewendungen — nicht als Ausländer auffallen",
    speakingStyleMix: "Stile mischen",
    speakingStyleMixDesc: "Wähle einen oder mehrere Stile",
    styleYourOption: "Deine Option...",
    styleTypePlaceholder: "Schreib deinen Stil...",
    // Input: Gender
    inputGenderTitle: "Wer bist du in den Dialogen?",
    inputGenderSubtitle: "Optional — du kannst es später ändern",
    genderMale: "Mann",
    genderFemale: "Frau",
    genderOther: "Andere / Nicht-binär",
    genderPreferNot: "Keine Angabe",
    // Navigation
    back: "Zurück",
    skipAll: "Alles überspringen",
    // Ready screen
    startLearning: "Mit dem Lernen beginnen",
    youreAllSet: "Alles bereit!",
    youreAllSetWithName: "Alles bereit, {name}!",
    startFirstConversation: "Starte dein erstes Gespräch und erlebe, wie natürlich Sprachenlernen sein kann.",
    analyticsOptIn: "Hilf SaySay zu verbessern, indem du anonyme Nutzungsdaten teilst (keine persönlichen Daten werden gesammelt)",
    // First conversation transition screen
    firstConversationTitle: "Super! Jetzt üben wir dein erstes Gespräch.",
    firstConversationSubtitle: "Wähle ein Thema und wir erstellen einen echten Dialog zum Üben.",
    firstConversationButton: "Los geht's!",
    // Preview UI elements
    previewTopicTitle: "Worüber möchtest du sprechen?",
    previewTopicSubtitle: "Gib ein Szenario oder Thema ein",
    previewExample: "Beispiel — tippen zum Verwenden",
    previewExampleText: "Einen Kaffee bestellen und nach lokalen Empfehlungen fragen",
    previewTypeOwn: "Oder gib dein eigenes ein...",
    previewVoiceText: "Ich hätte gerne einen Kaffee...",
    previewDialogTranslation: "Guten Morgen! Was darf es sein?",
  },
  knownLanguages: {
    spanish: "Spanisch",
    french: "Französisch",
    german: "Deutsch",
    italian: "Italienisch",
    portuguese: "Portugiesisch",
    russian: "Russisch",
    chinese: "Chinesisch",
    japanese: "Japanisch",
    korean: "Koreanisch",
    arabic: "Arabisch",
    hindi: "Hindi",
    dutch: "Niederländisch",
    polish: "Polnisch",
    swedish: "Schwedisch",
    turkish: "Türkisch",
    greek: "Griechisch",
    czech: "Tschechisch",
    romanian: "Rumänisch",
    hungarian: "Ungarisch",
    finnish: "Finnisch",
    norwegian: "Norwegisch",
    danish: "Dänisch",
    ukrainian: "Ukrainisch",
    thai: "Thailändisch",
    vietnamese: "Vietnamesisch",
    indonesian: "Indonesisch",
    malay: "Malaiisch",
    tagalog: "Tagalog",
    hebrew: "Hebräisch",
    persian: "Persisch",
    bengali: "Bengalisch",
    punjabi: "Punjabi",
    tamil: "Tamil",
    urdu: "Urdu",
    swahili: "Swahili",
    afrikaans: "Afrikaans",
    catalan: "Katalanisch",
    croatian: "Kroatisch",
    serbian: "Serbisch",
    slovak: "Slowakisch",
    slovenian: "Slowenisch",
    bulgarian: "Bulgarisch",
    lithuanian: "Litauisch",
    latvian: "Lettisch",
    estonian: "Estnisch",
    icelandic: "Isländisch",
    welsh: "Walisisch",
    irish: "Irisch",
    scottishGaelic: "Schottisches Gälisch",
    basque: "Baskisch",
    maltese: "Maltesisch",
    albanian: "Albanisch",
    macedonian: "Mazedonisch",
    georgian: "Georgisch",
    armenian: "Armenisch",
    kazakh: "Kasachisch",
    uzbek: "Usbekisch",
    mongolian: "Mongolisch",
    nepali: "Nepalesisch",
    sinhala: "Singhalesisch",
    burmese: "Birmanisch",
    khmer: "Khmer",
    lao: "Laotisch",
  },
  coachMarks: {
    startNodeTitle: "Gespräch starten",
    startNodeBody: "Tippe auf diese Nachrichtenblase, um den Dialog zu beginnen.",
    speakerTitle: "Aussprache anhören",
    speakerBody: "Tippe auf das Lautsprecher-Symbol, um den Satz laut zu hören.",
    answerTitle: "Wähle deine Antwort",
    answerBody: "Tippe auf eine der Optionen, um auszuwählen, was du sagen möchtest.",
    tabsTitle: "Mehr entdecken",
    tabsBody: "Wechsle zwischen Dialog, Übungen und Vokabeln.",
    skip: "Überspringen",
    next: "Weiter",
    done: "Fertig",
  },
  passwordGate: {
    enterPassword: "Passwort eingeben um fortzufahren",
    placeholder: "Passwort",
  },
  dialogueFeedback: {
    title: "Was denkst du über diesen Dialog?",
    bad: "Schlecht",
    meh: "Geht so",
    good: "Gut",
    thanks: "Danke für dein Feedback!",
    tellUsWhy: "Danke! Könntest du uns sagen, warum?",
    commentPlaceholder: "Was könnte besser sein?",
    skip: "Überspringen",
    send: "Senden",
  },
  dialog: {
    listening: "Ich höre dir zu...",
    stopRecording: "Aufnahme stoppen",
    practice: "Übung",
    vocabulary: "Wortschatz",
    loadingVocabulary: "Wortschatz wird geladen...",
  },
  languageNames: {
    "en-US": "Englisch (USA)",
    "en-GB": "Englisch (Vereinigtes Königreich)",
    "es-ES": "Spanisch (Spanien)",
    "es-MX": "Spanisch (Mexiko)",
    "pt-PT": "Portugiesisch (Portugal)",
    "pt-BR": "Portugiesisch (Brasilien)",
    "fr-FR": "Französisch",
    "de-DE": "Deutsch",
    "de-AT": "Deutsch (Österreich)",
    "de-CH": "Deutsch (Schweiz)",
    "it-IT": "Italienisch",
    "ja-JP": "Japanisch",
    "zh-CN": "Chinesisch",
    "ko-KR": "Koreanisch",
    "he-IL": "Hebräisch",
    "ru-RU": "Russisch",
    "pl-PL": "Polnisch",
    "uk-UA": "Ukrainisch",
    "fi-FI": "Finnisch",
    "et-EE": "Estnisch",
    "lv-LV": "Lettisch",
    "el-GR": "Griechisch",
  },
};

// Italian
const it: Translations = {
  nav: {
    dialogues: "Dialoghi",
    grammar: "Grammatica",
    practice: "Pratica",
    alphabet: "Alfabeto",
  },
  languageSelector: {
    title: "Lingue",
    iSpeak: "Parlo",
    iWantToLearn: "Voglio imparare",
    done: "Fatto",
    back: "Indietro",
    moreComingSoon: "Altre lingue in arrivo!",
  },
  topics: {
    title: "Argomenti di Dialogo",
    subtitle: "Scegli una conversazione da praticare",
    tagline: "Impara a dire ciò di cui hai bisogno",
    heroTitle: "Sii pronto per qualsiasi conversazione",
    heroSubtitle: "Smettila di annuire — inizia a parlare",
    createCustom: "Nuova conversazione",
    chooseTopic: "Scegli un Argomento",
    available: "disponibili",
    words: "parole",
    expressions: "espressioni",
    difficulty: {
      beginner: "Principiante",
      intermediate: "Intermedio",
      advanced: "Avanzato",
    },
    dialogNodes: "turni",
    regenerateWarning: "Creato per un'altra lingua — tocca per ricreare",
    deleteTitle: "Elimina Argomento",
    deleteDescription: "Sei sicuro di voler eliminare questo argomento? Questa azione non può essere annullata.",
    regenerateTitle: "Rigenera dialogo",
    regenerateDescription: "Verrà creato un nuovo dialogo per questo argomento. Il dialogo attuale sarà sostituito.",
    regenerate: "Rigenera",
    edit: "Modifica",
    recent: "Recenti",
    categories: "Categorie",
    byCategory: "Dialoghi per categoria",
    collapse: "Comprimi",
  },
  grammar: {
    title: "Regole Grammaticali",
    subtitle: "Come funziona il {language}",
    noRulesTitle: "Nessuna Regola Ancora",
    noRulesDescription: "Crea un argomento in {language} per vedere le regole qui.",
    rulesCount: "{count} regole grammaticali",
    rulesSingular: "{count} regola grammaticale",
    tapToExpand: "Tocca per espandere e imparare.",
    examples: "Esempi dal Dialogo",
    howToThink: "Come Pensarci",
    commonMistake: "Errore Comune",
  },
  alphabet: {
    title: "Alfabeto e Suoni",
    subtitle: "Pronuncia del {language}",
    comingSoonTitle: "Prossimamente",
    comingSoonDescription: "La guida all'alfabeto per {language} non è ancora disponibile.",
    examples: "Esempi",
    all: "Tutto",
  },
  generator: {
    title: "Di cosa vuoi parlare?",
    placeholder: "Descrivi una situazione...\n\nEsempi:\n• Ordinare un caffè al bar\n• Chiedere indicazioni\n• Prendere appuntamento dal medico",
    proficiency: "Come vuoi sembrare?",
    generate: "Andiamo",
    generating: "Preparando tutto...",
    loadTopic: "Carica",
    back: "Indietro",
    askAI: "Descrivi la tua situazione",
    responses: "risposte",
    modelTierFast: "Veloce",
    modelTierFastDesc: "Rapido ed economico",
    modelTierBalanced: "Equilibrato",
    modelTierBalancedDesc: "Miglior rapporto qualità-prezzo",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Massima qualità",
    loadingModels: "Caricamento modelli...",
    llmConfigWarning: "Configura il tuo provider LLM nelle impostazioni per abilitare la generazione AI",
    topicTitle: "Di cosa vorresti parlare?",
    topicHintMic: "Tocca il microfono e pronuncia il tuo argomento",
    topicHintKeyboard: "Scrivi uno scenario o argomento",
    exampleTapToUse: "Esempio — tocca per usare",
    enterText: "Scrivi testo",
    tapToStop: "Tocca per fermare",
    processing: "Elaborazione...",
    tapAndTalk: "Tocca e parla",
    recording: "Registrazione...",
    useVoice: "Usa la voce",
    attach: "Allega",
    continue: "Continua",
    apiKeyRequired: "Chiave API richiesta",
    apiKeyRequiredDesc: "Configura il tuo provider LLM nelle impostazioni per abilitare la generazione AI",
    difficultyTitle: "Come vuoi sembrare?",
    difficultySubtitle: "Scegli il tuo stile di parlata",
    styleBeginner: "Principiante",
    styleBeginnerDesc: "Parole semplici — come un principiante",
    styleLocal: "Come un locale",
    styleLocalDesc: "Parlare naturale, slang, espressioni — come un madrelingua",
    styleMix: "Mescolare",
    styleMixDesc: "Scegline uno o più",
    styleCustomPlaceholder: "Oppure scrivi il tuo...",
    richnessTitle: "Quanto contenuto?",
    richnessSubtitle: "Scegli la quantità di vocabolario e dialogo",
    richnessQuick: "Rapido",
    richnessQuickDesc: "Dialogo breve, vocabolario essenziale",
    richnessStandard: "Standard",
    richnessStandardDesc: "Dialogo medio, buona copertura di vocabolario",
    richnessComprehensive: "Completo",
    richnessComprehensiveDesc: "Dialogo lungo, vocabolario esteso",
    modelTitle: "Scegli il livello di qualità",
    modelSubtitle: "Qualità più alta = contenuto migliore, costo più alto",
    cachedAvailable: "Risposta in cache disponibile — caricamento istantaneo!",
    loadCached: "Carica cache",
    previewPrompt: "Anteprima prompt",
    fullPrompt: "Prompt completo",
    copied: "Copiato!",
    systemPrompt: "Prompt di sistema",
    userPrompt: "Prompt utente",
    gettingReady: "Preparando tutto...",
    connectingToAI: "Connessione all'IA...",
    done: "Fatto!",
    letsGo: "Andiamo",
    goBack: "Torna indietro",
    playWhileWaiting: "Gioca a {game1} o {game2} mentre aspetti!",
    play: "Gioca a",
    or: "o",
    whileYouWait: "mentre aspetti",
    exitGame: "Esci dal gioco",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Ho ordinato delle gomme online in un gommista e sono arrivate. Devo chiamare per prendere un appuntamento e farle montare questa settimana.",
      "Sono in una caffetteria di specialità e voglio ordinare un flat white con latte d'avena e un dolce. Voglio anche chiedere se hanno chicchi monorigine.",
      "Sono alla stazione dei treni e devo arrivare a destinazione. Voglio sapere quale binario, quanto costa un biglietto andata e ritorno e se posso pagare con carta.",
      "Sono in una pista di go-kart con amici. Vogliamo prenotare una sessione di 20 minuti per 4 persone e chiedere se ci sono sconti per gruppi.",
      "Lo schermo del mio telefono è rotto e sono in un negozio di riparazioni. Voglio chiedere quanto tempo ci vuole, quanto costa e se hanno la garanzia.",
      "Sono in farmacia e ho bisogno di qualcosa per un forte mal di testa e anche di un antistaminico. Voglio sapere quale è il più forte e se serve la ricetta.",
      "Sto facendo il check-in in un Airbnb e la chiave non è dove l'host ha detto. Devo chiamare per capire come entrare.",
      "Sono in un ristorante di pesce sulla spiaggia e voglio ordinare pesce alla griglia, ma voglio sapere cosa c'è di fresco oggi e chiedere raccomandazioni.",
      "Sono in una scuola di surf e voglio prenotare una lezione per principianti domani mattina. Devo chiedere del noleggio della muta e a che ora arrivare.",
      "La mia auto a noleggio ha una gomma a terra in autostrada. Devo chiamare l'assistenza stradale e spiegare dove sono e cosa è successo.",
    ],
  },
  pwa: {
    installPrompt: "Installa SaySay per un'esperienza migliore",
    install: "Installa",
  },
  settings: {
    title: "Impostazioni",
    theme: "Aspetto",
    themeLight: "Chiaro",
    themeDark: "Scuro",
    themeSystem: "Auto",
    resetAll: "Cancella Tutti i Dati",
    resetConfirm: "Questo cancellerà tutte le impostazioni e gli argomenti. Sei sicuro?",
    close: "Chiudi",
    speechRecognition: "Riconoscimento vocale",
    sttAzure: "Azure",
    sttAzureDesc: "Basato su cloud, alta qualità",
    sttBrowser: "Browser",
    sttBrowserDesc: "Integrato, funziona offline",
    speechSpeed: "Velocità del parlato",
    analytics: "Analitica",
    shareUsageData: "Condividi dati di utilizzo",
    analyticsDescription: "Aiuta a migliorare SaySay condividendo dati di utilizzo anonimi",
    knownLanguages: "Lingue conosciute",
    knownLanguagesDescription: "Seleziona le lingue che conosci già. Questo ci aiuta a fornire migliori suggerimenti di pronuncia usando suoni da queste lingue.",
    noneSelected: "Nessuna selezionata",
    learning: "Apprendimento",
    showPronunciationHints: "Mostra suggerimenti di pronuncia",
    showPronunciationHintsDescription: "Mostra come pronunciare le frasi usando suoni che già conosci",
  },
  learning: {
    back: "Indietro",
    dialogues: "Dialoghi",
    vocabulary: "Vocabolario",
    grammar: "Grammatica",
    practice: "Pratica",
    cards: "Cards",
    listen: "Ascolta",
    showTranslation: "Mostra traduzione",
    hideTranslation: "Nascondi traduzione",
    pronunciation: "Pronuncia",
    sayItLike: "Dillo come",
    allForms: "Tutte le Forme",
    example: "Esempio",
    inContext: "Nel Contesto",
    literalMeaning: "Significato Letterale",
    wordBreakdown: "Scomposizione della Parola",
    fromDialog: "Dal Dialogo",
    searchVocabulary: "Cerca vocabolario...",
    foundResults: "Trovati {count} risultati per \"{query}\"",
    progress: "Progresso",
    mastered: "{count} / {total} padroneggiati",
    words: "Parole",
    expressions: "Espressioni",
    topical: "Da Sapere",
    wordsDescription: "Vocabolario essenziale con tutte le forme",
    expressionsDescription: "Frasi e modi di dire",
    topicalDescription: "Vocabolario correlato",
    noWordsMatch: "Nessuna parola corrisponde alla ricerca",
    noExpressionsMatch: "Nessuna espressione corrisponde alla ricerca",
    noTopicalMatch: "Nessun vocabolario correlato corrisponde alla ricerca",
    forms: "Forme",
    buildingBlocks: "Elementi base",
    showBuildingBlocks: "Mostra elementi base",
    buildingBlocksDescription: "Parole comuni necessarie per questo dialogo (articoli, preposizioni, ecc.)",
    hideByDefault: "Li conosco già — nascondi per impostazione predefinita",
    longPressHint: "Premi a lungo qualsiasi parola per cercarla",
    hideAll: "Nascondi tutto",
    showAll: "Mostra tutto",
    tapToReveal: "Tocca per rivelare la traduzione",
    practiceMode: "Modalità Pratica",
    practiceDescription: "Naviga attraverso scenari di conversazione reali. Scegli le risposte e vedi come si sviluppa il dialogo.",
    howItWorks: "Come funziona:",
    practiceStep1: "Leggi il prompt della conversazione",
    practiceStep2: "Scegli la risposta migliore tra le opzioni",
    practiceStep3: "Continua fino alla fine della conversazione",
    startPractice: "Inizia Pratica",
    greatJob: "Ottimo lavoro!",
    keepPracticing: "Continua a praticare!",
    correctResponses: "{correct} di {total} risposte corrette",
    yourConversation: "La tua conversazione:",
    tryAgain: "Riprova",
    newPath: "Nuovo Percorso",
    turn: "Turno {count}",
    score: "Punteggio: {correct}/{total}",
    chooseResponse: "Scegli la tua risposta:",
    continueConversation: "Continua Conversazione",
    seeResults: "Vedi Risultati",
    conversationStarts: "La conversazione inizia",
    you: "Tu",
    them: "Loro",
    end: "Fine",
    alsoSay: "Dì anche",
    playing: "In riproduzione...",
    askAI: "Aiuto",
    response: "risposta",
    responses: "risposte",
    or: "oppure",
    systemResponse: "Sistema/Risposta",
    yourOptions: "Le tue opzioni",
    tapToExpand: "Tocca per espandere. Premi a lungo su una parola per tradurre.",
    startAgain: "Ricomincia",
    pickYourAnswer: "Scegli la tua risposta",
    pickYourAnswerHint: "(tocca una delle risposte qui sotto)",
    tapToAnswer: "Tocca la risposta qui sotto per continuare",
    pickTheirAnswer: "Scegli la loro risposta",
    tapToStart: "Tocca per iniziare",
    transcribing: "Trascrizione...",
    useVoice: "Usa la voce",
    saySomethingElse: "Voglio dire qualcos'altro",
    said: "ha detto",
    whatToSay: "Cosa vuoi dire?",
    typeYourResponse: "Scrivi la tua risposta...",
    customPhraseHint: "Scrivi nella tua lingua e la tradurremo naturalmente",
    fixThisResponse: "Correggi questa risposta",
    whatsWrong: "Cosa dovrebbe dire invece?",
    fixResponseHint: "Descrivi la correzione o fornisci la frase corretta",
    // Practice tab modes
    practiceTab: "Pratica",
    practiceWords: "Parole",
    practiceSpelling: "Ortografia",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Quiz con immagini",
    practiceSpellingDesc: "Pronuncia le parole ad alta voce",
    practiceKaraokeDesc: "Pronuncia le frasi parola per parola",
    introduceWord: "Nuova parola",
    composeWord: "Comporre una parola",
    describeImage: "Descrivi cosa c'è nell'immagine",
    pickWordForSentence: "Scegli una parola per la frase",
    iNeedAHint: "Ho bisogno di un suggerimento",
    iDontKnow: "Non conosco questa parola",
    tapHoldSayWord: "Tocca, tieni premuto e pronuncia la parola",
    tapHoldSayPhrase: "Tocca, tieni premuto e pronuncia la frase",
    weHeard: "Abbiamo sentito:",
    skipWord: "Salta",
    nextWord: "Avanti",
    practiceComplete: "Pratica completata!",
    wordsCorrectCount: "{correct} di {total} corrette",
    practiceAgain: "Pratica di nuovo",
    done: "Fatto",
    correct: "Corretto!",
    chooseModule: "Scegli un modulo",
    moduleHint: "Pratica in piccoli gruppi per una migliore memorizzazione",
    wordsCount: "parole",
    markKnown: "Lo so già",
    markTough: "Difficile",
    markLiked: "Mi piace",
    knownWordsHidden: "{count} parole conosciute nascoste",
    noWordsToReview: "Nessuna parola da ripassare",
    noWordsToReviewDesc: "Genera dei dialoghi per arricchire il tuo vocabolario",
    generatingMore: "Generazione...",
  },
  common: {
    loading: "Caricamento...",
    error: "Si è verificato un errore",
    retry: "Riprova",
    cancel: "Annulla",
    save: "Salva",
    delete: "Elimina",
    edit: "Modifica",
    close: "Chiudi",
    send: "Invia",
    copy: "Copia",
  },
  games: {
    score: "Punteggio",
    best: "Migliore",
    hi: "REC",
    tapOn: "Tocca su",
    getReady: "Preparati...",
    tapToStart: "Tocca per iniziare!",
    tapToJump: "Tocca per saltare!",
    gameOver: "FINE PARTITA",
    tapToRestart: "Tocca per ricominciare",
    speedSlow: "Lento",
    speedSteady: "Costante",
    speedNormal: "Normale",
    speedQuick: "Svelto",
    speedFast: "Veloce",
    speedBlazing: "Fulmineo",
    speedOnFire: "In fiamme!",
    speedRocket: "Razzo",
    speedLegendary: "Leggendario",
    cheers: ["Bene!", "Ottimo!", "Incredibile!", "Wow!", "Superbo!", "Preso!", "Sì!", "Boom!", "Perfetto!", "Fantastico!"],
  },
  errors: {
    microphoneAccessDenied: "Accesso al microfono negato",
    recognitionError: "Errore di riconoscimento vocale",
    recordingFailed: "Impossibile avviare la registrazione",
    noSpeechDetected: "Nessun discorso rilevato. Per favore riprova.",
    llmConfigRequired: "Configura la tua chiave API LLM nelle impostazioni per utilizzare questa funzione.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Benvenuto su SaySay",
    welcomeSubtitle: "Sii pronto per qualsiasi conversazione",
    getStarted: "Inizia",
    // Feature slides
    featureTopicsPain: "I libri generici non si adattano alla tua vita",
    featureTopicsSolution: "Esercitati su ciò che ti serve davvero",
    featureTopicsDesc: "Descrivi qualsiasi situazione — ordinare un caffè, andare dal medico, chiacchierare con i vicini — e ottieni un dialogo su misura per te.",
    featureVoicePain: "Ma come si dice davvero?",
    featureVoiceSolution: "Ascolta ogni parola",
    featureVoiceDesc: "Tocca qualsiasi frase per sentire la pronuncia madrelingua. Usa la voce per rispondere a mani libere.",
    featureAiPain: "La taglia unica non funziona",
    featureAiSolution: "L'IA costruisce il tuo vocabolario",
    featureAiDesc: "Ogni dialogo include parole ed espressioni che userai davvero. Impara un vocabolario che si adatta alla tua vita.",
    continue: "Continua",
    inputNativeLangTitle: "Che lingua parli?",
    inputNativeLangSubtitle: "Ti insegneremo in questa lingua",
    inputTargetLangTitle: "Cosa vuoi imparare?",
    inputTargetLangSubtitle: "Scegli la lingua che vuoi praticare",
    // Input: Name
    inputNameTitle: "Come ti chiamiamo?",
    inputNameSubtitle: "Puoi cambiarlo dopo",
    inputNamePlaceholder: "Il tuo nome",
    // Input: Languages
    inputLangTitle: "Conosci altre lingue?",
    inputLangSubtitle: "Useremo le somiglianze per aiutarti a imparare più velocemente",
    inputLangSelect: "Seleziona le lingue che conosci oltre all'italiano",
    inputLangFindMore: "Trova altre",
    inputLangSearch: "Cerca una lingua...",
    skipStep: "Salta questo passaggio",
    // Input: Style
    inputStyleTitle: "Come vuoi sembrare?",
    inputStyleSubtitle: "Puoi cambiarlo dopo",
    speakingStyleBeginner: "Principiante",
    speakingStyleBeginnerDesc: "Parole semplici, frasi base — come chi sta imparando",
    speakingStyleLocal: "Come un locale",
    speakingStyleLocalDesc: "Espressioni naturali, gergo, modi di dire — passare per italiano",
    speakingStyleMix: "Mescola gli stili",
    speakingStyleMixDesc: "Scegli uno o più stili",
    styleYourOption: "La tua opzione...",
    styleTypePlaceholder: "Scrivi il tuo stile...",
    // Input: Gender
    inputGenderTitle: "Chi sei nei dialoghi?",
    inputGenderSubtitle: "Facoltativo — puoi cambiarlo dopo",
    genderMale: "Uomo",
    genderFemale: "Donna",
    genderOther: "Altro / Non-binario",
    genderPreferNot: "Preferisco non dirlo",
    // Navigation
    back: "Indietro",
    skipAll: "Salta tutto",
    // Ready screen
    startLearning: "Inizia a Imparare",
    youreAllSet: "Tutto pronto!",
    youreAllSetWithName: "Tutto pronto, {name}!",
    startFirstConversation: "Inizia la tua prima conversazione e scopri quanto può essere naturale imparare una lingua.",
    analyticsOptIn: "Aiuta a migliorare SaySay condividendo dati di utilizzo anonimi (nessuna informazione personale raccolta)",
    // First conversation transition screen
    firstConversationTitle: "Perfetto! Ora facciamo pratica con la tua prima conversazione.",
    firstConversationSubtitle: "Scegli un argomento e genereremo un vero dialogo per esercitarti.",
    firstConversationButton: "Andiamo!",
    // Preview UI elements
    previewTopicTitle: "Di cosa vorresti parlare?",
    previewTopicSubtitle: "Scrivi uno scenario o un argomento",
    previewExample: "Esempio — tocca per usare",
    previewExampleText: "Ordinare un caffè e chiedere consigli locali",
    previewTypeOwn: "Oppure scrivi il tuo...",
    previewVoiceText: "Vorrei un caffè...",
    previewDialogTranslation: "Buongiorno! Cosa desidera?",
  },
  knownLanguages: {
    spanish: "Spagnolo",
    french: "Francese",
    german: "Tedesco",
    italian: "Italiano",
    portuguese: "Portoghese",
    russian: "Russo",
    chinese: "Cinese",
    japanese: "Giapponese",
    korean: "Coreano",
    arabic: "Arabo",
    hindi: "Hindi",
    dutch: "Olandese",
    polish: "Polacco",
    swedish: "Svedese",
    turkish: "Turco",
    greek: "Greco",
    czech: "Ceco",
    romanian: "Rumeno",
    hungarian: "Ungherese",
    finnish: "Finlandese",
    norwegian: "Norvegese",
    danish: "Danese",
    ukrainian: "Ucraino",
    thai: "Thailandese",
    vietnamese: "Vietnamita",
    indonesian: "Indonesiano",
    malay: "Malese",
    tagalog: "Tagalog",
    hebrew: "Ebraico",
    persian: "Persiano",
    bengali: "Bengalese",
    punjabi: "Punjabi",
    tamil: "Tamil",
    urdu: "Urdu",
    swahili: "Swahili",
    afrikaans: "Afrikaans",
    catalan: "Catalano",
    croatian: "Croato",
    serbian: "Serbo",
    slovak: "Slovacco",
    slovenian: "Sloveno",
    bulgarian: "Bulgaro",
    lithuanian: "Lituano",
    latvian: "Lettone",
    estonian: "Estone",
    icelandic: "Islandese",
    welsh: "Gallese",
    irish: "Irlandese",
    scottishGaelic: "Gaelico scozzese",
    basque: "Basco",
    maltese: "Maltese",
    albanian: "Albanese",
    macedonian: "Macedone",
    georgian: "Georgiano",
    armenian: "Armeno",
    kazakh: "Kazako",
    uzbek: "Uzbeko",
    mongolian: "Mongolo",
    nepali: "Nepalese",
    sinhala: "Singalese",
    burmese: "Birmano",
    khmer: "Khmer",
    lao: "Lao",
  },
  coachMarks: {
    startNodeTitle: "Inizia la conversazione",
    startNodeBody: "Tocca questa bolla di messaggio per iniziare il dialogo.",
    speakerTitle: "Ascolta la pronuncia",
    speakerBody: "Tocca l'icona dell'altoparlante per ascoltare la frase ad alta voce.",
    answerTitle: "Scegli la tua risposta",
    answerBody: "Tocca una delle opzioni per scegliere cosa vuoi dire.",
    tabsTitle: "Esplora di più",
    tabsBody: "Passa tra il dialogo, gli esercizi di pratica e il vocabolario.",
    skip: "Salta",
    next: "Avanti",
    done: "Fatto",
  },
  passwordGate: {
    enterPassword: "Inserisci la password per continuare",
    placeholder: "Password",
  },
  dialogueFeedback: {
    title: "Cosa pensi di questo dialogo?",
    bad: "Male",
    meh: "Così così",
    good: "Bene",
    thanks: "Grazie per il tuo feedback!",
    tellUsWhy: "Grazie! Potresti dirci perché?",
    commentPlaceholder: "Cosa potrebbe essere migliorato?",
    skip: "Salta",
    send: "Invia",
  },
  dialog: {
    listening: "Ti sto ascoltando...",
    stopRecording: "Ferma registrazione",
    practice: "Pratica",
    vocabulary: "Vocabolario",
    loadingVocabulary: "Caricamento vocabolario...",
  },
  languageNames: {
    "en-US": "Inglese (USA)",
    "en-GB": "Inglese (Regno Unito)",
    "es-ES": "Spagnolo (Spagna)",
    "es-MX": "Spagnolo (Messico)",
    "pt-PT": "Portoghese (Portogallo)",
    "pt-BR": "Portoghese (Brasile)",
    "fr-FR": "Francese",
    "de-DE": "Tedesco",
    "de-AT": "Tedesco (Austria)",
    "de-CH": "Tedesco (Svizzera)",
    "it-IT": "Italiano",
    "ja-JP": "Giapponese",
    "zh-CN": "Cinese",
    "ko-KR": "Coreano",
    "he-IL": "Ebraico",
    "ru-RU": "Russo",
    "pl-PL": "Polacco",
    "uk-UA": "Ucraino",
    "fi-FI": "Finlandese",
    "et-EE": "Estone",
    "lv-LV": "Lettone",
    "el-GR": "Greco",
  },
};

// Russian
const ru: Translations = {
  nav: {
    dialogues: "Диалоги",
    grammar: "Грамматика",
    practice: "Практика",
    alphabet: "Алфавит",
  },
  languageSelector: {
    title: "Языки",
    iSpeak: "Я говорю на",
    iWantToLearn: "Я хочу изучать",
    done: "Готово",
    back: "Назад",
    moreComingSoon: "Скоро будет больше языков!",
  },
  topics: {
    title: "Темы Диалогов",
    subtitle: "Выбери разговор для практики",
    tagline: "Научись говорить то, что тебе нужно",
    heroTitle: "Будь готов к любому разговору",
    heroSubtitle: "Хватит кивать — начни говорить",
    createCustom: "Проработать новую ситуацию",
    chooseTopic: "Выбери тему",
    available: "доступно",
    words: "слов",
    expressions: "выражений",
    difficulty: {
      beginner: "Начинающий",
      intermediate: "Средний",
      advanced: "Продвинутый",
    },
    dialogNodes: "реплик",
    regenerateWarning: "Создано для другого языка — нажми, чтобы пересоздать",
    deleteTitle: "Удалить Тему",
    deleteDescription: "Точно удалить эту тему? Это действие нельзя отменить.",
    regenerateTitle: "Создать заново",
    regenerateDescription: "Будет создан новый диалог для этой темы. Текущий диалог будет заменён.",
    regenerate: "Создать заново",
    edit: "Изменить",
    recent: "Недавние",
    categories: "Категории",
    byCategory: "Диалоги по категориям",
    collapse: "Свернуть",
  },
  grammar: {
    title: "Правила Грамматики",
    subtitle: "Как работает {language}",
    noRulesTitle: "Пока Нет Правил",
    noRulesDescription: "Создайте тему на {language}, чтобы увидеть правила здесь.",
    rulesCount: "{count} правил грамматики",
    rulesSingular: "{count} правило грамматики",
    tapToExpand: "Нажми, чтобы развернуть.",
    examples: "Примеры из Диалога",
    howToThink: "Как Это Понимать",
    commonMistake: "Частая Ошибка",
  },
  alphabet: {
    title: "Алфавит и Звуки",
    subtitle: "Произношение {language}",
    comingSoonTitle: "Скоро",
    comingSoonDescription: "Руководство по алфавиту для {language} пока недоступно.",
    examples: "Примеры",
    all: "Все",
  },
  generator: {
    title: "О чём хочешь поговорить?",
    placeholder: "Опиши ситуацию...\n\nПримеры:\n• Заказать кофе в кафе\n• Спросить дорогу\n• Записаться к врачу",
    proficiency: "Как ты хочешь звучать?",
    generate: "Поехали",
    generating: "Готовим всё...",
    loadTopic: "Загрузить",
    back: "Назад",
    askAI: "Опиши свою ситуацию",
    responses: "ответов",
    modelTierFast: "Быстро",
    modelTierFastDesc: "Быстро и доступно",
    modelTierBalanced: "Сбалансированный",
    modelTierBalancedDesc: "Лучшее соотношение цены и качества",
    modelTierPremium: "Премиум",
    modelTierPremiumDesc: "Высочайшее качество",
    loadingModels: "Загрузка моделей...",
    llmConfigWarning: "Настрой поставщика LLM в настройках для включения генерации ИИ",
    topicTitle: "О чём бы ты хотел поговорить?",
    topicHintMic: "Нажми на микрофон и произнеси тему",
    topicHintKeyboard: "Введи сценарий или тему",
    exampleTapToUse: "Пример — нажми для использования",
    enterText: "Ввести текст",
    tapToStop: "Нажми для остановки",
    processing: "Обработка...",
    tapAndTalk: "Нажми и говори",
    recording: "Запись...",
    useVoice: "Использовать голос",
    attach: "Прикрепить",
    continue: "Продолжить",
    apiKeyRequired: "Требуется API-ключ",
    apiKeyRequiredDesc: "Настрой поставщика LLM в настройках для включения генерации ИИ",
    difficultyTitle: "Как ты хочешь звучать?",
    difficultySubtitle: "Выбери свой стиль речи",
    styleBeginner: "Начинающий",
    styleBeginnerDesc: "Простые слова и фразы — как начинающий",
    styleLocal: "Как местный",
    styleLocalDesc: "Живая речь, сленг, идиомы — как носитель",
    styleMix: "Микс",
    styleMixDesc: "Выбери один или несколько стилей",
    styleCustomPlaceholder: "Или напиши свой...",
    richnessTitle: "Сколько контента?",
    richnessSubtitle: "Выбери количество словаря и диалога",
    richnessQuick: "Быстро",
    richnessQuickDesc: "Короткий диалог, базовый словарь",
    richnessStandard: "Стандарт",
    richnessStandardDesc: "Средний диалог, хороший охват словаря",
    richnessComprehensive: "Полный",
    richnessComprehensiveDesc: "Длинный диалог, обширный словарь",
    modelTitle: "Выбери уровень качества",
    modelSubtitle: "Лучше качество — дороже",
    cachedAvailable: "Кэшированный ответ доступен — мгновенная загрузка!",
    loadCached: "Загрузить кэш",
    previewPrompt: "Предпросмотр промпта",
    fullPrompt: "Полный промпт",
    copied: "Скопировано!",
    systemPrompt: "Системный промпт",
    userPrompt: "Пользовательский промпт",
    gettingReady: "Создаём урок...",
    connectingToAI: "Подключаемся к ИИ...",
    done: "Готово!",
    letsGo: "Поехали",
    goBack: "Назад",
    playWhileWaiting: "Сыграй в {game1} или {game2} пока ждёшь!",
    play: "Сыграй в",
    or: "или",
    whileYouWait: "пока ждёшь",
    exitGame: "Выйти из игры",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Я заказал шины онлайн в шиномонтаже, и они приехали. Мне нужно позвонить и записаться на установку на этой неделе.",
      "Я в кофейне и хочу заказать флэт уайт на овсяном молоке и выпечку. Также хочу спросить, есть ли у них зёрна моносорта.",
      "Я на вокзале и мне нужно добраться до места назначения. Хочу узнать, какая платформа, сколько стоит билет туда-обратно и можно ли оплатить картой.",
      "Я на картинге с друзьями. Мы хотим забронировать 20-минутную сессию на 4 человека и узнать о групповых скидках.",
      "Экран моего телефона разбит, и я в мастерской по ремонту. Хочу узнать, сколько времени займёт ремонт, сколько стоит и есть ли гарантия.",
      "Я в аптеке и мне нужно что-то от сильной головной боли и от аллергии. Хочу узнать, что эффективнее и нужен ли рецепт.",
      "Я заселяюсь в Airbnb, но ключа нет там, где сказал хозяин. Нужно позвонить и выяснить, как попасть внутрь.",
      "Я в ресторане морепродуктов на пляже и хочу заказать рыбу на гриле, но хочу узнать, что свежее сегодня, и попросить рекомендации.",
      "Я в школе сёрфинга и хочу записаться на урок для начинающих на завтра утром. Нужно спросить про аренду гидрокостюма и во сколько приходить.",
      "У моей арендованной машины спустило колесо на шоссе. Нужно вызвать техпомощь и объяснить, где я и что случилось.",
    ],
  },
  pwa: {
    installPrompt: "Установите SaySay для лучшего опыта",
    install: "Установить",
  },
  settings: {
    title: "Настройки",
    theme: "Внешний вид",
    themeLight: "Светлая",
    themeDark: "Тёмная",
    themeSystem: "Авто",
    resetAll: "Удалить Все Данные",
    resetConfirm: "Это удалит все настройки и темы. Вы уверены?",
    close: "Закрыть",
    speechRecognition: "Распознавание речи",
    sttAzure: "Azure",
    sttAzureDesc: "Облачное, высокое качество",
    sttBrowser: "Браузер",
    sttBrowserDesc: "Встроенное, работает офлайн",
    speechSpeed: "Скорость речи",
    analytics: "Аналитика",
    shareUsageData: "Делиться данными использования",
    analyticsDescription: "Помогите улучшить SaySay, делясь анонимными данными использования",
    knownLanguages: "Известные языки",
    knownLanguagesDescription: "Выберите языки, которые вы уже знаете. Это поможет нам давать лучшие советы по произношению, используя звуки из этих языков.",
    noneSelected: "Не выбрано",
    learning: "Обучение",
    showPronunciationHints: "Показать подсказки произношения",
    showPronunciationHintsDescription: "Показывает, как произносить фразы, используя знакомые звуки",
  },
  learning: {
    back: "Назад",
    dialogues: "Диалоги",
    vocabulary: "Словарь",
    grammar: "Грамматика",
    practice: "Практика",
    cards: "Cards",
    listen: "Слушать",
    showTranslation: "Показать перевод",
    hideTranslation: "Скрыть перевод",
    pronunciation: "Произношение",
    sayItLike: "Скажи как",
    allForms: "Все Формы",
    example: "Пример",
    inContext: "В Контексте",
    literalMeaning: "Буквальное Значение",
    wordBreakdown: "Разбор Слова",
    fromDialog: "Из Диалога",
    searchVocabulary: "Поиск словаря...",
    foundResults: "Найдено {count} результатов для \"{query}\"",
    progress: "Прогресс",
    mastered: "{count} / {total} освоено",
    words: "Слова",
    expressions: "Выражения",
    topical: "Полезно Знать",
    wordsDescription: "Основная лексика со всеми формами",
    expressionsDescription: "Фразы и идиомы",
    topicalDescription: "Связанная лексика",
    noWordsMatch: "Слова не найдены",
    noExpressionsMatch: "Выражения не найдены",
    noTopicalMatch: "Связанная лексика не найдена",
    forms: "Формы",
    buildingBlocks: "Базовые слова",
    showBuildingBlocks: "Показать базовые слова",
    buildingBlocksDescription: "Слова, нужные для этого диалога (артикли, предлоги и т.д.)",
    hideByDefault: "Я уже знаю — скрыть по умолчанию",
    longPressHint: "Удерживай слово для поиска",
    hideAll: "Скрыть всё",
    showAll: "Показать всё",
    tapToReveal: "Нажми для показа перевода",
    practiceMode: "Режим Практики",
    practiceDescription: "Проходи через реальные разговорные сценарии. Выбирай ответы и смотри, как развивается диалог.",
    howItWorks: "Как это работает:",
    practiceStep1: "Прочитай реплику",
    practiceStep2: "Выбери лучший ответ из вариантов",
    practiceStep3: "Продолжай до конца разговора",
    startPractice: "Начать Практику",
    greatJob: "Отлично!",
    keepPracticing: "Продолжай практиковаться!",
    correctResponses: "{correct} из {total} правильных ответов",
    yourConversation: "Твой разговор:",
    tryAgain: "Попробовать Снова",
    newPath: "Новый Путь",
    turn: "Ход {count}",
    score: "Счёт: {correct}/{total}",
    chooseResponse: "Выбери ответ:",
    continueConversation: "Продолжить Разговор",
    seeResults: "Смотреть Результаты",
    conversationStarts: "Разговор начинается",
    you: "Ты",
    them: "Они",
    end: "Конец",
    alsoSay: "Также скажите",
    playing: "Воспроизведение...",
    askAI: "Помощь",
    response: "ответ",
    responses: "ответов",
    or: "или",
    systemResponse: "Система/Ответ",
    yourOptions: "Твои варианты",
    tapToExpand: "Нажми для раскрытия. Удерживай слово для перевода.",
    startAgain: "Начать сначала",
    pickYourAnswer: "Выбери ответ",
    pickYourAnswerHint: "(нажми на один из вариантов ниже)",
    tapToAnswer: "Нажми на ответ ниже, чтобы продолжить",
    pickTheirAnswer: "Выбери их ответ",
    tapToStart: "Нажми, чтобы начать",
    transcribing: "Распознаём...",
    useVoice: "Голосом",
    saySomethingElse: "Я хочу сказать что-то другое",
    said: "сказал(а)",
    whatToSay: "Что ты хочешь сказать?",
    typeYourResponse: "Введи свой ответ...",
    customPhraseHint: "Введи на своём языке, и мы переведём естественно",
    fixThisResponse: "Исправить этот ответ",
    whatsWrong: "Что должно быть вместо этого?",
    fixResponseHint: "Опишите исправление или укажите правильную фразу",
    // Practice tab modes
    practiceTab: "Практика",
    practiceWords: "Слова",
    practiceSpelling: "Правописание",
    practiceKaraoke: "Караоке",
    practiceWordsDesc: "Викторина с картинками",
    practiceSpellingDesc: "Произнесите слова вслух",
    practiceKaraokeDesc: "Произносите фразы слово за словом",
    introduceWord: "Новое слово",
    composeWord: "Составить слово",
    describeImage: "Опишите, что на картинке",
    pickWordForSentence: "Выберите слово для предложения",
    iNeedAHint: "Мне нужна подсказка",
    iDontKnow: "Я не знаю это слово",
    tapHoldSayWord: "Нажмите, удерживайте и произнесите слово",
    tapHoldSayPhrase: "Нажмите, удерживайте и произнесите фразу",
    weHeard: "Мы услышали:",
    skipWord: "Пропустить",
    nextWord: "Далее",
    practiceComplete: "Практика завершена!",
    wordsCorrectCount: "{correct} из {total} правильно",
    practiceAgain: "Повторить практику",
    done: "Готово",
    correct: "Правильно!",
    chooseModule: "Выберите модуль",
    moduleHint: "Практикуйте небольшими группами для лучшего запоминания",
    wordsCount: "слов",
    markKnown: "Уже знаю",
    markTough: "Сложное",
    markLiked: "Нравится",
    knownWordsHidden: "{count} известных слов скрыто",
    noWordsToReview: "Пока нет слов для повторения",
    noWordsToReviewDesc: "Создайте диалоги, чтобы пополнить словарный запас",
    generatingMore: "Генерация...",
  },
  common: {
    loading: "Загрузка...",
    error: "Произошла ошибка",
    retry: "Повторить",
    cancel: "Отмена",
    save: "Сохранить",
    delete: "Удалить",
    edit: "Редактировать",
    close: "Закрыть",
    send: "Отправить",
    copy: "Копировать",
  },
  games: {
    score: "Счёт",
    best: "Лучший",
    hi: "РЕК",
    tapOn: "Нажми на",
    getReady: "Приготовься...",
    tapToStart: "Нажми, чтобы начать!",
    tapToJump: "Нажми, чтобы прыгнуть!",
    gameOver: "ИГРА ОКОНЧЕНА",
    tapToRestart: "Нажми, чтобы начать заново",
    speedSlow: "Медленно",
    speedSteady: "Устойчиво",
    speedNormal: "Нормально",
    speedQuick: "Быстро",
    speedFast: "Стремительно",
    speedBlazing: "Пылающий",
    speedOnFire: "В огне!",
    speedRocket: "Ракета",
    speedLegendary: "Легендарно",
    cheers: ["Класс!", "Отлично!", "Невероятно!", "Ого!", "Великолепно!", "Есть!", "Да!", "Бум!", "Идеально!", "Потрясающе!"],
  },
  errors: {
    microphoneAccessDenied: "Доступ к микрофону запрещен",
    recognitionError: "Ошибка распознавания речи",
    recordingFailed: "Не удалось начать запись",
    noSpeechDetected: "Речь не обнаружена. Попробуй ещё раз.",
    llmConfigRequired: "Настрой ключ API LLM в настройках для использования этой функции.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Добро пожаловать в SaySay",
    welcomeSubtitle: "Будь готов к любому разговору",
    getStarted: "Начать",
    // Feature slides
    featureTopicsPain: "Учебники не про твою жизнь",
    featureTopicsSolution: "Практикуй то, что тебе реально нужно",
    featureTopicsDesc: "Опиши любую ситуацию — заказать кофе, сходить к врачу, поболтать с соседями — и получи диалог под себя.",
    featureVoicePain: "А как это вообще произносится?",
    featureVoiceSolution: "Слушай каждое слово",
    featureVoiceDesc: "Нажми на любую фразу, чтобы услышать произношение носителя. Отвечай голосом без рук.",
    featureAiPain: "Шаблонный подход не работает",
    featureAiSolution: "ИИ собирает твой словарь",
    featureAiDesc: "Каждый диалог содержит слова и выражения, которые ты реально будешь использовать. Учи лексику, которая пригодится в жизни.",
    continue: "Продолжить",
    inputNativeLangTitle: "На каком языке ты говоришь?",
    inputNativeLangSubtitle: "Мы будем обучать тебя на этом языке",
    inputTargetLangTitle: "Какой язык хочешь учить?",
    inputTargetLangSubtitle: "Выбери язык, который хочешь практиковать",
    // Input: Name
    inputNameTitle: "Как тебя называть?",
    inputNameSubtitle: "Можно изменить потом",
    inputNamePlaceholder: "Твоё имя",
    // Input: Languages
    inputLangTitle: "Знаешь другие языки?",
    inputLangSubtitle: "Мы используем сходства, чтобы ты учился быстрее",
    inputLangSelect: "Выбери языки, которые знаешь кроме русского",
    inputLangFindMore: "Найти ещё",
    inputLangSearch: "Поиск языка...",
    skipStep: "Пропустить",
    // Input: Style
    inputStyleTitle: "Как хочешь звучать?",
    inputStyleSubtitle: "Можно изменить потом",
    speakingStyleBeginner: "Начинающий",
    speakingStyleBeginnerDesc: "Простые слова, базовые фразы — как тот, кто учится",
    speakingStyleLocal: "Как местный",
    speakingStyleLocalDesc: "Живая речь, сленг, идиомы — чтобы сойти за своего",
    speakingStyleMix: "Смешать стили",
    speakingStyleMixDesc: "Выбери один или несколько стилей",
    styleYourOption: "Свой вариант...",
    styleTypePlaceholder: "Напиши свой стиль...",
    // Input: Gender
    inputGenderTitle: "Кто ты в диалогах?",
    inputGenderSubtitle: "Необязательно — можно изменить потом",
    genderMale: "Мужчина",
    genderFemale: "Женщина",
    genderOther: "Другое / Небинарный",
    genderPreferNot: "Не хочу отвечать",
    // Navigation
    back: "Назад",
    skipAll: "Пропустить всё",
    // Ready screen
    startLearning: "Начать учиться",
    youreAllSet: "Всё готово!",
    youreAllSetWithName: "Всё готово, {name}!",
    startFirstConversation: "Начни первый диалог и почувствуй, как естественно можно учить язык.",
    analyticsOptIn: "Помоги улучшить SaySay — анонимная статистика использования (без личных данных)",
    // First conversation transition screen
    firstConversationTitle: "Отлично! Теперь давай попрактикуем твой первый разговор.",
    firstConversationSubtitle: "Выбери тему, и мы создадим настоящий диалог для практики.",
    firstConversationButton: "Поехали!",
    // Preview UI elements
    previewTopicTitle: "О чём хочешь поговорить?",
    previewTopicSubtitle: "Введи сценарий или тему",
    previewExample: "Пример — нажми, чтобы использовать",
    previewExampleText: "Заказать кофе и спросить о местных рекомендациях",
    previewTypeOwn: "Или введи свою тему...",
    previewVoiceText: "Я бы хотел кофе...",
    previewDialogTranslation: "Доброе утро! Что желаете?",
  },
  knownLanguages: {
    spanish: "Испанский",
    french: "Французский",
    german: "Немецкий",
    italian: "Итальянский",
    portuguese: "Португальский",
    russian: "Русский",
    chinese: "Китайский",
    japanese: "Японский",
    korean: "Корейский",
    arabic: "Арабский",
    hindi: "Хинди",
    dutch: "Нидерландский",
    polish: "Польский",
    swedish: "Шведский",
    turkish: "Турецкий",
    greek: "Греческий",
    czech: "Чешский",
    romanian: "Румынский",
    hungarian: "Венгерский",
    finnish: "Финский",
    norwegian: "Норвежский",
    danish: "Датский",
    ukrainian: "Украинский",
    thai: "Тайский",
    vietnamese: "Вьетнамский",
    indonesian: "Индонезийский",
    malay: "Малайский",
    tagalog: "Тагальский",
    hebrew: "Иврит",
    persian: "Персидский",
    bengali: "Бенгальский",
    punjabi: "Пенджаби",
    tamil: "Тамильский",
    urdu: "Урду",
    swahili: "Суахили",
    afrikaans: "Африкаанс",
    catalan: "Каталанский",
    croatian: "Хорватский",
    serbian: "Сербский",
    slovak: "Словацкий",
    slovenian: "Словенский",
    bulgarian: "Болгарский",
    lithuanian: "Литовский",
    latvian: "Латышский",
    estonian: "Эстонский",
    icelandic: "Исландский",
    welsh: "Валлийский",
    irish: "Ирландский",
    scottishGaelic: "Шотландский гэльский",
    basque: "Баскский",
    maltese: "Мальтийский",
    albanian: "Албанский",
    macedonian: "Македонский",
    georgian: "Грузинский",
    armenian: "Армянский",
    kazakh: "Казахский",
    uzbek: "Узбекский",
    mongolian: "Монгольский",
    nepali: "Непальский",
    sinhala: "Сингальский",
    burmese: "Бирманский",
    khmer: "Кхмерский",
    lao: "Лаосский",
  },
  coachMarks: {
    startNodeTitle: "Начать разговор",
    startNodeBody: "Нажмите на это сообщение, чтобы начать диалог.",
    speakerTitle: "Послушать произношение",
    speakerBody: "Нажмите на значок динамика, чтобы услышать фразу вслух.",
    answerTitle: "Выберите ответ",
    answerBody: "Нажмите на один из вариантов, чтобы выбрать, что вы хотите сказать.",
    tabsTitle: "Узнать больше",
    tabsBody: "Переключайтесь между диалогом, упражнениями и словарём.",
    skip: "Пропустить",
    next: "Далее",
    done: "Готово",
  },
  passwordGate: {
    enterPassword: "Введи пароль для продолжения",
    placeholder: "Пароль",
  },
  dialogueFeedback: {
    title: "Что вы думаете об этом диалоге?",
    bad: "Плохо",
    meh: "Так себе",
    good: "Хорошо",
    thanks: "Спасибо за отзыв!",
    tellUsWhy: "Спасибо! Не могли бы вы рассказать почему?",
    commentPlaceholder: "Что можно улучшить?",
    skip: "Пропустить",
    send: "Отправить",
  },
  dialog: {
    listening: "Слушаю тебя...",
    stopRecording: "Остановить запись",
    practice: "Практика",
    vocabulary: "Словарь",
    loadingVocabulary: "Загрузка словаря...",
  },
  languageNames: {
    "en-US": "Английский (США)",
    "en-GB": "Английский (Великобритания)",
    "es-ES": "Испанский (Испания)",
    "es-MX": "Испанский (Мексика)",
    "pt-PT": "Португальский (Португалия)",
    "pt-BR": "Португальский (Бразилия)",
    "fr-FR": "Французский",
    "de-DE": "Немецкий",
    "de-AT": "Немецкий (Австрия)",
    "de-CH": "Немецкий (Швейцария)",
    "it-IT": "Итальянский",
    "ja-JP": "Японский",
    "zh-CN": "Китайский",
    "ko-KR": "Корейский",
    "he-IL": "Иврит",
    "ru-RU": "Русский",
    "pl-PL": "Польский",
    "uk-UA": "Украинский",
    "fi-FI": "Финский",
    "et-EE": "Эстонский",
    "lv-LV": "Латышский",
    "el-GR": "Греческий",
  },
};

// Japanese
const ja: Translations = {
  nav: {
    dialogues: "会話",
    grammar: "文法",
    practice: "練習",
    alphabet: "文字",
  },
  languageSelector: {
    title: "言語",
    iSpeak: "話せる言語",
    iWantToLearn: "学びたい言語",
    done: "完了",
    back: "戻る",
    moreComingSoon: "他の言語も近日公開！",
  },
  topics: {
    title: "会話トピック",
    subtitle: "練習する会話を選んでください",
    tagline: "必要な言葉を話せるようになろう",
    heroTitle: "どんな会話にも備えよう",
    heroSubtitle: "うなずくのはもうやめ。話し始めよう",
    createCustom: "新しい会話",
    chooseTopic: "トピックを選択",
    available: "利用可能",
    words: "単語",
    expressions: "表現",
    difficulty: {
      beginner: "初級",
      intermediate: "中級",
      advanced: "上級",
    },
    dialogNodes: "ターン",
    regenerateWarning: "別の言語用です — タップして再作成",
    deleteTitle: "トピックを削除",
    deleteDescription: "このトピックを削除してもよろしいですか？この操作は元に戻せません。",
    regenerateTitle: "ダイアログを再生成",
    regenerateDescription: "このトピックの新しいダイアログが作成されます。現在のダイアログは置き換えられます。",
    regenerate: "再生成",
    edit: "編集",
    recent: "最近",
    categories: "カテゴリー",
    byCategory: "カテゴリー別ダイアログ",
    collapse: "閉じる",
  },
  grammar: {
    title: "文法ルール",
    subtitle: "{language}の仕組み",
    noRulesTitle: "まだルールがありません",
    noRulesDescription: "{language}でトピックを作成すると、文法ルールがここに表示されます。",
    rulesCount: "{count}個の文法ルール",
    rulesSingular: "{count}個の文法ルール",
    tapToExpand: "タップして展開",
    examples: "会話からの例",
    howToThink: "考え方",
    commonMistake: "よくある間違い",
  },
  alphabet: {
    title: "文字と発音",
    subtitle: "{language}の発音",
    comingSoonTitle: "近日公開",
    comingSoonDescription: "{language}のアルファベットガイドはまだ利用できません。",
    examples: "例",
    all: "すべて",
  },
  generator: {
    title: "何について話したい？",
    placeholder: "シチュエーションを説明してください...\n\n例:\n• カフェでコーヒーを注文する\n• 道を尋ねる\n• 医者の予約を取る",
    proficiency: "どんな感じで話したい？",
    generate: "始めよう",
    generating: "準備中...",
    loadTopic: "読み込む",
    back: "戻る",
    askAI: "シチュエーションを説明",
    responses: "件の回答",
    modelTierFast: "高速",
    modelTierFastDesc: "速くて手頃",
    modelTierBalanced: "バランス",
    modelTierBalancedDesc: "最高のコストパフォーマンス",
    modelTierPremium: "プレミアム",
    modelTierPremiumDesc: "最高品質",
    loadingModels: "モデルを読み込み中...",
    llmConfigWarning: "AI生成を有効にするには、設定でLLMプロバイダーを構成してください",
    topicTitle: "何について話したいですか？",
    topicHintMic: "マイクをタップしてトピックを話してください",
    topicHintKeyboard: "シナリオやトピックを入力",
    exampleTapToUse: "例 — タップして使用",
    enterText: "テキスト入力",
    tapToStop: "タップして停止",
    processing: "処理中...",
    tapAndTalk: "タップして話す",
    recording: "録音中...",
    useVoice: "音声を使う",
    attach: "添付",
    continue: "続ける",
    apiKeyRequired: "APIキーが必要です",
    apiKeyRequiredDesc: "AI生成を有効にするには、設定でLLMプロバイダーを構成してください",
    difficultyTitle: "どんな感じで話したい？",
    difficultySubtitle: "話し方のスタイルを選択",
    styleBeginner: "初心者",
    styleBeginnerDesc: "シンプルな言葉 — 初心者らしく",
    styleLocal: "地元の人のように",
    styleLocalDesc: "自然な話し方、スラング — ネイティブらしく",
    styleMix: "ミックス",
    styleMixDesc: "1つ以上選択",
    styleCustomPlaceholder: "または自分で入力...",
    richnessTitle: "どのくらいのコンテンツ？",
    richnessSubtitle: "語彙とダイアログの量を選択",
    richnessQuick: "クイック",
    richnessQuickDesc: "短いダイアログ、必須語彙",
    richnessStandard: "スタンダード",
    richnessStandardDesc: "中程度のダイアログ、良い語彙カバレッジ",
    richnessComprehensive: "包括的",
    richnessComprehensiveDesc: "長いダイアログ、豊富な語彙",
    modelTitle: "品質レベルを選択",
    modelSubtitle: "高品質 = より良いコンテンツ、高コスト",
    cachedAvailable: "キャッシュされた応答が利用可能 — 即座にロード！",
    loadCached: "キャッシュを読み込む",
    previewPrompt: "プロンプトのプレビュー",
    fullPrompt: "完全なプロンプト",
    copied: "コピーしました！",
    systemPrompt: "システムプロンプト",
    userPrompt: "ユーザープロンプト",
    gettingReady: "準備中...",
    connectingToAI: "AIに接続中...",
    done: "完了！",
    letsGo: "始めよう",
    goBack: "戻る",
    playWhileWaiting: "{game1}か{game2}で遊びながら待とう！",
    play: "プレイ",
    or: "か",
    whileYouWait: "待っている間に",
    exitGame: "ゲーム終了",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "オンラインでタイヤを注文して届きました。今週中に取り付けの予約の電話をしなければなりません。",
      "スペシャルティコーヒーショップにいて、オーツミルクのフラットホワイトとペストリーを注文したいです。シングルオリジンの豆があるか聞きたいです。",
      "駅にいて目的地に行く必要があります。何番ホームか、往復切符の値段、カードで払えるか知りたいです。",
      "友達とゴーカート場にいます。4人で20分のセッションを予約して、グループ割引があるか聞きたいです。",
      "スマホの画面が割れて修理店にいます。どのくらい時間がかかるか、いくらか、保証があるか聞きたいです。",
      "薬局にいて、ひどい頭痛薬とアレルギーの薬が必要です。どれが一番効くか、処方箋が必要か聞きたいです。",
      "Airbnbにチェックインしていますが、鍵がホストが言った場所にありません。電話して入り方を確認する必要があります。",
      "ビーチ沿いのシーフードレストランにいて、焼き魚を注文したいですが、今日何が新鮮かおすすめを聞きたいです。",
      "サーフスクールにいて、明日の朝の初心者レッスンを予約したいです。ウェットスーツのレンタルと到着時間について聞く必要があります。",
      "レンタカーが高速道路でパンクしました。ロードサービスに電話して、場所と何があったか説明する必要があります。",
    ],
  },
  pwa: {
    installPrompt: "より良い体験のためにSaySayをインストール",
    install: "インストール",
  },
  settings: {
    title: "設定",
    theme: "外観",
    themeLight: "ライト",
    themeDark: "ダーク",
    themeSystem: "自動",
    resetAll: "すべてのデータを削除",
    resetConfirm: "すべての設定とトピックが削除されます。よろしいですか？",
    close: "閉じる",
    speechRecognition: "音声認識",
    sttAzure: "Azure",
    sttAzureDesc: "クラウドベース、高品質",
    sttBrowser: "ブラウザ",
    sttBrowserDesc: "内蔵、オフラインで動作",
    speechSpeed: "話す速度",
    analytics: "分析",
    shareUsageData: "使用データを共有",
    analyticsDescription: "匿名の使用データを共有してSaySayの改善にご協力ください",
    knownLanguages: "既知の言語",
    knownLanguagesDescription: "すでに知っている言語を選択してください。これにより、それらの言語の音を使って、より良い発音のヒントを提供できます。",
    noneSelected: "未選択",
    learning: "学習",
    showPronunciationHints: "発音ヒントを表示",
    showPronunciationHintsDescription: "知っている音を使ってフレーズの発音方法を表示します",
  },
  learning: {
    back: "戻る",
    dialogues: "会話",
    vocabulary: "単語",
    grammar: "文法",
    practice: "練習",
    cards: "Cards",
    listen: "聞く",
    showTranslation: "翻訳を表示",
    hideTranslation: "翻訳を隠す",
    pronunciation: "発音",
    sayItLike: "こう言って",
    allForms: "すべての形",
    example: "例",
    inContext: "文脈で",
    literalMeaning: "直訳",
    wordBreakdown: "単語の分解",
    fromDialog: "会話から",
    searchVocabulary: "単語を検索...",
    foundResults: "\"{query}\"の検索結果: {count}件",
    progress: "進捗",
    mastered: "{count} / {total} 習得済み",
    words: "単語",
    expressions: "表現",
    topical: "知っておくと便利",
    wordsDescription: "基本語彙と活用形",
    expressionsDescription: "慣用句と表現",
    topicalDescription: "関連語彙",
    noWordsMatch: "一致する単語がありません",
    noExpressionsMatch: "一致する表現がありません",
    noTopicalMatch: "関連する語彙が見つかりません",
    forms: "活用形",
    buildingBlocks: "基本パーツ",
    showBuildingBlocks: "基本パーツを表示",
    buildingBlocksDescription: "このダイアログに必要な一般的な単語（冠詞、前置詞など）",
    hideByDefault: "もう知っています — デフォルトで非表示",
    longPressHint: "長押しで単語を検索",
    hideAll: "すべて非表示",
    showAll: "すべて表示",
    tapToReveal: "タップで翻訳を表示",
    practiceMode: "練習モード",
    practiceDescription: "実際の会話シナリオを体験。回答を選んで会話の流れを見てみましょう。",
    howItWorks: "使い方:",
    practiceStep1: "会話のプロンプトを読む",
    practiceStep2: "選択肢から最適な回答を選ぶ",
    practiceStep3: "会話の終わりまで続ける",
    startPractice: "練習を始める",
    greatJob: "素晴らしい！",
    keepPracticing: "練習を続けましょう！",
    correctResponses: "{total}問中{correct}問正解",
    yourConversation: "あなたの会話:",
    tryAgain: "もう一度",
    newPath: "新しいルート",
    turn: "ターン {count}",
    score: "スコア: {correct}/{total}",
    chooseResponse: "回答を選んでください:",
    continueConversation: "会話を続ける",
    seeResults: "結果を見る",
    conversationStarts: "会話開始",
    you: "あなた",
    them: "相手",
    end: "終了",
    alsoSay: "他の言い方",
    playing: "再生中...",
    askAI: "ヘルプ",
    response: "件の返答",
    responses: "件の返答",
    or: "または",
    systemResponse: "システム/返答",
    yourOptions: "選択肢",
    tapToExpand: "タップで展開。長押しで翻訳。",
    startAgain: "最初から",
    pickYourAnswer: "答えを選んでください",
    pickYourAnswerHint: "(下の選択肢をタップして選んでください)",
    tapToAnswer: "下の答えをタップして続けてください",
    pickTheirAnswer: "相手の答えを選んでください",
    tapToStart: "タップして開始",
    transcribing: "文字起こし中...",
    useVoice: "音声入力",
    saySomethingElse: "別のことを言いたい",
    said: "が言いました",
    whatToSay: "何を言いたいですか？",
    typeYourResponse: "返答を入力...",
    customPhraseHint: "あなたの言語で入力すると自然に翻訳します",
    fixThisResponse: "この返答を修正",
    whatsWrong: "代わりに何と言うべきですか？",
    fixResponseHint: "修正内容を説明するか、正しいフレーズを入力してください",
    // Practice tab modes
    practiceTab: "練習",
    practiceWords: "単語",
    practiceSpelling: "スペル",
    practiceKaraoke: "カラオケ",
    practiceWordsDesc: "画像付きクイズ",
    practiceSpellingDesc: "単語を声に出して言う",
    practiceKaraokeDesc: "フレーズを一語ずつ話す",
    introduceWord: "新しい単語",
    composeWord: "単語を組み立てる",
    describeImage: "画像の内容を説明する",
    pickWordForSentence: "文に合う単語を選ぶ",
    iNeedAHint: "ヒントが必要です",
    iDontKnow: "この単語がわかりません",
    tapHoldSayWord: "タップして長押しし、単語を言う",
    tapHoldSayPhrase: "タップして長押しし、フレーズを言う",
    weHeard: "聞き取った内容：",
    skipWord: "スキップ",
    nextWord: "次へ",
    practiceComplete: "練習完了！",
    wordsCorrectCount: "{total}問中{correct}問正解",
    practiceAgain: "もう一度練習",
    done: "完了",
    correct: "正解！",
    chooseModule: "モジュールを選択",
    moduleHint: "小さなグループで練習して定着率アップ",
    wordsCount: "語",
    markKnown: "知ってる",
    markTough: "難しい",
    markLiked: "お気に入り",
    knownWordsHidden: "{count}個の既知の単語を非表示",
    noWordsToReview: "まだ復習する単語がありません",
    noWordsToReviewDesc: "会話を作成して語彙を増やしましょう",
    generatingMore: "生成中...",
  },
  common: {
    loading: "読み込み中...",
    error: "エラーが発生しました",
    retry: "再試行",
    cancel: "キャンセル",
    save: "保存",
    delete: "削除",
    edit: "編集",
    close: "閉じる",
    send: "送信",
    copy: "コピー",
  },
  games: {
    score: "スコア",
    best: "ベスト",
    hi: "HI",
    tapOn: "タップして",
    getReady: "準備して...",
    tapToStart: "タップして開始！",
    tapToJump: "タップしてジャンプ！",
    gameOver: "ゲームオーバー",
    tapToRestart: "タップして再スタート",
    speedSlow: "ゆっくり",
    speedSteady: "安定",
    speedNormal: "普通",
    speedQuick: "速い",
    speedFast: "高速",
    speedBlazing: "爆速",
    speedOnFire: "燃えてる！",
    speedRocket: "ロケット",
    speedLegendary: "伝説",
    cheers: ["いいね！", "すごい！", "素晴らしい！", "わぉ！", "見事！", "やった！", "よし！", "ドカン！", "完璧！", "最高！"],
  },
  errors: {
    microphoneAccessDenied: "マイクへのアクセスが拒否されました",
    recognitionError: "音声認識エラー",
    recordingFailed: "録音の開始に失敗しました",
    noSpeechDetected: "音声が検出されませんでした。もう一度お試しください。",
    llmConfigRequired: "この機能を使用するには、設定でLLM APIキーを構成してください。",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "SaySayへようこそ",
    welcomeSubtitle: "どんな会話にも対応できるように",
    getStarted: "はじめる",
    // Feature slides
    featureTopicsPain: "教科書は実生活に合わない",
    featureTopicsSolution: "本当に必要なことを練習しよう",
    featureTopicsDesc: "どんな場面でも大丈夫 — コーヒーを注文する、病院に行く、ご近所と話す。あなた専用の会話が作れます。",
    featureVoicePain: "それ、どう発音するの？",
    featureVoiceSolution: "すべての言葉を聞ける",
    featureVoiceDesc: "フレーズをタップしてネイティブの発音を聞こう。声で返答すればハンズフリー。",
    featureAiPain: "画一的なやり方は合わない",
    featureAiSolution: "AIがあなたの語彙を作る",
    featureAiDesc: "すべての会話に実際に使う単語と表現が含まれています。あなたの生活に役立つ語彙を学ぼう。",
    continue: "次へ",
    inputNativeLangTitle: "あなたの言語は何ですか？",
    inputNativeLangSubtitle: "この言語でお教えします",
    inputTargetLangTitle: "何を学びたいですか？",
    inputTargetLangSubtitle: "練習したい言語を選んでください",
    // Input: Name
    inputNameTitle: "お名前は？",
    inputNameSubtitle: "後で変更できます",
    inputNamePlaceholder: "あなたの名前",
    // Input: Languages
    inputLangTitle: "他に話せる言語はありますか？",
    inputLangSubtitle: "似ている点を活かして、より早く学べます",
    inputLangSelect: "日本語以外に知っている言語を選んでください",
    inputLangFindMore: "もっと探す",
    inputLangSearch: "言語を検索...",
    skipStep: "スキップ",
    // Input: Style
    inputStyleTitle: "どんな話し方がいい？",
    inputStyleSubtitle: "後で変更できます",
    speakingStyleBeginner: "初心者",
    speakingStyleBeginnerDesc: "シンプルな単語と基本フレーズ — 学習者らしく",
    speakingStyleLocal: "現地人のように",
    speakingStyleLocalDesc: "自然な表現、スラング、慣用句 — 溶け込む",
    speakingStyleMix: "ミックス",
    speakingStyleMixDesc: "1つ以上のスタイルを選択",
    styleYourOption: "あなたのスタイル...",
    styleTypePlaceholder: "スタイルを入力...",
    // Input: Gender
    inputGenderTitle: "会話での役割は？",
    inputGenderSubtitle: "任意 — 後で変更できます",
    genderMale: "男性",
    genderFemale: "女性",
    genderOther: "その他 / ノンバイナリー",
    genderPreferNot: "回答しない",
    // Navigation
    back: "戻る",
    skipAll: "すべてスキップ",
    // Ready screen
    startLearning: "学習を始める",
    youreAllSet: "準備完了！",
    youreAllSetWithName: "{name}さん、準備完了！",
    startFirstConversation: "最初の会話を始めて、自然な語学学習を体験しよう。",
    analyticsOptIn: "匿名の使用データを共有してSaySayの改善にご協力ください（個人情報は収集されません）",
    // First conversation transition screen
    firstConversationTitle: "では、最初の会話を練習しましょう！",
    firstConversationSubtitle: "トピックを選んで、練習用の本格的な会話を生成します。",
    firstConversationButton: "始めよう！",
    // Preview UI elements
    previewTopicTitle: "何について話したいですか？",
    previewTopicSubtitle: "シナリオやトピックを入力",
    previewExample: "例 — タップして使用",
    previewExampleText: "コーヒーを注文して地元のおすすめを聞く",
    previewTypeOwn: "または自分で入力...",
    previewVoiceText: "コーヒーをお願いします...",
    previewDialogTranslation: "おはようございます！何になさいますか？",
  },
  knownLanguages: {
    spanish: "スペイン語",
    french: "フランス語",
    german: "ドイツ語",
    italian: "イタリア語",
    portuguese: "ポルトガル語",
    russian: "ロシア語",
    chinese: "中国語",
    japanese: "日本語",
    korean: "韓国語",
    arabic: "アラビア語",
    hindi: "ヒンディー語",
    dutch: "オランダ語",
    polish: "ポーランド語",
    swedish: "スウェーデン語",
    turkish: "トルコ語",
    greek: "ギリシャ語",
    czech: "チェコ語",
    romanian: "ルーマニア語",
    hungarian: "ハンガリー語",
    finnish: "フィンランド語",
    norwegian: "ノルウェー語",
    danish: "デンマーク語",
    ukrainian: "ウクライナ語",
    thai: "タイ語",
    vietnamese: "ベトナム語",
    indonesian: "インドネシア語",
    malay: "マレー語",
    tagalog: "タガログ語",
    hebrew: "ヘブライ語",
    persian: "ペルシャ語",
    bengali: "ベンガル語",
    punjabi: "パンジャブ語",
    tamil: "タミル語",
    urdu: "ウルドゥー語",
    swahili: "スワヒリ語",
    afrikaans: "アフリカーンス語",
    catalan: "カタルーニャ語",
    croatian: "クロアチア語",
    serbian: "セルビア語",
    slovak: "スロバキア語",
    slovenian: "スロベニア語",
    bulgarian: "ブルガリア語",
    lithuanian: "リトアニア語",
    latvian: "ラトビア語",
    estonian: "エストニア語",
    icelandic: "アイスランド語",
    welsh: "ウェールズ語",
    irish: "アイルランド語",
    scottishGaelic: "スコットランド・ゲール語",
    basque: "バスク語",
    maltese: "マルタ語",
    albanian: "アルバニア語",
    macedonian: "マケドニア語",
    georgian: "ジョージア語",
    armenian: "アルメニア語",
    kazakh: "カザフ語",
    uzbek: "ウズベク語",
    mongolian: "モンゴル語",
    nepali: "ネパール語",
    sinhala: "シンハラ語",
    burmese: "ビルマ語",
    khmer: "クメール語",
    lao: "ラオス語",
  },
  coachMarks: {
    startNodeTitle: "会話を始める",
    startNodeBody: "このメッセージバブルをタップして会話を始めましょう。",
    speakerTitle: "発音を聞く",
    speakerBody: "スピーカーアイコンをタップしてフレーズを聞きましょう。",
    answerTitle: "返答を選ぶ",
    answerBody: "選択肢の一つをタップして、言いたいことを選びましょう。",
    tabsTitle: "もっと探す",
    tabsBody: "会話、練習問題、単語帳を切り替えられます。",
    skip: "スキップ",
    next: "次へ",
    done: "完了",
  },
  passwordGate: {
    enterPassword: "続行するにはパスワードを入力してください",
    placeholder: "パスワード",
  },
  dialogueFeedback: {
    title: "この会話はいかがでしたか？",
    bad: "悪い",
    meh: "まあまあ",
    good: "良い",
    thanks: "フィードバックありがとうございます！",
    tellUsWhy: "ありがとうございます！理由を教えていただけますか？",
    commentPlaceholder: "どうすれば良くなりますか？",
    skip: "スキップ",
    send: "送信",
  },
  dialog: {
    listening: "聞いています...",
    stopRecording: "録音を停止",
    practice: "練習",
    vocabulary: "語彙",
    loadingVocabulary: "語彙を読み込み中...",
  },
  languageNames: {
    "en-US": "英語（アメリカ）",
    "en-GB": "英語（イギリス）",
    "es-ES": "スペイン語（スペイン）",
    "es-MX": "スペイン語（メキシコ）",
    "pt-PT": "ポルトガル語（ポルトガル）",
    "pt-BR": "ポルトガル語（ブラジル）",
    "fr-FR": "フランス語",
    "de-DE": "ドイツ語",
    "de-AT": "ドイツ語（オーストリア）",
    "de-CH": "ドイツ語（スイス）",
    "it-IT": "イタリア語",
    "ja-JP": "日本語",
    "zh-CN": "中国語",
    "ko-KR": "韓国語",
    "he-IL": "ヘブライ語",
    "ru-RU": "ロシア語",
    "pl-PL": "ポーランド語",
    "uk-UA": "ウクライナ語",
    "fi-FI": "フィンランド語",
    "et-EE": "エストニア語",
    "lv-LV": "ラトビア語",
    "el-GR": "ギリシャ語",
  },
};

// Korean
const ko: Translations = {
  nav: {
    dialogues: "대화",
    grammar: "문법",
    practice: "연습",
    alphabet: "알파벳",
  },
  languageSelector: {
    title: "언어",
    iSpeak: "사용 언어",
    iWantToLearn: "배우고 싶은 언어",
    done: "완료",
    back: "뒤로",
    moreComingSoon: "더 많은 언어가 곧 추가됩니다!",
  },
  topics: {
    title: "대화 주제",
    subtitle: "연습할 대화를 선택하세요",
    tagline: "필요한 말을 배우세요",
    heroTitle: "어떤 대화에도 준비되세요",
    heroSubtitle: "고개만 끄덕이지 말고 — 말을 시작해",
    createCustom: "새 대화",
    chooseTopic: "주제 선택",
    available: "이용 가능",
    words: "단어",
    expressions: "표현",
    difficulty: {
      beginner: "초급",
      intermediate: "중급",
      advanced: "고급",
    },
    dialogNodes: "턴",
    regenerateWarning: "다른 언어용 — 탭하여 다시 만들기",
    deleteTitle: "주제 삭제",
    deleteDescription: "이 주제를 삭제하시겠습니까? 이 작업은 취소할 수 없습니다.",
    regenerateTitle: "대화 재생성",
    regenerateDescription: "이 주제에 대한 새로운 대화가 생성됩니다. 현재 대화가 대체됩니다.",
    regenerate: "재생성",
    edit: "편집",
    recent: "최근",
    categories: "카테고리",
    byCategory: "카테고리별 대화",
    collapse: "접기",
  },
  grammar: {
    title: "문법 규칙",
    subtitle: "{language}의 구조",
    noRulesTitle: "아직 규칙이 없습니다",
    noRulesDescription: "{language}로 주제를 만들면 문법 규칙이 여기에 표시됩니다.",
    rulesCount: "{count}개의 문법 규칙",
    rulesSingular: "{count}개의 문법 규칙",
    tapToExpand: "탭하여 펼치기",
    examples: "대화 예시",
    howToThink: "이해하는 방법",
    commonMistake: "흔한 실수",
  },
  alphabet: {
    title: "알파벳과 발음",
    subtitle: "{language} 발음",
    comingSoonTitle: "곧 제공 예정",
    comingSoonDescription: "{language}의 알파벳 가이드는 아직 준비 중입니다.",
    examples: "예시",
    all: "전체",
  },
  generator: {
    title: "무엇에 대해 이야기하고 싶으세요?",
    placeholder: "상황을 설명하세요...\n\n예시:\n• 카페에서 커피 주문하기\n• 길 묻기\n• 병원 예약하기",
    proficiency: "어떻게 들리고 싶으세요?",
    generate: "시작하기",
    generating: "준비 중...",
    loadTopic: "불러오기",
    back: "뒤로",
    askAI: "상황을 설명하세요",
    responses: "개의 답변",
    modelTierFast: "빠름",
    modelTierFastDesc: "빠르고 저렴함",
    modelTierBalanced: "균형잡힘",
    modelTierBalancedDesc: "최고의 가성비",
    modelTierPremium: "프리미엄",
    modelTierPremiumDesc: "최고 품질",
    loadingModels: "모델 로딩 중...",
    llmConfigWarning: "AI 생성을 활성화하려면 설정에서 LLM 공급자를 구성하세요",
    topicTitle: "무엇에 대해 이야기하고 싶으세요?",
    topicHintMic: "마이크를 탭하고 주제를 말하세요",
    topicHintKeyboard: "시나리오나 주제를 입력하세요",
    exampleTapToUse: "예시 — 탭해서 사용",
    enterText: "텍스트 입력",
    tapToStop: "탭해서 중지",
    processing: "처리 중...",
    tapAndTalk: "탭하고 말하기",
    recording: "녹음 중...",
    useVoice: "음성 사용",
    attach: "첨부",
    continue: "계속",
    apiKeyRequired: "API 키 필요",
    apiKeyRequiredDesc: "AI 생성을 활성화하려면 설정에서 LLM 공급자를 구성하세요",
    difficultyTitle: "어떻게 들리고 싶으세요?",
    difficultySubtitle: "말하기 스타일 선택",
    styleBeginner: "초보자",
    styleBeginnerDesc: "간단한 단어 — 초보자처럼",
    styleLocal: "현지인처럼",
    styleLocalDesc: "자연스러운 말투, 슬랭 — 원어민처럼",
    styleMix: "섞기",
    styleMixDesc: "하나 이상 선택",
    styleCustomPlaceholder: "또는 직접 입력...",
    richnessTitle: "콘텐츠 양은?",
    richnessSubtitle: "어휘와 대화 양 선택",
    richnessQuick: "빠름",
    richnessQuickDesc: "짧은 대화, 필수 어휘",
    richnessStandard: "표준",
    richnessStandardDesc: "중간 대화, 좋은 어휘 범위",
    richnessComprehensive: "포괄적",
    richnessComprehensiveDesc: "긴 대화, 풍부한 어휘",
    modelTitle: "품질 수준 선택",
    modelSubtitle: "높은 품질 = 더 나은 콘텐츠, 높은 비용",
    cachedAvailable: "캐시된 응답 사용 가능 — 즉시 로드!",
    loadCached: "캐시 불러오기",
    previewPrompt: "프롬프트 미리보기",
    fullPrompt: "전체 프롬프트",
    copied: "복사됨!",
    systemPrompt: "시스템 프롬프트",
    userPrompt: "사용자 프롬프트",
    gettingReady: "준비 중...",
    connectingToAI: "AI에 연결 중...",
    done: "완료!",
    letsGo: "시작하기",
    goBack: "뒤로",
    playWhileWaiting: "기다리는 동안 {game1} 또는 {game2}을 해보세요!",
    play: "플레이",
    or: "또는",
    whileYouWait: "기다리는 동안",
    exitGame: "게임 종료",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "타이어 가게에서 온라인으로 타이어를 주문했는데 도착했어요. 이번 주에 장착 예약 전화를 해야 해요.",
      "스페셜티 커피숍에서 오트밀크 플랫화이트와 페이스트리를 주문하고 싶어요. 싱글오리진 원두가 있는지도 물어보고 싶어요.",
      "기차역에서 목적지까지 가야 해요. 몇 번 플랫폼인지, 왕복 티켓이 얼마인지, 카드로 결제할 수 있는지 알고 싶어요.",
      "친구들이랑 카트장에 왔어요. 4명이서 20분 세션을 예약하고 단체 할인이 있는지 물어보고 싶어요.",
      "핸드폰 화면이 깨져서 수리점에 왔어요. 얼마나 걸리는지, 비용이 얼마인지, 보증이 있는지 물어보고 싶어요.",
      "약국에서 심한 두통약과 알레르기약이 필요해요. 어떤 게 가장 센지, 처방전이 필요한지 알고 싶어요.",
      "에어비앤비에 체크인 중인데 호스트가 말한 곳에 열쇠가 없어요. 전화해서 어떻게 들어가는지 알아봐야 해요.",
      "해변가 해산물 레스토랑에서 생선구이를 주문하고 싶은데, 오늘 뭐가 신선한지 추천을 받고 싶어요.",
      "서핑 스쿨에서 내일 아침 초보자 수업을 예약하고 싶어요. 웻수트 대여와 도착 시간에 대해 물어봐야 해요.",
      "렌트카가 고속도로에서 펑크났어요. 긴급출동 서비스에 전화해서 어디 있는지, 무슨 일인지 설명해야 해요.",
    ],
  },
  pwa: {
    installPrompt: "더 나은 경험을 위해 SaySay를 설치하세요",
    install: "설치",
  },
  settings: {
    title: "설정",
    theme: "테마",
    themeLight: "라이트",
    themeDark: "다크",
    themeSystem: "자동",
    resetAll: "모든 데이터 삭제",
    resetConfirm: "모든 설정과 주제가 삭제됩니다. 확실합니까?",
    close: "닫기",
    speechRecognition: "음성 인식",
    sttAzure: "Azure",
    sttAzureDesc: "클라우드 기반, 고품질",
    sttBrowser: "브라우저",
    sttBrowserDesc: "내장, 오프라인 작동",
    speechSpeed: "말하기 속도",
    analytics: "분석",
    shareUsageData: "사용 데이터 공유",
    analyticsDescription: "익명 사용 데이터를 공유하여 SaySay 개선에 도움을 주세요",
    knownLanguages: "알고 있는 언어",
    knownLanguagesDescription: "이미 알고 있는 언어를 선택하세요. 이를 통해 해당 언어의 발음을 활용한 더 나은 발음 팁을 제공할 수 있습니다.",
    noneSelected: "선택 없음",
    learning: "학습",
    showPronunciationHints: "발음 힌트 표시",
    showPronunciationHintsDescription: "알고 있는 소리를 사용하여 문장 발음 방법을 표시합니다",
  },
  learning: {
    back: "뒤로",
    dialogues: "대화",
    vocabulary: "어휘",
    grammar: "문법",
    practice: "연습",
    cards: "Cards",
    listen: "듣기",
    showTranslation: "번역 보기",
    hideTranslation: "번역 숨기기",
    pronunciation: "발음",
    sayItLike: "이렇게 말해",
    allForms: "모든 형태",
    example: "예시",
    inContext: "맥락에서",
    literalMeaning: "직역",
    wordBreakdown: "단어 분석",
    fromDialog: "대화에서",
    searchVocabulary: "어휘 검색...",
    foundResults: "\"{query}\" 검색 결과: {count}개",
    progress: "진행 상황",
    mastered: "{count} / {total} 습득",
    words: "단어",
    expressions: "표현",
    topical: "알아두면 좋은",
    wordsDescription: "핵심 어휘와 활용형",
    expressionsDescription: "관용구와 표현",
    topicalDescription: "관련 어휘",
    noWordsMatch: "일치하는 단어가 없습니다",
    noExpressionsMatch: "일치하는 표현이 없습니다",
    noTopicalMatch: "관련 어휘가 없습니다",
    forms: "형태",
    buildingBlocks: "기본 구성 요소",
    showBuildingBlocks: "기본 구성 요소 표시",
    buildingBlocksDescription: "이 대화에 필요한 일반 단어 (관사, 전치사 등)",
    hideByDefault: "이미 알고 있어요 — 기본으로 숨기기",
    longPressHint: "길게 눌러 단어 검색",
    hideAll: "모두 숨기기",
    showAll: "모두 보기",
    tapToReveal: "탭하여 번역 보기",
    practiceMode: "연습 모드",
    practiceDescription: "실제 대화 시나리오를 경험하세요. 답변을 선택하고 대화가 어떻게 전개되는지 확인하세요.",
    howItWorks: "사용 방법:",
    practiceStep1: "대화 프롬프트 읽기",
    practiceStep2: "옵션에서 최선의 답변 선택",
    practiceStep3: "대화 끝까지 계속하기",
    startPractice: "연습 시작",
    greatJob: "잘했어요!",
    keepPracticing: "계속 연습하세요!",
    correctResponses: "{total}개 중 {correct}개 정답",
    yourConversation: "당신의 대화:",
    tryAgain: "다시 시도",
    newPath: "새 경로",
    turn: "턴 {count}",
    score: "점수: {correct}/{total}",
    chooseResponse: "답변을 선택하세요:",
    continueConversation: "대화 계속하기",
    seeResults: "결과 보기",
    conversationStarts: "대화 시작",
    you: "당신",
    them: "상대방",
    end: "끝",
    alsoSay: "다른 표현",
    playing: "재생 중...",
    askAI: "도움말",
    response: "개 답변",
    responses: "개 답변",
    or: "또는",
    systemResponse: "시스템/답변",
    yourOptions: "선택지",
    tapToExpand: "탭하여 펼치기. 길게 눌러 번역.",
    startAgain: "처음부터",
    pickYourAnswer: "답을 선택하세요",
    pickYourAnswerHint: "(아래 보기 중 하나를 탭하여 선택하세요)",
    tapToAnswer: "아래 답을 탭하여 계속하세요",
    pickTheirAnswer: "상대방의 답을 선택하세요",
    tapToStart: "탭하여 시작",
    transcribing: "받아쓰는 중...",
    useVoice: "음성 사용",
    saySomethingElse: "다른 말을 하고 싶어요",
    said: "라고 말했습니다",
    whatToSay: "무엇을 말하고 싶으세요?",
    typeYourResponse: "응답을 입력하세요...",
    customPhraseHint: "모국어로 입력하면 자연스럽게 번역해 드립니다",
    fixThisResponse: "이 응답 수정하기",
    whatsWrong: "대신 뭐라고 해야 하나요?",
    fixResponseHint: "수정 내용을 설명하거나 올바른 문구를 입력하세요",
    // Practice tab modes
    practiceTab: "연습",
    practiceWords: "단어",
    practiceSpelling: "맞춤법",
    practiceKaraoke: "가라오케",
    practiceWordsDesc: "이미지 퀴즈",
    practiceSpellingDesc: "단어를 소리 내어 말하기",
    practiceKaraokeDesc: "문구를 단어별로 말하기",
    introduceWord: "새 단어",
    composeWord: "단어 구성",
    describeImage: "그림에 있는 것을 설명하세요",
    pickWordForSentence: "문장에 맞는 단어를 고르세요",
    iNeedAHint: "힌트가 필요해요",
    iDontKnow: "이 단어를 모르겠어요",
    tapHoldSayWord: "탭하고 누른 채 단어를 말하세요",
    tapHoldSayPhrase: "탭하고 누른 채 문구를 말하세요",
    weHeard: "들은 내용:",
    skipWord: "건너뛰기",
    nextWord: "다음",
    practiceComplete: "연습 완료!",
    wordsCorrectCount: "{total}개 중 {correct}개 정답",
    practiceAgain: "다시 연습하기",
    done: "완료",
    correct: "정답!",
    chooseModule: "모듈 선택",
    moduleHint: "작은 그룹으로 연습하면 더 잘 기억돼요",
    wordsCount: "단어",
    markKnown: "알고 있어요",
    markTough: "어려워요",
    markLiked: "좋아요",
    knownWordsHidden: "{count}개의 아는 단어 숨김",
    noWordsToReview: "아직 복습할 단어가 없습니다",
    noWordsToReviewDesc: "대화를 생성하여 어휘를 늘려보세요",
    generatingMore: "생성 중...",
  },
  common: {
    loading: "로딩 중...",
    error: "오류가 발생했습니다",
    retry: "다시 시도",
    cancel: "취소",
    save: "저장",
    delete: "삭제",
    edit: "편집",
    close: "닫기",
    send: "보내기",
    copy: "복사",
  },
  games: {
    score: "점수",
    best: "최고",
    hi: "HI",
    tapOn: "탭하세요",
    getReady: "준비하세요...",
    tapToStart: "탭하여 시작!",
    tapToJump: "탭하여 점프!",
    gameOver: "게임 오버",
    tapToRestart: "탭하여 재시작",
    speedSlow: "느림",
    speedSteady: "꾸준히",
    speedNormal: "보통",
    speedQuick: "빠름",
    speedFast: "쾌속",
    speedBlazing: "초고속",
    speedOnFire: "불꽃!",
    speedRocket: "로켓",
    speedLegendary: "전설",
    cheers: ["좋아!", "훌륭해!", "대단해!", "와!", "멋져!", "맞았어!", "예!", "쾅!", "완벽해!", "최고야!"],
  },
  errors: {
    microphoneAccessDenied: "마이크 액세스가 거부되었습니다",
    recognitionError: "음성 인식 오류",
    recordingFailed: "녹음을 시작하지 못했습니다",
    noSpeechDetected: "음성이 감지되지 않았습니다. 다시 시도해 주세요.",
    llmConfigRequired: "이 기능을 사용하려면 설정에서 LLM API 키를 구성하세요.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "SaySay에 오신 것을 환영합니다",
    welcomeSubtitle: "어떤 대화에도 준비되어 있으세요",
    getStarted: "시작하기",
    // Feature slides
    featureTopicsPain: "교과서는 실생활에 맞지 않아요",
    featureTopicsSolution: "진짜 필요한 것을 연습하세요",
    featureTopicsDesc: "어떤 상황이든 설명해 보세요 — 커피 주문, 병원 방문, 이웃과 대화. 맞춤형 대화를 받아보세요.",
    featureVoicePain: "그걸 어떻게 발음하지?",
    featureVoiceSolution: "모든 단어를 들어보세요",
    featureVoiceDesc: "어떤 문장이든 탭해서 원어민 발음을 들어보세요. 음성으로 핸즈프리 응답하세요.",
    featureAiPain: "획일적인 방식은 안 통해요",
    featureAiSolution: "AI가 어휘를 만들어줍니다",
    featureAiDesc: "모든 대화에는 실제로 사용할 단어와 표현이 포함되어 있어요. 내 삶에 맞는 어휘를 배워보세요.",
    continue: "계속",
    inputNativeLangTitle: "어떤 언어를 사용하시나요?",
    inputNativeLangSubtitle: "이 언어로 가르쳐 드릴게요",
    inputTargetLangTitle: "무엇을 배우고 싶으신가요?",
    inputTargetLangSubtitle: "연습하고 싶은 언어를 선택하세요",
    // Input: Name
    inputNameTitle: "뭐라고 불러드릴까요?",
    inputNameSubtitle: "나중에 변경할 수 있어요",
    inputNamePlaceholder: "이름",
    // Input: Languages
    inputLangTitle: "다른 언어를 알고 계신가요?",
    inputLangSubtitle: "유사점을 활용해 더 빨리 배울 수 있어요",
    inputLangSelect: "한국어 외에 아는 언어를 선택하세요",
    inputLangFindMore: "더 찾기",
    inputLangSearch: "언어 검색...",
    skipStep: "건너뛰기",
    // Input: Style
    inputStyleTitle: "어떻게 말하고 싶으세요?",
    inputStyleSubtitle: "나중에 변경할 수 있어요",
    speakingStyleBeginner: "초급자",
    speakingStyleBeginnerDesc: "간단한 단어, 기본 문장 — 배우는 사람처럼",
    speakingStyleLocal: "현지인처럼",
    speakingStyleLocalDesc: "자연스러운 표현, 속어, 관용구 — 어울리기",
    speakingStyleMix: "스타일 섞기",
    speakingStyleMixDesc: "하나 이상의 스타일을 선택하세요",
    styleYourOption: "직접 입력...",
    styleTypePlaceholder: "스타일을 입력하세요...",
    // Input: Gender
    inputGenderTitle: "대화에서의 역할은?",
    inputGenderSubtitle: "선택사항 — 나중에 변경할 수 있어요",
    genderMale: "남성",
    genderFemale: "여성",
    genderOther: "기타 / 논바이너리",
    genderPreferNot: "밝히고 싶지 않음",
    // Navigation
    back: "뒤로",
    skipAll: "모두 건너뛰기",
    // Ready screen
    startLearning: "학습 시작",
    youreAllSet: "준비 완료!",
    youreAllSetWithName: "{name}님, 준비 완료!",
    startFirstConversation: "첫 대화를 시작하고 자연스러운 언어 학습을 경험해보세요.",
    analyticsOptIn: "익명 사용 데이터를 공유해 SaySay 개선에 도움을 주세요 (개인정보 수집 없음)",
    // First conversation transition screen
    firstConversationTitle: "좋아요! 이제 첫 번째 대화를 연습해 봅시다.",
    firstConversationSubtitle: "주제를 선택하면 연습할 수 있는 실제 대화를 만들어 드립니다.",
    firstConversationButton: "시작하기!",
    // Preview UI elements
    previewTopicTitle: "무엇에 대해 이야기하고 싶으세요?",
    previewTopicSubtitle: "시나리오나 주제를 입력하세요",
    previewExample: "예시 — 탭하여 사용",
    previewExampleText: "커피 주문하고 현지 추천 물어보기",
    previewTypeOwn: "또는 직접 입력...",
    previewVoiceText: "커피 한 잔 주세요...",
    previewDialogTranslation: "좋은 아침이에요! 무엇을 드릴까요?",
  },
  knownLanguages: {
    spanish: "스페인어",
    french: "프랑스어",
    german: "독일어",
    italian: "이탈리아어",
    portuguese: "포르투갈어",
    russian: "러시아어",
    chinese: "중국어",
    japanese: "일본어",
    korean: "한국어",
    arabic: "아랍어",
    hindi: "힌디어",
    dutch: "네덜란드어",
    polish: "폴란드어",
    swedish: "스웨덴어",
    turkish: "터키어",
    greek: "그리스어",
    czech: "체코어",
    romanian: "루마니아어",
    hungarian: "헝가리어",
    finnish: "핀란드어",
    norwegian: "노르웨이어",
    danish: "덴마크어",
    ukrainian: "우크라이나어",
    thai: "태국어",
    vietnamese: "베트남어",
    indonesian: "인도네시아어",
    malay: "말레이어",
    tagalog: "타갈로그어",
    hebrew: "히브리어",
    persian: "페르시아어",
    bengali: "벵골어",
    punjabi: "펀자브어",
    tamil: "타밀어",
    urdu: "우르두어",
    swahili: "스와힐리어",
    afrikaans: "아프리칸스어",
    catalan: "카탈로니아어",
    croatian: "크로아티아어",
    serbian: "세르비아어",
    slovak: "슬로바키아어",
    slovenian: "슬로베니아어",
    bulgarian: "불가리아어",
    lithuanian: "리투아니아어",
    latvian: "라트비아어",
    estonian: "에스토니아어",
    icelandic: "아이슬란드어",
    welsh: "웨일스어",
    irish: "아일랜드어",
    scottishGaelic: "스코틀랜드 게일어",
    basque: "바스크어",
    maltese: "몰타어",
    albanian: "알바니아어",
    macedonian: "마케도니아어",
    georgian: "조지아어",
    armenian: "아르메니아어",
    kazakh: "카자흐어",
    uzbek: "우즈베크어",
    mongolian: "몽골어",
    nepali: "네팔어",
    sinhala: "싱할라어",
    burmese: "버마어",
    khmer: "크메르어",
    lao: "라오어",
  },
  coachMarks: {
    startNodeTitle: "대화 시작하기",
    startNodeBody: "이 메시지 말풍선을 탭하여 대화를 시작하세요.",
    speakerTitle: "발음 듣기",
    speakerBody: "스피커 아이콘을 탭하여 문장을 들어보세요.",
    answerTitle: "답변 선택하기",
    answerBody: "옵션 중 하나를 탭하여 말하고 싶은 것을 선택하세요.",
    tabsTitle: "더 살펴보기",
    tabsBody: "대화, 연습 문제, 어휘 사이를 전환하세요.",
    skip: "건너뛰기",
    next: "다음",
    done: "완료",
  },
  passwordGate: {
    enterPassword: "계속하려면 비밀번호를 입력하세요",
    placeholder: "비밀번호",
  },
  dialogueFeedback: {
    title: "이 대화는 어땠나요?",
    bad: "나쁨",
    meh: "보통",
    good: "좋음",
    thanks: "피드백 감사합니다!",
    tellUsWhy: "감사합니다! 이유를 알려주시겠어요?",
    commentPlaceholder: "어떤 점이 개선되면 좋을까요?",
    skip: "건너뛰기",
    send: "보내기",
  },
  dialog: {
    listening: "듣고 있습니다...",
    stopRecording: "녹음 중지",
    practice: "연습",
    vocabulary: "어휘",
    loadingVocabulary: "어휘 로딩 중...",
  },
  languageNames: {
    "en-US": "영어 (미국)",
    "en-GB": "영어 (영국)",
    "es-ES": "스페인어 (스페인)",
    "es-MX": "스페인어 (멕시코)",
    "pt-PT": "포르투갈어 (포르투갈)",
    "pt-BR": "포르투갈어 (브라질)",
    "fr-FR": "프랑스어",
    "de-DE": "독일어",
    "de-AT": "독일어 (오스트리아)",
    "de-CH": "독일어 (스위스)",
    "it-IT": "이탈리아어",
    "ja-JP": "일본어",
    "zh-CN": "중국어",
    "ko-KR": "한국어",
    "he-IL": "히브리어",
    "ru-RU": "러시아어",
    "pl-PL": "폴란드어",
    "uk-UA": "우크라이나어",
    "fi-FI": "핀란드어",
    "et-EE": "에스토니아어",
    "lv-LV": "라트비아어",
    "el-GR": "그리스어",
  },
};

// Chinese (Simplified)
const zh: Translations = {
  nav: {
    dialogues: "对话",
    grammar: "语法",
    practice: "练习",
    alphabet: "字母",
  },
  languageSelector: {
    title: "语言",
    iSpeak: "我会说",
    iWantToLearn: "我想学习",
    done: "完成",
    back: "返回",
    moreComingSoon: "更多语言即将推出！",
  },
  topics: {
    title: "对话主题",
    subtitle: "选择一个对话来练习",
    tagline: "学会说你需要的话",
    heroTitle: "为任何对话做好准备",
    heroSubtitle: "别再只是点头——开口说话",
    createCustom: "新对话",
    chooseTopic: "选择主题",
    available: "可用",
    words: "单词",
    expressions: "表达",
    difficulty: {
      beginner: "初级",
      intermediate: "中级",
      advanced: "高级",
    },
    dialogNodes: "轮次",
    regenerateWarning: "为其他语言创建 — 点击重新创建",
    deleteTitle: "删除主题",
    deleteDescription: "您确定要删除此主题吗？此操作无法撤消。",
    regenerateTitle: "重新生成对话",
    regenerateDescription: "将为此主题创建新的对话。当前对话将被替换。",
    regenerate: "重新生成",
    edit: "编辑",
    recent: "最近",
    categories: "分类",
    byCategory: "按类别分类的对话",
    collapse: "收起",
  },
  grammar: {
    title: "语法规则",
    subtitle: "{language}的结构",
    noRulesTitle: "暂无规则",
    noRulesDescription: "用{language}创建主题后，语法规则将显示在这里。",
    rulesCount: "{count}条语法规则",
    rulesSingular: "{count}条语法规则",
    tapToExpand: "点击展开学习",
    examples: "对话示例",
    howToThink: "理解方式",
    commonMistake: "常见错误",
  },
  alphabet: {
    title: "字母与发音",
    subtitle: "{language}发音",
    comingSoonTitle: "即将推出",
    comingSoonDescription: "{language}的字母指南尚未提供。",
    examples: "示例",
    all: "全部",
  },
  generator: {
    title: "你想聊什么？",
    placeholder: "描述一个情境...\n\n示例:\n• 在咖啡馆点咖啡\n• 问路\n• 预约医生",
    proficiency: "你想听起来怎样？",
    generate: "开始吧",
    generating: "准备中...",
    loadTopic: "加载",
    back: "返回",
    askAI: "描述你的情境",
    responses: "条回复",
    modelTierFast: "快速",
    modelTierFastDesc: "快速且实惠",
    modelTierBalanced: "均衡",
    modelTierBalancedDesc: "最佳性价比",
    modelTierPremium: "高级",
    modelTierPremiumDesc: "最高品质",
    loadingModels: "正在加载模型...",
    llmConfigWarning: "请在设置中配置您的LLM提供商以启用AI生成",
    topicTitle: "你想聊什么？",
    topicHintMic: "点击麦克风说出你的主题",
    topicHintKeyboard: "输入场景或主题",
    exampleTapToUse: "示例 — 点击使用",
    enterText: "输入文字",
    tapToStop: "点击停止",
    processing: "处理中...",
    tapAndTalk: "点击说话",
    recording: "录音中...",
    useVoice: "使用语音",
    attach: "附加",
    continue: "继续",
    apiKeyRequired: "需要API密钥",
    apiKeyRequiredDesc: "请在设置中配置您的LLM提供商以启用AI生成",
    difficultyTitle: "你想听起来怎样？",
    difficultySubtitle: "选择你的说话风格",
    styleBeginner: "初学者",
    styleBeginnerDesc: "简单词汇 — 像初学者",
    styleLocal: "像本地人",
    styleLocalDesc: "自然表达、俚语 — 像本地人",
    styleMix: "混搭",
    styleMixDesc: "选择一个或多个",
    styleCustomPlaceholder: "或自己输入...",
    richnessTitle: "内容量？",
    richnessSubtitle: "选择词汇和对话的数量",
    richnessQuick: "快速",
    richnessQuickDesc: "短对话，基本词汇",
    richnessStandard: "标准",
    richnessStandardDesc: "中等对话，良好词汇覆盖",
    richnessComprehensive: "全面",
    richnessComprehensiveDesc: "长对话，丰富词汇",
    modelTitle: "选择质量级别",
    modelSubtitle: "更高质量 = 更好内容，更高成本",
    cachedAvailable: "缓存响应可用 — 即时加载！",
    loadCached: "加载缓存",
    previewPrompt: "预览提示词",
    fullPrompt: "完整提示词",
    copied: "已复制！",
    systemPrompt: "系统提示词",
    userPrompt: "用户提示词",
    gettingReady: "准备中...",
    connectingToAI: "连接AI中...",
    done: "完成！",
    letsGo: "开始吧",
    goBack: "返回",
    playWhileWaiting: "等待时来玩{game1}或{game2}吧！",
    play: "玩",
    or: "或",
    whileYouWait: "等待时",
    exitGame: "退出游戏",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "我在网上的轮胎店订了轮胎，已经到了。我需要打电话预约这周安装。",
      "我在一家精品咖啡店，想点一杯燕麦奶澳白和一份点心。还想问问有没有单品咖啡豆卖。",
      "我在火车站，需要到达目的地。想知道是哪个站台、往返票多少钱、能不能刷卡。",
      "我和朋友在卡丁车场。我们想预订4人20分钟的场次，问问有没有团体折扣。",
      "我的手机屏幕碎了，在维修店。想问修多久、多少钱、有没有保修。",
      "我在药店，需要买治严重头痛的药和过敏药。想知道哪个最有效，需不需要处方。",
      "我在办理Airbnb入住，但钥匙不在房东说的地方。需要打电话问怎么进去。",
      "我在海边的海鲜餐厅，想点烤鱼，但想知道今天什么新鲜，想要推荐。",
      "我在冲浪学校，想预约明天早上的初学者课程。需要问问潜水服租借和几点到。",
      "我的租车在高速公路上爆胎了。需要打道路救援电话，说明我在哪里、发生了什么。",
    ],
  },
  pwa: {
    installPrompt: "安装 SaySay 以获得更好的体验",
    install: "安装",
  },
  settings: {
    title: "设置",
    theme: "外观",
    themeLight: "浅色",
    themeDark: "深色",
    themeSystem: "自动",
    resetAll: "删除所有数据",
    resetConfirm: "这将删除所有设置和主题。确定吗？",
    close: "关闭",
    speechRecognition: "语音识别",
    sttAzure: "Azure",
    sttAzureDesc: "云端，高品质",
    sttBrowser: "浏览器",
    sttBrowserDesc: "内置，离线可用",
    speechSpeed: "语速",
    analytics: "分析",
    shareUsageData: "共享使用数据",
    analyticsDescription: "通过共享匿名使用数据帮助改进SaySay",
    knownLanguages: "已知语言",
    knownLanguagesDescription: "选择您已经掌握的语言。这有助于我们使用这些语言的发音提供更好的发音技巧。",
    noneSelected: "未选择",
    learning: "学习",
    showPronunciationHints: "显示发音提示",
    showPronunciationHintsDescription: "使用你已知的发音来展示如何说短语",
  },
  learning: {
    back: "返回",
    dialogues: "对话",
    vocabulary: "词汇",
    grammar: "语法",
    practice: "练习",
    cards: "Cards",
    listen: "听",
    showTranslation: "显示翻译",
    hideTranslation: "隐藏翻译",
    pronunciation: "发音",
    sayItLike: "这样说",
    allForms: "所有形式",
    example: "示例",
    inContext: "在语境中",
    literalMeaning: "字面意思",
    wordBreakdown: "词汇分解",
    fromDialog: "来自对话",
    searchVocabulary: "搜索词汇...",
    foundResults: "找到 {count} 个\"{query}\"的结果",
    progress: "进度",
    mastered: "{count} / {total} 已掌握",
    words: "单词",
    expressions: "表达",
    topical: "值得了解",
    wordsDescription: "核心词汇及变形",
    expressionsDescription: "短语和习语",
    topicalDescription: "相关词汇",
    noWordsMatch: "没有匹配的单词",
    noExpressionsMatch: "没有匹配的表达",
    noTopicalMatch: "没有匹配的相关词汇",
    forms: "形式",
    buildingBlocks: "基础词汇",
    showBuildingBlocks: "显示基础词汇",
    buildingBlocksDescription: "构建对话所需的常用词（冠词、介词等）",
    hideByDefault: "我已经认识了 — 默认隐藏",
    longPressHint: "长按任意单词查看",
    hideAll: "全部隐藏",
    showAll: "全部显示",
    tapToReveal: "点击显示翻译",
    practiceMode: "练习模式",
    practiceDescription: "体验真实对话场景。选择回答，看看对话如何展开。",
    howItWorks: "使用方法：",
    practiceStep1: "阅读对话提示",
    practiceStep2: "从选项中选择最佳回答",
    practiceStep3: "继续直到对话结束",
    startPractice: "开始练习",
    greatJob: "做得好！",
    keepPracticing: "继续练习！",
    correctResponses: "{total}题中答对{correct}题",
    yourConversation: "您的对话：",
    tryAgain: "重试",
    newPath: "新路径",
    turn: "第{count}轮",
    score: "得分：{correct}/{total}",
    chooseResponse: "选择您的回答：",
    continueConversation: "继续对话",
    seeResults: "查看结果",
    conversationStarts: "对话开始",
    you: "你",
    them: "对方",
    end: "结束",
    alsoSay: "也可以说",
    playing: "播放中...",
    askAI: "帮助",
    response: "个回答",
    responses: "个回答",
    or: "或",
    systemResponse: "系统/回答",
    yourOptions: "你的选项",
    tapToExpand: "点击展开。长按单词翻译。",
    startAgain: "重新开始",
    pickYourAnswer: "选择你的回答",
    pickYourAnswerHint: "(点击下方选项来选择)",
    tapToAnswer: "点击下方答案继续",
    pickTheirAnswer: "选择对方的回答",
    tapToStart: "点击开始",
    transcribing: "识别中...",
    useVoice: "语音输入",
    saySomethingElse: "我想说点别的",
    said: "说",
    whatToSay: "你想说什么？",
    typeYourResponse: "输入你的回复...",
    customPhraseHint: "用你的语言输入，我们会自然地翻译",
    fixThisResponse: "修正此回复",
    whatsWrong: "应该怎么说？",
    fixResponseHint: "描述修正内容或提供正确的短语",
    // Practice tab modes
    practiceTab: "练习",
    practiceWords: "单词",
    practiceSpelling: "拼写",
    practiceKaraoke: "卡拉OK",
    practiceWordsDesc: "图片测验",
    practiceSpellingDesc: "大声说出单词",
    practiceKaraokeDesc: "逐词说出短语",
    introduceWord: "新单词",
    composeWord: "组成一个单词",
    describeImage: "描述图片中的内容",
    pickWordForSentence: "为句子选择一个单词",
    iNeedAHint: "我需要提示",
    iDontKnow: "我不认识这个词",
    tapHoldSayWord: "点击、按住并说出单词",
    tapHoldSayPhrase: "点击、按住并说出短语",
    weHeard: "我们听到了：",
    skipWord: "跳过",
    nextWord: "下一个",
    practiceComplete: "练习完成！",
    wordsCorrectCount: "{total}个中答对{correct}个",
    practiceAgain: "再次练习",
    done: "完成",
    correct: "正确！",
    chooseModule: "选择模块",
    moduleHint: "分小组练习，记忆更牢固",
    wordsCount: "个单词",
    markKnown: "已掌握",
    markTough: "难记",
    markLiked: "收藏",
    knownWordsHidden: "已隐藏{count}个已知单词",
    noWordsToReview: "还没有需要复习的单词",
    noWordsToReviewDesc: "生成一些对话来积累词汇",
    generatingMore: "生成中...",
  },
  common: {
    loading: "加载中...",
    error: "发生错误",
    retry: "重试",
    cancel: "取消",
    save: "保存",
    delete: "删除",
    edit: "编辑",
    close: "关闭",
    send: "发送",
    copy: "复制",
  },
  games: {
    score: "得分",
    best: "最佳",
    hi: "HI",
    tapOn: "点击",
    getReady: "准备好...",
    tapToStart: "点击开始！",
    tapToJump: "点击跳跃！",
    gameOver: "游戏结束",
    tapToRestart: "点击重新开始",
    speedSlow: "慢速",
    speedSteady: "平稳",
    speedNormal: "正常",
    speedQuick: "快速",
    speedFast: "飞速",
    speedBlazing: "极速",
    speedOnFire: "燃烧！",
    speedRocket: "火箭",
    speedLegendary: "传奇",
    cheers: ["不错！", "太棒了！", "难以置信！", "哇！", "精彩！", "答对了！", "耶！", "轰！", "完美！", "厉害！"],
  },
  errors: {
    microphoneAccessDenied: "麦克风访问被拒绝",
    recognitionError: "语音识别错误",
    recordingFailed: "无法开始录音",
    noSpeechDetected: "未检测到语音。请重试。",
    llmConfigRequired: "请在设置中配置您的LLM API密钥以使用此功能。",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "欢迎来到 SaySay",
    welcomeSubtitle: "为任何对话做好准备",
    getStarted: "开始",
    // Feature slides
    featureTopicsPain: "教科书不适合你的生活",
    featureTopicsSolution: "练习你真正需要的内容",
    featureTopicsDesc: "描述任何场景 — 点咖啡、看医生、和邻居聊天 — 获得专属于你的对话。",
    featureVoicePain: "这个到底怎么发音？",
    featureVoiceSolution: "听听每个词怎么说",
    featureVoiceDesc: "点击任意句子听母语者发音。用语音免提回复。",
    featureAiPain: "千篇一律行不通",
    featureAiSolution: "AI 为你打造词汇库",
    featureAiDesc: "每段对话都包含你实际会用到的词汇和表达。学习真正适合你生活的词汇。",
    continue: "继续",
    inputNativeLangTitle: "你说什么语言？",
    inputNativeLangSubtitle: "我们将用这种语言教你",
    inputTargetLangTitle: "你想学什么语言？",
    inputTargetLangSubtitle: "选择你想练习的语言",
    // Input: Name
    inputNameTitle: "怎么称呼你？",
    inputNameSubtitle: "之后可以更改",
    inputNamePlaceholder: "你的名字",
    // Input: Languages
    inputLangTitle: "你会其他语言吗？",
    inputLangSubtitle: "我们会利用相似性帮你更快学习",
    inputLangSelect: "选择你除中文外会的语言",
    inputLangFindMore: "查找更多",
    inputLangSearch: "搜索语言...",
    skipStep: "跳过",
    // Input: Style
    inputStyleTitle: "你想听起来怎样？",
    inputStyleSubtitle: "之后可以更改",
    speakingStyleBeginner: "初学者",
    speakingStyleBeginnerDesc: "简单词汇、基本句子 — 像学习者一样",
    speakingStyleLocal: "像本地人",
    speakingStyleLocalDesc: "自然表达、俚语、习语 — 融入当地",
    speakingStyleMix: "混搭风格",
    speakingStyleMixDesc: "选择一种或多种风格",
    styleYourOption: "自定义...",
    styleTypePlaceholder: "输入你的风格...",
    // Input: Gender
    inputGenderTitle: "对话中你是谁？",
    inputGenderSubtitle: "可选 — 之后可以更改",
    genderMale: "男性",
    genderFemale: "女性",
    genderOther: "其他 / 非二元",
    genderPreferNot: "不想说",
    // Navigation
    back: "返回",
    skipAll: "全部跳过",
    // Ready screen
    startLearning: "开始学习",
    youreAllSet: "准备就绪！",
    youreAllSetWithName: "{name}，准备就绪！",
    startFirstConversation: "开始你的第一次对话，体验自然的语言学习。",
    analyticsOptIn: "分享匿名使用数据，帮助改进 SaySay（不收集个人信息）",
    // First conversation transition screen
    firstConversationTitle: "太好了！现在来练习你的第一段对话吧。",
    firstConversationSubtitle: "选择一个话题，我们会生成真实的对话供你练习。",
    firstConversationButton: "开始吧！",
    // Preview UI elements
    previewTopicTitle: "你想聊什么？",
    previewTopicSubtitle: "输入场景或话题",
    previewExample: "示例 — 点击使用",
    previewExampleText: "点咖啡并询问当地推荐",
    previewTypeOwn: "或输入你自己的...",
    previewVoiceText: "我想要一杯咖啡...",
    previewDialogTranslation: "早上好！请问您想要什么？",
  },
  knownLanguages: {
    spanish: "西班牙语",
    french: "法语",
    german: "德语",
    italian: "意大利语",
    portuguese: "葡萄牙语",
    russian: "俄语",
    chinese: "中文",
    japanese: "日语",
    korean: "韩语",
    arabic: "阿拉伯语",
    hindi: "印地语",
    dutch: "荷兰语",
    polish: "波兰语",
    swedish: "瑞典语",
    turkish: "土耳其语",
    greek: "希腊语",
    czech: "捷克语",
    romanian: "罗马尼亚语",
    hungarian: "匈牙利语",
    finnish: "芬兰语",
    norwegian: "挪威语",
    danish: "丹麦语",
    ukrainian: "乌克兰语",
    thai: "泰语",
    vietnamese: "越南语",
    indonesian: "印尼语",
    malay: "马来语",
    tagalog: "他加禄语",
    hebrew: "希伯来语",
    persian: "波斯语",
    bengali: "孟加拉语",
    punjabi: "旁遮普语",
    tamil: "泰米尔语",
    urdu: "乌尔都语",
    swahili: "斯瓦希里语",
    afrikaans: "南非荷兰语",
    catalan: "加泰罗尼亚语",
    croatian: "克罗地亚语",
    serbian: "塞尔维亚语",
    slovak: "斯洛伐克语",
    slovenian: "斯洛文尼亚语",
    bulgarian: "保加利亚语",
    lithuanian: "立陶宛语",
    latvian: "拉脱维亚语",
    estonian: "爱沙尼亚语",
    icelandic: "冰岛语",
    welsh: "威尔士语",
    irish: "爱尔兰语",
    scottishGaelic: "苏格兰盖尔语",
    basque: "巴斯克语",
    maltese: "马耳他语",
    albanian: "阿尔巴尼亚语",
    macedonian: "马其顿语",
    georgian: "格鲁吉亚语",
    armenian: "亚美尼亚语",
    kazakh: "哈萨克语",
    uzbek: "乌兹别克语",
    mongolian: "蒙古语",
    nepali: "尼泊尔语",
    sinhala: "僧伽罗语",
    burmese: "缅甸语",
    khmer: "高棉语",
    lao: "老挝语",
  },
  coachMarks: {
    startNodeTitle: "开始对话",
    startNodeBody: "点击此消息气泡开始对话。",
    speakerTitle: "听发音",
    speakerBody: "点击扬声器图标来听短语的发音。",
    answerTitle: "选择你的回答",
    answerBody: "点击其中一个选项来选择你想说的话。",
    tabsTitle: "探索更多",
    tabsBody: "在对话、练习和词汇之间切换。",
    skip: "跳过",
    next: "下一步",
    done: "完成",
  },
  passwordGate: {
    enterPassword: "输入密码以继续",
    placeholder: "密码",
  },
  dialogueFeedback: {
    title: "你觉得这段对话怎么样？",
    bad: "差",
    meh: "一般",
    good: "好",
    thanks: "感谢你的反馈！",
    tellUsWhy: "谢谢！能告诉我们原因吗？",
    commentPlaceholder: "哪里可以改进？",
    skip: "跳过",
    send: "发送",
  },
  dialog: {
    listening: "正在聆听...",
    stopRecording: "停止录音",
    practice: "练习",
    vocabulary: "词汇",
    loadingVocabulary: "正在加载词汇...",
  },
  languageNames: {
    "en-US": "英语（美国）",
    "en-GB": "英语（英国）",
    "es-ES": "西班牙语（西班牙）",
    "es-MX": "西班牙语（墨西哥）",
    "pt-PT": "葡萄牙语（葡萄牙）",
    "pt-BR": "葡萄牙语（巴西）",
    "fr-FR": "法语",
    "de-DE": "德语",
    "de-AT": "德语（奥地利）",
    "de-CH": "德语（瑞士）",
    "it-IT": "意大利语",
    "ja-JP": "日语",
    "zh-CN": "中文",
    "ko-KR": "韩语",
    "he-IL": "希伯来语",
    "ru-RU": "俄语",
    "pl-PL": "波兰语",
    "uk-UA": "乌克兰语",
    "fi-FI": "芬兰语",
    "et-EE": "爱沙尼亚语",
    "lv-LV": "拉脱维亚语",
    "el-GR": "希腊语",
  },
};

// Hebrew
const he: Translations = {
  nav: {
    dialogues: "דיאלוגים",
    grammar: "דקדוק",
    practice: "תרגול",
    alphabet: "אלפבית",
  },
  languageSelector: {
    title: "שפות",
    iSpeak: "אני מדבר",
    iWantToLearn: "אני רוצה ללמוד",
    done: "סיום",
    back: "חזרה",
    moreComingSoon: "עוד שפות בקרוב!",
  },
  topics: {
    title: "נושאי שיחה",
    subtitle: "בחר שיחה לתרגול",
    tagline: "למד לומר את מה שאתה צריך",
    heroTitle: "היה מוכן לכל שיחה",
    heroSubtitle: "תפסיק להנהן — תתחיל לדבר",
    createCustom: "שיחה חדשה",
    chooseTopic: "בחר נושא",
    available: "זמינים",
    words: "מילים",
    expressions: "ביטויים",
    difficulty: {
      beginner: "מתחיל",
      intermediate: "בינוני",
      advanced: "מתקדם",
    },
    dialogNodes: "תורות",
    regenerateWarning: "נוצר לשפה אחרת — הקש ליצירה מחדש",
    deleteTitle: "מחק נושא",
    deleteDescription: "האם אתה בטוח שברצונך למחוק נושא זה? לא ניתן לבטל פעולה זו.",
    regenerateTitle: "יצירת דיאלוג מחדש",
    regenerateDescription: "ייווצר דיאלוג חדש לנושא זה. הדיאלוג הנוכחי יוחלף.",
    regenerate: "יצירה מחדש",
    edit: "עריכה",
    recent: "אחרונים",
    categories: "קטגוריות",
    byCategory: "דיאלוגים לפי קטגוריה",
    collapse: "צמצם",
  },
  grammar: {
    title: "כללי דקדוק",
    subtitle: "איך {language} עובד",
    noRulesTitle: "אין עדיין כללים",
    noRulesDescription: "צור נושא ב{language} כדי לראות כללי דקדוק כאן.",
    rulesCount: "{count} כללי דקדוק",
    rulesSingular: "כלל דקדוק {count}",
    tapToExpand: "הקש להרחבה",
    examples: "דוגמאות מהדיאלוג",
    howToThink: "איך לחשוב על זה",
    commonMistake: "טעות נפוצה",
  },
  alphabet: {
    title: "אלפבית וצלילים",
    subtitle: "הגייה של {language}",
    comingSoonTitle: "בקרוב",
    comingSoonDescription: "מדריך האלפבית ל{language} עדיין לא זמין.",
    examples: "דוגמאות",
    all: "הכל",
  },
  generator: {
    title: "על מה אתה רוצה לדבר?",
    placeholder: "תאר מצב...\n\nדוגמאות:\n• להזמין קפה בבית קפה\n• לשאול הוראות\n• לקבוע תור לרופא",
    proficiency: "איך אתה רוצה להישמע?",
    generate: "בוא נתחיל",
    generating: "מכינים הכל...",
    loadTopic: "טען",
    back: "חזרה",
    askAI: "תאר את המצב שלך",
    responses: "תשובות",
    modelTierFast: "מהיר",
    modelTierFastDesc: "מהיר ובמחיר נוח",
    modelTierBalanced: "מאוזן",
    modelTierBalancedDesc: "הערך הטוב ביותר",
    modelTierPremium: "פרימיום",
    modelTierPremiumDesc: "איכות גבוהה ביותר",
    loadingModels: "טוען מודלים...",
    llmConfigWarning: "הגדר את ספק ה-LLM שלך בהגדרות כדי להפעיל יצירת AI",
    topicTitle: "על מה תרצה לדבר?",
    topicHintMic: "הקש על המיקרופון ואמור את הנושא שלך",
    topicHintKeyboard: "הקלד תרחיש או נושא",
    exampleTapToUse: "דוגמה — הקש להשתמש",
    enterText: "הזן טקסט",
    tapToStop: "הקש לעצור",
    processing: "מעבד...",
    tapAndTalk: "הקש ודבר",
    recording: "מקליט...",
    useVoice: "השתמש בקול",
    attach: "צרף",
    continue: "המשך",
    apiKeyRequired: "נדרש מפתח API",
    apiKeyRequiredDesc: "הגדר את ספק ה-LLM שלך בהגדרות כדי להפעיל יצירת AI",
    difficultyTitle: "איך אתה רוצה להישמע?",
    difficultySubtitle: "בחר את סגנון הדיבור שלך",
    styleBeginner: "מתחיל",
    styleBeginnerDesc: "מילים פשוטות — כמו מתחיל",
    styleLocal: "כמו מקומי",
    styleLocalDesc: "דיבור טבעי, סלנג — כמו מקומי",
    styleMix: "לערבב",
    styleMixDesc: "בחר אחד או יותר",
    styleCustomPlaceholder: "או הקלד משלך...",
    richnessTitle: "כמה תוכן?",
    richnessSubtitle: "בחר את כמות האוצר מילים והדיאלוג",
    richnessQuick: "מהיר",
    richnessQuickDesc: "דיאלוג קצר, אוצר מילים חיוני",
    richnessStandard: "סטנדרטי",
    richnessStandardDesc: "דיאלוג בינוני, כיסוי טוב של אוצר מילים",
    richnessComprehensive: "מקיף",
    richnessComprehensiveDesc: "דיאלוג ארוך, אוצר מילים נרחב",
    modelTitle: "בחר רמת איכות",
    modelSubtitle: "איכות גבוהה יותר = תוכן טוב יותר, עלות גבוהה יותר",
    cachedAvailable: "תגובה מהמטמון זמינה — טעינה מיידית!",
    loadCached: "טען מהמטמון",
    previewPrompt: "תצוגה מקדימה של הפרומפט",
    fullPrompt: "פרומפט מלא",
    copied: "הועתק!",
    systemPrompt: "פרומפט מערכת",
    userPrompt: "פרומפט משתמש",
    gettingReady: "מכינים הכל...",
    connectingToAI: "מתחבר ל-AI...",
    done: "סיום!",
    letsGo: "בוא נתחיל",
    goBack: "חזור",
    playWhileWaiting: "שחקו {game1} או {game2} בזמן ההמתנה!",
    play: "שחקו",
    or: "או",
    whileYouWait: "בזמן ההמתנה",
    exitGame: "יציאה מהמשחק",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "הזמנתי צמיגים אונליין בחנות צמיגים והם הגיעו. אני צריך להתקשר ולקבוע תור להתקנה השבוע.",
      "אני בבית קפה מיוחד ורוצה להזמין פלט וייט עם חלב שיבולת שועל ומאפה. רוצה גם לשאול אם יש להם פולים סינגל אוריג'ין.",
      "אני בתחנת הרכבת וצריך להגיע ליעד. רוצה לדעת איזה רציף, כמה עולה כרטיס הלוך-חזור, ואם אפשר לשלם בכרטיס.",
      "אני במסלול גו-קארט עם חברים. רוצים להזמין סשן של 20 דקות ל-4 אנשים ולשאול על הנחות קבוצתיות.",
      "המסך של הטלפון שלי סדוק ואני בחנות תיקונים. רוצה לשאול כמה זמן זה לוקח, כמה זה עולה, ואם יש אחריות.",
      "אני בבית מרקחת וצריך משהו לכאב ראש חזק וגם תרופה לאלרגיה. רוצה לדעת מה הכי חזק ואם צריך מרשם.",
      "אני עושה צ'ק-אין ב-Airbnb והמפתח לא במקום שהמארח אמר. צריך להתקשר ולהבין איך להיכנס.",
      "אני במסעדת פירות ים על החוף ורוצה להזמין דג צלוי, אבל רוצה לדעת מה טרי היום ולבקש המלצות.",
      "אני בבית ספר לגלישה ורוצה להזמין שיעור למתחילים מחר בבוקר. צריך לשאול על השכרת חליפה ומתי להגיע.",
      "לרכב השכור שלי יש פנצ'ר בכביש המהיר. צריך להתקשר לעזרה בדרכים ולהסביר איפה אני ומה קרה.",
    ],
  },
  pwa: {
    installPrompt: "התקן את SaySay לחוויה טובה יותר",
    install: "התקן",
  },
  settings: {
    title: "הגדרות",
    theme: "מראה",
    themeLight: "בהיר",
    themeDark: "כהה",
    themeSystem: "אוטומטי",
    resetAll: "מחק את כל הנתונים",
    resetConfirm: "פעולה זו תמחק את כל ההגדרות והנושאים. בטוח?",
    close: "סגור",
    speechRecognition: "זיהוי דיבור",
    sttAzure: "Azure",
    sttAzureDesc: "מבוסס ענן, איכות גבוהה",
    sttBrowser: "דפדפן",
    sttBrowserDesc: "מובנה, עובד אופליין",
    speechSpeed: "מהירות דיבור",
    analytics: "אנליטיקה",
    shareUsageData: "שתף נתוני שימוש",
    analyticsDescription: "עזור לשפר את SaySay על ידי שיתוף נתוני שימוש אנונימיים",
    knownLanguages: "שפות ידועות",
    knownLanguagesDescription: "בחר שפות שאתה כבר יודע. זה עוזר לנו לספק טיפים טובים יותר להגייה באמצעות צלילים משפות אלה.",
    noneSelected: "לא נבחר",
    learning: "למידה",
    showPronunciationHints: "הצג רמזי הגייה",
    showPronunciationHintsDescription: "הצג כיצד לומר ביטויים באמצעות צלילים שאתה מכיר",
  },
  learning: {
    back: "חזרה",
    dialogues: "דיאלוגים",
    vocabulary: "אוצר מילים",
    grammar: "דקדוק",
    practice: "תרגול",
    cards: "Cards",
    listen: "האזן",
    showTranslation: "הצג תרגום",
    hideTranslation: "הסתר תרגום",
    pronunciation: "הגייה",
    sayItLike: "תגיד את זה כמו",
    allForms: "כל הצורות",
    example: "דוגמה",
    inContext: "בהקשר",
    literalMeaning: "משמעות מילולית",
    wordBreakdown: "פירוק המילה",
    fromDialog: "מהדיאלוג",
    searchVocabulary: "חפש מילים...",
    foundResults: "נמצאו {count} תוצאות עבור \"{query}\"",
    progress: "התקדמות",
    mastered: "{count} / {total} נשלטו",
    words: "מילים",
    expressions: "ביטויים",
    topical: "כדאי לדעת",
    wordsDescription: "אוצר מילים עם כל הצורות",
    expressionsDescription: "ביטויים ומטבעות לשון",
    topicalDescription: "אוצר מילים קשור",
    noWordsMatch: "לא נמצאו מילים תואמות",
    noExpressionsMatch: "לא נמצאו ביטויים תואמים",
    noTopicalMatch: "לא נמצא אוצר מילים קשור",
    forms: "צורות",
    buildingBlocks: "אבני בניין",
    showBuildingBlocks: "הצג אבני בניין",
    buildingBlocksDescription: "מילים נפוצות לבניית הדיאלוג (תוויות, מילות יחס וכו')",
    hideByDefault: "אני כבר מכיר — הסתר כברירת מחדל",
    longPressHint: "לחץ לחיצה ארוכה על מילה לחיפוש",
    hideAll: "הסתר הכל",
    showAll: "הצג הכל",
    tapToReveal: "הקש לחשיפת התרגום",
    practiceMode: "מצב תרגול",
    practiceDescription: "נווט בתרחישי שיחה אמיתיים. בחר תשובות וראה איך השיחה מתפתחת.",
    howItWorks: "איך זה עובד:",
    practiceStep1: "קרא את הנחיית השיחה",
    practiceStep2: "בחר את התשובה הטובה ביותר מהאפשרויות",
    practiceStep3: "המשך עד סוף השיחה",
    startPractice: "התחל תרגול",
    greatJob: "עבודה מצוינת!",
    keepPracticing: "המשך להתאמן!",
    correctResponses: "{correct} מתוך {total} תשובות נכונות",
    yourConversation: "השיחה שלך:",
    tryAgain: "נסה שוב",
    newPath: "נתיב חדש",
    turn: "תור {count}",
    score: "ניקוד: {correct}/{total}",
    chooseResponse: "בחר את תשובתך:",
    continueConversation: "המשך שיחה",
    seeResults: "ראה תוצאות",
    conversationStarts: "השיחה מתחילה",
    you: "אתה",
    them: "הם",
    end: "סיום",
    alsoSay: "אפשר גם להגיד",
    playing: "מנגן...",
    askAI: "עזרה",
    response: "תגובה",
    responses: "תגובות",
    or: "או",
    systemResponse: "מערכת/תגובה",
    yourOptions: "האפשרויות שלך",
    tapToExpand: "הקש להרחבה. לחץ לחיצה ארוכה על מילה לתרגום.",
    startAgain: "התחל מחדש",
    pickYourAnswer: "בחרו תשובה",
    pickYourAnswerHint: "(הקישו על אחת מהתשובות למטה)",
    tapToAnswer: "לחצו על התשובה למטה כדי להמשיך",
    pickTheirAnswer: "בחרו את תשובתם",
    tapToStart: "לחצו להתחיל",
    transcribing: "מתמלל...",
    useVoice: "השתמשו בקול",
    saySomethingElse: "אני רוצה להגיד משהו אחר",
    said: "אמר/ה",
    whatToSay: "מה אתה רוצה להגיד?",
    typeYourResponse: "הקלד את התשובה שלך...",
    customPhraseHint: "הקלד בשפה שלך ונתרגם באופן טבעי",
    fixThisResponse: "תקן תגובה זו",
    whatsWrong: "מה צריך להיות במקום?",
    fixResponseHint: "תאר את התיקון או ספק את המשפט הנכון",
    // Practice tab modes
    practiceTab: "תרגול",
    practiceWords: "מילים",
    practiceSpelling: "איות",
    practiceKaraoke: "קריוקי",
    practiceWordsDesc: "חידון עם תמונות",
    practiceSpellingDesc: "אמור מילים בקול",
    practiceKaraokeDesc: "אמור משפטים מילה אחר מילה",
    introduceWord: "מילה חדשה",
    composeWord: "הרכב מילה",
    describeImage: "תאר מה בתמונה",
    pickWordForSentence: "בחר מילה למשפט",
    iNeedAHint: "אני צריך רמז",
    iDontKnow: "אני לא מכיר את המילה הזו",
    tapHoldSayWord: "לחץ, החזק ואמור את המילה",
    tapHoldSayPhrase: "לחץ, החזק ואמור את המשפט",
    weHeard: "שמענו:",
    skipWord: "דלג",
    nextWord: "הבא",
    practiceComplete: "התרגול הושלם!",
    wordsCorrectCount: "{correct} מתוך {total} נכונות",
    practiceAgain: "תרגל שוב",
    done: "סיום",
    correct: "נכון!",
    chooseModule: "בחר מודול",
    moduleHint: "תרגול בקבוצות קטנות לשינון טוב יותר",
    wordsCount: "מילים",
    markKnown: "אני יודע",
    markTough: "קשה",
    markLiked: "אהבתי",
    knownWordsHidden: "{count} מילים מוכרות מוסתרות",
    noWordsToReview: "עדיין אין מילים לחזרה",
    noWordsToReviewDesc: "צרו כמה דיאלוגים כדי לבנות את אוצר המילים שלכם",
    generatingMore: "...מייצר",
  },
  common: {
    loading: "טוען...",
    error: "אירעה שגיאה",
    retry: "נסה שוב",
    cancel: "ביטול",
    save: "שמור",
    delete: "מחק",
    edit: "ערוך",
    close: "סגור",
    send: "שלח",
    copy: "העתק",
  },
  games: {
    score: "ניקוד",
    best: "הכי טוב",
    hi: "שיא",
    tapOn: "הקש על",
    getReady: "התכונן...",
    tapToStart: "הקש כדי להתחיל!",
    tapToJump: "הקש כדי לקפוץ!",
    gameOver: "המשחק נגמר",
    tapToRestart: "הקש כדי להתחיל מחדש",
    speedSlow: "איטי",
    speedSteady: "יציב",
    speedNormal: "רגיל",
    speedQuick: "מהיר",
    speedFast: "זריז",
    speedBlazing: "להוט",
    speedOnFire: "בוער!",
    speedRocket: "רקטה",
    speedLegendary: "אגדי",
    cheers: ["יפה!", "מעולה!", "מדהים!", "וואו!", "נהדר!", "קלעת!", "כן!", "בום!", "מושלם!", "אדיר!"],
  },
  errors: {
    microphoneAccessDenied: "גישה למיקרופון נדחתה",
    recognitionError: "שגיאת זיהוי דיבור",
    recordingFailed: "הפעלת ההקלטה נכשלה",
    noSpeechDetected: "לא זוהה דיבור. אנא נסה שוב.",
    llmConfigRequired: "הגדר את מפתח ה-API של LLM בהגדרות כדי להשתמש בתכונה זו.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "ברוכים הבאים ל-SaySay",
    welcomeSubtitle: "תהיה מוכן לכל שיחה",
    getStarted: "בואו נתחיל",
    // Feature slides
    featureTopicsPain: "ספרי לימוד לא מתאימים לחיים שלך",
    featureTopicsSolution: "תרגל את מה שאתה באמת צריך",
    featureTopicsDesc: "תאר כל מצב — להזמין קפה, לבקר אצל רופא, לפטפט עם שכנים — ותקבל שיחה מותאמת אישית.",
    featureVoicePain: "איך בכלל מבטאים את זה?",
    featureVoiceSolution: "שמע כל מילה",
    featureVoiceDesc: "הקש על כל משפט כדי לשמוע הגייה של דובר שפת אם. השתמש בקול שלך לתשובות ללא ידיים.",
    featureAiPain: "גישה אחידה לא עובדת",
    featureAiSolution: "AI בונה את אוצר המילים שלך",
    featureAiDesc: "כל שיחה כוללת מילים וביטויים שתשתמש בהם באמת. למד אוצר מילים שמתאים לחיים שלך.",
    continue: "המשך",
    inputNativeLangTitle: "באיזו שפה אתם מדברים?",
    inputNativeLangSubtitle: "נלמד אתכם בשפה הזו",
    inputTargetLangTitle: "מה תרצו ללמוד?",
    inputTargetLangSubtitle: "בחרו את השפה שתרצו לתרגל",
    // Input: Name
    inputNameTitle: "איך לקרוא לך?",
    inputNameSubtitle: "אפשר לשנות אחר כך",
    inputNamePlaceholder: "השם שלך",
    // Input: Languages
    inputLangTitle: "אתה מדבר שפות אחרות?",
    inputLangSubtitle: "נשתמש בדמיון כדי לעזור לך ללמוד יותר מהר",
    inputLangSelect: "בחר שפות שאתה מכיר מעבר לעברית",
    inputLangFindMore: "מצא עוד",
    inputLangSearch: "חפש שפה...",
    skipStep: "דלג",
    // Input: Style
    inputStyleTitle: "איך אתה רוצה להישמע?",
    inputStyleSubtitle: "אפשר לשנות אחר כך",
    speakingStyleBeginner: "מתחיל",
    speakingStyleBeginnerDesc: "מילים פשוטות, משפטים בסיסיים — כמו מי שלומד",
    speakingStyleLocal: "כמו מקומי",
    speakingStyleLocalDesc: "ביטויים טבעיים, סלנג, ניבים — להשתלב",
    speakingStyleMix: "לערבב סגנונות",
    speakingStyleMixDesc: "בחר סגנון אחד או יותר",
    styleYourOption: "האפשרות שלך...",
    styleTypePlaceholder: "כתוב את הסגנון שלך...",
    // Input: Gender
    inputGenderTitle: "מי אתה בשיחות?",
    inputGenderSubtitle: "אופציונלי — אפשר לשנות אחר כך",
    genderMale: "גבר",
    genderFemale: "אישה",
    genderOther: "אחר / לא-בינארי",
    genderPreferNot: "מעדיף לא לומר",
    // Navigation
    back: "חזרה",
    skipAll: "דלג על הכל",
    // Ready screen
    startLearning: "התחל ללמוד",
    youreAllSet: "הכל מוכן!",
    youreAllSetWithName: "הכל מוכן, {name}!",
    startFirstConversation: "התחל את השיחה הראשונה שלך וראה כמה טבעי יכול להיות לימוד שפה.",
    analyticsOptIn: "עזור לשפר את SaySay על ידי שיתוף נתוני שימוש אנונימיים (לא נאסף מידע אישי)",
    // First conversation transition screen
    firstConversationTitle: "!מעולה! בואו נתרגל את השיחה הראשונה שלך",
    firstConversationSubtitle: ".בחרו נושא ונייצר דיאלוג אמיתי לתרגול",
    firstConversationButton: "!יאללה",
    // Preview UI elements
    previewTopicTitle: "על מה היית רוצה לדבר?",
    previewTopicSubtitle: "הקלד תרחיש או נושא",
    previewExample: "דוגמה — לחץ לשימוש",
    previewExampleText: "להזמין קפה ולשאול על המלצות מקומיות",
    previewTypeOwn: "או הקלד משלך...",
    previewVoiceText: "הייתי רוצה קפה...",
    previewDialogTranslation: "בוקר טוב! מה תרצה?",
  },
  knownLanguages: {
    spanish: "ספרדית",
    french: "צרפתית",
    german: "גרמנית",
    italian: "איטלקית",
    portuguese: "פורטוגזית",
    russian: "רוסית",
    chinese: "סינית",
    japanese: "יפנית",
    korean: "קוריאנית",
    arabic: "ערבית",
    hindi: "הינדית",
    dutch: "הולנדית",
    polish: "פולנית",
    swedish: "שוודית",
    turkish: "טורקית",
    greek: "יוונית",
    czech: "צ׳כית",
    romanian: "רומנית",
    hungarian: "הונגרית",
    finnish: "פינית",
    norwegian: "נורווגית",
    danish: "דנית",
    ukrainian: "אוקראינית",
    thai: "תאילנדית",
    vietnamese: "וייטנאמית",
    indonesian: "אינדונזית",
    malay: "מלאית",
    tagalog: "טגלוג",
    hebrew: "עברית",
    persian: "פרסית",
    bengali: "בנגלית",
    punjabi: "פנג׳אבית",
    tamil: "טמילית",
    urdu: "אורדו",
    swahili: "סווהילית",
    afrikaans: "אפריקאנס",
    catalan: "קטלאנית",
    croatian: "קרואטית",
    serbian: "סרבית",
    slovak: "סלובקית",
    slovenian: "סלובנית",
    bulgarian: "בולגרית",
    lithuanian: "ליטאית",
    latvian: "לטבית",
    estonian: "אסטונית",
    icelandic: "איסלנדית",
    welsh: "וולשית",
    irish: "אירית",
    scottishGaelic: "גאלית סקוטית",
    basque: "בסקית",
    maltese: "מלטזית",
    albanian: "אלבנית",
    macedonian: "מקדונית",
    georgian: "גאורגית",
    armenian: "ארמנית",
    kazakh: "קזחית",
    uzbek: "אוזבקית",
    mongolian: "מונגולית",
    nepali: "נפאלית",
    sinhala: "סינהלה",
    burmese: "בורמזית",
    khmer: "חמרית",
    lao: "לאית",
  },
  coachMarks: {
    startNodeTitle: "התחל את השיחה",
    startNodeBody: "לחץ על בועת ההודעה הזו כדי להתחיל את הדיאלוג.",
    speakerTitle: "האזן להגייה",
    speakerBody: "לחץ על סמל הרמקול כדי לשמוע את המשפט בקול.",
    answerTitle: "בחר את תשובתך",
    answerBody: "לחץ על אחת האפשרויות כדי לבחור מה אתה רוצה להגיד.",
    tabsTitle: "גלה עוד",
    tabsBody: "עבור בין הדיאלוג, תרגילי תרגול ואוצר מילים.",
    skip: "דלג",
    next: "הבא",
    done: "סיום",
  },
  passwordGate: {
    enterPassword: "הזן סיסמה להמשך",
    placeholder: "סיסמה",
  },
  dialogueFeedback: {
    title: "מה דעתך על הדיאלוג הזה?",
    bad: "רע",
    meh: "ככה ככה",
    good: "טוב",
    thanks: "תודה על המשוב!",
    tellUsWhy: "תודה! אפשר לדעת למה?",
    commentPlaceholder: "מה אפשר לשפר?",
    skip: "דלג",
    send: "שלח",
  },
  dialog: {
    listening: "מאזין לך...",
    stopRecording: "הפסק הקלטה",
    practice: "תרגול",
    vocabulary: "אוצר מילים",
    loadingVocabulary: "טוען אוצר מילים...",
  },
  languageNames: {
    "en-US": "אנגלית (ארה״ב)",
    "en-GB": "אנגלית (בריטניה)",
    "es-ES": "ספרדית (ספרד)",
    "es-MX": "ספרדית (מקסיקו)",
    "pt-PT": "פורטוגזית (פורטוגל)",
    "pt-BR": "פורטוגזית (ברזיל)",
    "fr-FR": "צרפתית",
    "de-DE": "גרמנית",
    "de-AT": "גרמנית (אוסטריה)",
    "de-CH": "גרמנית (שווייץ)",
    "it-IT": "איטלקית",
    "ja-JP": "יפנית",
    "zh-CN": "סינית",
    "ko-KR": "קוריאנית",
    "he-IL": "עברית",
    "ru-RU": "רוסית",
    "pl-PL": "פולנית",
    "uk-UA": "אוקראינית",
    "fi-FI": "פינית",
    "et-EE": "אסטונית",
    "lv-LV": "לטבית",
    "el-GR": "יוונית",
  },
};

// Polish
const pl: Translations = {
  nav: {
    dialogues: "Dialogi",
    grammar: "Gramatyka",
    practice: "Ćwiczenia",
    alphabet: "Alfabet",
  },
  languageSelector: {
    title: "Języki",
    iSpeak: "Mówię po",
    iWantToLearn: "Chcę się uczyć",
    done: "Gotowe",
    back: "Wstecz",
    moreComingSoon: "Więcej języków wkrótce!",
  },
  topics: {
    title: "Tematy Dialogów",
    subtitle: "Wybierz rozmowę do ćwiczenia",
    tagline: "Naucz się mówić to, czego potrzebujesz",
    heroTitle: "Bądź gotowy na każdą rozmowę",
    heroSubtitle: "Przestań kiwać głową — zacznij mówić",
    createCustom: "Nowa rozmowa",
    chooseTopic: "Wybierz Temat",
    available: "dostępnych",
    words: "słów",
    expressions: "wyrażeń",
    difficulty: {
      beginner: "Początkujący",
      intermediate: "Średniozaawansowany",
      advanced: "Zaawansowany",
    },
    dialogNodes: "wymian",
    regenerateWarning: "Utworzono dla innego języka — dotknij, aby odtworzyć",
    deleteTitle: "Usuń Temat",
    deleteDescription: "Czy na pewno chcesz usunąć ten temat? Tej akcji nie można cofnąć.",
    regenerateTitle: "Wygeneruj ponownie",
    regenerateDescription: "Zostanie utworzony nowy dialog dla tego tematu. Obecny dialog zostanie zastąpiony.",
    regenerate: "Wygeneruj ponownie",
    edit: "Edytuj",
    recent: "Ostatnie",
    categories: "Kategorie",
    byCategory: "Dialogi według kategorii",
    collapse: "Zwiń",
  },
  grammar: {
    title: "Zasady Gramatyki",
    subtitle: "Jak działa {language}",
    noRulesTitle: "Brak Zasad",
    noRulesDescription: "Utwórz temat w {language}, aby zobaczyć zasady gramatyki.",
    rulesCount: "{count} zasad gramatyki",
    rulesSingular: "{count} zasada gramatyki",
    tapToExpand: "Dotknij, aby rozwinąć",
    examples: "Przykłady z Dialogu",
    howToThink: "Jak o Tym Myśleć",
    commonMistake: "Częsty Błąd",
  },
  alphabet: {
    title: "Alfabet i Dźwięki",
    subtitle: "Wymowa {language}",
    comingSoonTitle: "Wkrótce",
    comingSoonDescription: "Przewodnik po alfabecie {language} nie jest jeszcze dostępny.",
    examples: "Przykłady",
    all: "Wszystko",
  },
  generator: {
    title: "O czym chcesz porozmawiać?",
    placeholder: "Opisz sytuację...\n\nPrzykłady:\n• Zamówienie kawy w kawiarni\n• Pytanie o drogę\n• Umówienie wizyty u lekarza",
    proficiency: "Jak chcesz brzmieć?",
    generate: "Zaczynamy",
    generating: "Przygotowuję wszystko...",
    loadTopic: "Załaduj",
    back: "Wstecz",
    askAI: "Opisz swoją sytuację",
    responses: "odpowiedzi",
    modelTierFast: "Szybko",
    modelTierFastDesc: "Szybko i tanio",
    modelTierBalanced: "Zbalansowany",
    modelTierBalancedDesc: "Najlepsza wartość",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Najwyższa jakość",
    loadingModels: "Ładowanie modeli...",
    llmConfigWarning: "Skonfiguruj dostawcę LLM w ustawieniach, aby włączyć generowanie AI",
    topicTitle: "O czym chciałbyś porozmawiać?",
    topicHintMic: "Dotknij mikrofonu i powiedz temat",
    topicHintKeyboard: "Wpisz scenariusz lub temat",
    exampleTapToUse: "Przykład — dotknij, aby użyć",
    enterText: "Wpisz tekst",
    tapToStop: "Dotknij, aby zatrzymać",
    processing: "Przetwarzanie...",
    tapAndTalk: "Dotknij i mów",
    recording: "Nagrywanie...",
    useVoice: "Użyj głosu",
    attach: "Załącz",
    continue: "Kontynuuj",
    apiKeyRequired: "Wymagany klucz API",
    apiKeyRequiredDesc: "Skonfiguruj dostawcę LLM w ustawieniach, aby włączyć generowanie AI",
    difficultyTitle: "Jak chcesz brzmieć?",
    difficultySubtitle: "Wybierz swój styl mówienia",
    styleBeginner: "Początkujący",
    styleBeginnerDesc: "Proste słowa — jak początkujący",
    styleLocal: "Jak miejscowy",
    styleLocalDesc: "Naturalny język, slang — jak native",
    styleMix: "Mieszanka",
    styleMixDesc: "Wybierz jeden lub więcej",
    styleCustomPlaceholder: "Lub wpisz własny...",
    richnessTitle: "Ile treści?",
    richnessSubtitle: "Wybierz ilość słownictwa i dialogu",
    richnessQuick: "Szybko",
    richnessQuickDesc: "Krótki dialog, podstawowe słownictwo",
    richnessStandard: "Standardowy",
    richnessStandardDesc: "Średni dialog, dobre pokrycie słownictwa",
    richnessComprehensive: "Kompleksowy",
    richnessComprehensiveDesc: "Długi dialog, bogate słownictwo",
    modelTitle: "Wybierz poziom jakości",
    modelSubtitle: "Wyższa jakość = lepsza treść, wyższy koszt",
    cachedAvailable: "Odpowiedź z pamięci podręcznej dostępna — natychmiastowe ładowanie!",
    loadCached: "Załaduj z cache",
    previewPrompt: "Podgląd promptu",
    fullPrompt: "Pełny prompt",
    copied: "Skopiowano!",
    systemPrompt: "Prompt systemowy",
    userPrompt: "Prompt użytkownika",
    gettingReady: "Przygotowuję wszystko...",
    connectingToAI: "Łączenie z AI...",
    done: "Gotowe!",
    letsGo: "Zaczynamy",
    goBack: "Wróć",
    playWhileWaiting: "Zagraj w {game1} lub {game2} podczas czekania!",
    play: "Zagraj w",
    or: "lub",
    whileYouWait: "podczas czekania",
    exitGame: "Wyjdź z gry",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Zamówiłem opony online w sklepie z oponami i już dotarły. Muszę zadzwonić i umówić się na wymianę w tym tygodniu.",
      "Jestem w kawiarni speciality i chcę zamówić flat white z mlekiem owsianym i ciastko. Chcę też zapytać, czy mają ziarna single origin.",
      "Jestem na dworcu i muszę dojechać do celu. Chcę wiedzieć, który peron, ile kosztuje bilet powrotny i czy mogę zapłacić kartą.",
      "Jestem na torze gokartowym ze znajomymi. Chcemy zarezerwować 20-minutową sesję dla 4 osób i zapytać o zniżki grupowe.",
      "Ekran mojego telefonu jest pęknięty i jestem w serwisie. Chcę zapytać, ile to potrwa, ile kosztuje i czy mają gwarancję.",
      "Jestem w aptece i potrzebuję czegoś na silny ból głowy i lekarstwa na alergię. Chcę wiedzieć, które jest najsilniejsze i czy potrzebuję recepty.",
      "Melduję się w Airbnb, ale klucza nie ma tam, gdzie powiedział gospodarz. Muszę zadzwonić i dowiedzieć się, jak wejść.",
      "Jestem w restauracji z owocami morza nad plażą i chcę zamówić grillowaną rybę, ale chcę wiedzieć, co jest świeże i poprosić o rekomendacje.",
      "Jestem w szkole surfingu i chcę zarezerwować lekcję dla początkujących na jutro rano. Muszę zapytać o wypożyczenie pianki i o której przyjść.",
      "Mój wynajęty samochód ma przebitą oponę na autostradzie. Muszę zadzwonić po pomoc drogową i wyjaśnić, gdzie jestem i co się stało.",
    ],
  },
  pwa: {
    installPrompt: "Zainstaluj SaySay dla lepszego doświadczenia",
    install: "Zainstaluj",
  },
  settings: {
    title: "Ustawienia",
    theme: "Wygląd",
    themeLight: "Jasny",
    themeDark: "Ciemny",
    themeSystem: "Auto",
    resetAll: "Usuń Wszystkie Dane",
    resetConfirm: "To usunie wszystkie ustawienia i tematy. Czy na pewno?",
    close: "Zamknij",
    speechRecognition: "Rozpoznawanie mowy",
    sttAzure: "Azure",
    sttAzureDesc: "W chmurze, wysoka jakość",
    sttBrowser: "Przeglądarka",
    sttBrowserDesc: "Wbudowane, działa offline",
    speechSpeed: "Szybkość mowy",
    analytics: "Analityka",
    shareUsageData: "Udostępnij dane użytkowania",
    analyticsDescription: "Pomóż ulepszyć SaySay, udostępniając anonimowe dane użytkowania",
    knownLanguages: "Znane języki",
    knownLanguagesDescription: "Wybierz języki, które już znasz. Pomaga nam to dostarczać lepsze wskazówki dotyczące wymowy, używając dźwięków z tych języków.",
    noneSelected: "Nie wybrano",
    learning: "Nauka",
    showPronunciationHints: "Pokaż wskazówki wymowy",
    showPronunciationHintsDescription: "Wyświetla jak wymawiać frazy, używając znanych ci dźwięków",
  },
  learning: {
    back: "Wstecz",
    dialogues: "Dialogi",
    vocabulary: "Słownictwo",
    grammar: "Gramatyka",
    practice: "Ćwiczenia",
    cards: "Cards",
    listen: "Słuchaj",
    showTranslation: "Pokaż tłumaczenie",
    hideTranslation: "Ukryj tłumaczenie",
    pronunciation: "Wymowa",
    sayItLike: "Powiedz to jak",
    allForms: "Wszystkie Formy",
    example: "Przykład",
    inContext: "W Kontekście",
    literalMeaning: "Znaczenie Dosłowne",
    wordBreakdown: "Rozkład Słowa",
    fromDialog: "Z Dialogu",
    searchVocabulary: "Szukaj słownictwa...",
    foundResults: "Znaleziono {count} wyników dla \"{query}\"",
    progress: "Postęp",
    mastered: "{count} / {total} opanowane",
    words: "Słowa",
    expressions: "Wyrażenia",
    topical: "Warto Wiedzieć",
    wordsDescription: "Podstawowe słownictwo ze wszystkimi formami",
    expressionsDescription: "Zwroty i idiomy",
    topicalDescription: "Powiązane słownictwo",
    noWordsMatch: "Brak pasujących słów",
    noExpressionsMatch: "Brak pasujących wyrażeń",
    noTopicalMatch: "Brak pasującego powiązanego słownictwa",
    forms: "Formy",
    buildingBlocks: "Podstawowe elementy",
    showBuildingBlocks: "Pokaż podstawowe elementy",
    buildingBlocksDescription: "Popularne słowa potrzebne do tego dialogu (rodzajniki, przyimki itp.)",
    hideByDefault: "Już znam — ukryj domyślnie",
    longPressHint: "Przytrzymaj słowo, aby je wyszukać",
    hideAll: "Ukryj wszystko",
    showAll: "Pokaż wszystko",
    tapToReveal: "Dotknij, aby pokazać tłumaczenie",
    practiceMode: "Tryb Ćwiczeń",
    practiceDescription: "Przechodź przez rzeczywiste scenariusze rozmów. Wybieraj odpowiedzi i zobacz, jak rozwija się dialog.",
    howItWorks: "Jak to działa:",
    practiceStep1: "Przeczytaj podpowiedź rozmowy",
    practiceStep2: "Wybierz najlepszą odpowiedź z opcji",
    practiceStep3: "Kontynuuj do końca rozmowy",
    startPractice: "Rozpocznij Ćwiczenie",
    greatJob: "Świetna robota!",
    keepPracticing: "Ćwicz dalej!",
    correctResponses: "{correct} z {total} poprawnych odpowiedzi",
    yourConversation: "Twoja rozmowa:",
    tryAgain: "Spróbuj Ponownie",
    newPath: "Nowa Ścieżka",
    turn: "Tura {count}",
    score: "Wynik: {correct}/{total}",
    chooseResponse: "Wybierz odpowiedź:",
    continueConversation: "Kontynuuj Rozmowę",
    seeResults: "Zobacz Wyniki",
    conversationStarts: "Rozmowa zaczyna się",
    you: "Ty",
    them: "Oni",
    end: "Koniec",
    alsoSay: "Powiedz też",
    playing: "Odtwarzanie...",
    askAI: "Pomoc",
    response: "odpowiedź",
    responses: "odpowiedzi",
    or: "lub",
    systemResponse: "System/Odpowiedź",
    yourOptions: "Twoje opcje",
    tapToExpand: "Dotknij, aby rozwinąć. Przytrzymaj słowo, aby przetłumaczyć.",
    startAgain: "Zacznij od nowa",
    pickYourAnswer: "Wybierz odpowiedź",
    pickYourAnswerHint: "(dotknij jednej z odpowiedzi poniżej)",
    tapToAnswer: "Dotknij odpowiedzi poniżej, aby kontynuować",
    pickTheirAnswer: "Wybierz ich odpowiedź",
    tapToStart: "Dotknij, aby zacząć",
    transcribing: "Transkrybuję...",
    useVoice: "Użyj głosu",
    saySomethingElse: "Chcę powiedzieć coś innego",
    said: "powiedział(a)",
    whatToSay: "Co chcesz powiedzieć?",
    typeYourResponse: "Wpisz swoją odpowiedź...",
    customPhraseHint: "Wpisz w swoim języku, a przetłumaczymy naturalnie",
    fixThisResponse: "Popraw tę odpowiedź",
    whatsWrong: "Co powinno być zamiast tego?",
    fixResponseHint: "Opisz poprawkę lub podaj prawidłową frazę",
    // Practice tab modes
    practiceTab: "Ćwiczenia",
    practiceWords: "Słowa",
    practiceSpelling: "Pisownia",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Quiz z obrazkami",
    practiceSpellingDesc: "Powiedz słowa na głos",
    practiceKaraokeDesc: "Wypowiedz frazy słowo po słowie",
    introduceWord: "Nowe słowo",
    composeWord: "Ułóż słowo",
    describeImage: "Opisz, co jest na obrazku",
    pickWordForSentence: "Wybierz słowo do zdania",
    iNeedAHint: "Potrzebuję podpowiedzi",
    iDontKnow: "Nie znam tego słowa",
    tapHoldSayWord: "Dotknij, przytrzymaj i powiedz słowo",
    tapHoldSayPhrase: "Dotknij, przytrzymaj i powiedz frazę",
    weHeard: "Usłyszeliśmy:",
    skipWord: "Pomiń",
    nextWord: "Dalej",
    practiceComplete: "Ćwiczenie ukończone!",
    wordsCorrectCount: "{correct} z {total} poprawnych",
    practiceAgain: "Ćwicz ponownie",
    done: "Gotowe",
    correct: "Poprawnie!",
    chooseModule: "Wybierz moduł",
    moduleHint: "Ćwicz w małych grupach, by lepiej zapamiętać",
    wordsCount: "słów",
    markKnown: "Już znam",
    markTough: "Trudne",
    markLiked: "Lubię",
    knownWordsHidden: "{count} znanych słów ukrytych",
    noWordsToReview: "Brak słów do powtórki",
    noWordsToReviewDesc: "Wygeneruj dialogi, aby poszerzyć słownictwo",
    generatingMore: "Generowanie...",
  },
  common: {
    loading: "Ładowanie...",
    error: "Wystąpił błąd",
    retry: "Ponów",
    cancel: "Anuluj",
    save: "Zapisz",
    delete: "Usuń",
    edit: "Edytuj",
    close: "Zamknij",
    send: "Wyślij",
    copy: "Kopiuj",
  },
  games: {
    score: "Wynik",
    best: "Najlepszy",
    hi: "REK",
    tapOn: "Dotknij",
    getReady: "Przygotuj się...",
    tapToStart: "Dotknij, aby zacząć!",
    tapToJump: "Dotknij, aby skoczyć!",
    gameOver: "KONIEC GRY",
    tapToRestart: "Dotknij, aby zacząć od nowa",
    speedSlow: "Wolno",
    speedSteady: "Miarowo",
    speedNormal: "Normalnie",
    speedQuick: "Szybko",
    speedFast: "Prędko",
    speedBlazing: "Błyskawicznie",
    speedOnFire: "W ogniu!",
    speedRocket: "Rakieta",
    speedLegendary: "Legendarnie",
    cheers: ["Nieźle!", "Świetnie!", "Niesamowite!", "Wow!", "Wspaniale!", "Masz to!", "Tak!", "Bum!", "Idealnie!", "Ekstra!"],
  },
  errors: {
    microphoneAccessDenied: "Dostęp do mikrofonu odrzucony",
    recognitionError: "Błąd rozpoznawania mowy",
    recordingFailed: "Nie udało się rozpocząć nagrywania",
    noSpeechDetected: "Nie wykryto mowy. Spróbuj ponownie.",
    llmConfigRequired: "Skonfiguruj klucz API LLM w ustawieniach, aby korzystać z tej funkcji.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Witaj w SaySay",
    welcomeSubtitle: "Bądź gotowy na każdą rozmowę",
    getStarted: "Zaczynamy",
    // Feature slides
    featureTopicsPain: "Podręczniki nie pasują do Twojego życia",
    featureTopicsSolution: "Ćwicz to, czego naprawdę potrzebujesz",
    featureTopicsDesc: "Opisz dowolną sytuację — zamówienie kawy, wizyta u lekarza, rozmowa z sąsiadami — i otrzymaj dialog dopasowany do Ciebie.",
    featureVoicePain: "Jak to się właściwie wymawia?",
    featureVoiceSolution: "Posłuchaj każdego słowa",
    featureVoiceDesc: "Dotknij dowolnej frazy, aby usłyszeć wymowę native speakera. Odpowiadaj głosem bez użycia rąk.",
    featureAiPain: "Uniwersalne rozwiązania nie działają",
    featureAiSolution: "AI buduje Twoje słownictwo",
    featureAiDesc: "Każdy dialog zawiera słowa i wyrażenia, których naprawdę użyjesz. Ucz się słownictwa dopasowanego do Twojego życia.",
    continue: "Dalej",
    inputNativeLangTitle: "Jakim językiem mówisz?",
    inputNativeLangSubtitle: "Będziemy uczyć cię w tym języku",
    inputTargetLangTitle: "Czego chcesz się uczyć?",
    inputTargetLangSubtitle: "Wybierz język, który chcesz ćwiczyć",
    // Input: Name
    inputNameTitle: "Jak mamy Cię nazywać?",
    inputNameSubtitle: "Możesz to zmienić później",
    inputNamePlaceholder: "Twoje imię",
    // Input: Languages
    inputLangTitle: "Znasz inne języki?",
    inputLangSubtitle: "Wykorzystamy podobieństwa, żeby pomóc Ci szybciej się uczyć",
    inputLangSelect: "Wybierz języki, które znasz oprócz polskiego",
    inputLangFindMore: "Znajdź więcej",
    inputLangSearch: "Szukaj języka...",
    skipStep: "Pomiń",
    // Input: Style
    inputStyleTitle: "Jak chcesz brzmieć?",
    inputStyleSubtitle: "Możesz to zmienić później",
    speakingStyleBeginner: "Początkujący",
    speakingStyleBeginnerDesc: "Proste słowa, podstawowe frazy — jak ktoś, kto się uczy",
    speakingStyleLocal: "Jak miejscowy",
    speakingStyleLocalDesc: "Naturalne wyrażenia, slang, idiomy — wtopić się w tłum",
    speakingStyleMix: "Mix stylów",
    speakingStyleMixDesc: "Wybierz jeden lub więcej stylów",
    styleYourOption: "Twoja opcja...",
    styleTypePlaceholder: "Wpisz swój styl...",
    // Input: Gender
    inputGenderTitle: "Kim jesteś w dialogach?",
    inputGenderSubtitle: "Opcjonalne — możesz zmienić później",
    genderMale: "Mężczyzna",
    genderFemale: "Kobieta",
    genderOther: "Inna / Niebinarna",
    genderPreferNot: "Wolę nie mówić",
    // Navigation
    back: "Wstecz",
    skipAll: "Pomiń wszystko",
    // Ready screen
    startLearning: "Rozpocznij naukę",
    youreAllSet: "Wszystko gotowe!",
    youreAllSetWithName: "Wszystko gotowe, {name}!",
    startFirstConversation: "Zacznij pierwszą rozmowę i przekonaj się, jak naturalna może być nauka języka.",
    analyticsOptIn: "Pomóż ulepszyć SaySay, udostępniając anonimowe dane (nie zbieramy danych osobowych)",
    // First conversation transition screen
    firstConversationTitle: "Świetnie! Teraz przećwiczmy twoją pierwszą rozmowę.",
    firstConversationSubtitle: "Wybierz temat, a wygenerujemy prawdziwy dialog do ćwiczeń.",
    firstConversationButton: "Zaczynamy!",
    // Preview UI elements
    previewTopicTitle: "O czym chcesz porozmawiać?",
    previewTopicSubtitle: "Wpisz scenariusz lub temat",
    previewExample: "Przykład — dotknij, aby użyć",
    previewExampleText: "Zamówienie kawy i pytanie o lokalne rekomendacje",
    previewTypeOwn: "Lub wpisz własny...",
    previewVoiceText: "Poproszę kawę...",
    previewDialogTranslation: "Dzień dobry! Co podać?",
  },
  knownLanguages: {
    spanish: "Hiszpański",
    french: "Francuski",
    german: "Niemiecki",
    italian: "Włoski",
    portuguese: "Portugalski",
    russian: "Rosyjski",
    chinese: "Chiński",
    japanese: "Japoński",
    korean: "Koreański",
    arabic: "Arabski",
    hindi: "Hindi",
    dutch: "Niderlandzki",
    polish: "Polski",
    swedish: "Szwedzki",
    turkish: "Turecki",
    greek: "Grecki",
    czech: "Czeski",
    romanian: "Rumuński",
    hungarian: "Węgierski",
    finnish: "Fiński",
    norwegian: "Norweski",
    danish: "Duński",
    ukrainian: "Ukraiński",
    thai: "Tajski",
    vietnamese: "Wietnamski",
    indonesian: "Indonezyjski",
    malay: "Malajski",
    tagalog: "Tagalski",
    hebrew: "Hebrajski",
    persian: "Perski",
    bengali: "Bengalski",
    punjabi: "Pendżabski",
    tamil: "Tamilski",
    urdu: "Urdu",
    swahili: "Suahili",
    afrikaans: "Afrikaans",
    catalan: "Kataloński",
    croatian: "Chorwacki",
    serbian: "Serbski",
    slovak: "Słowacki",
    slovenian: "Słoweński",
    bulgarian: "Bułgarski",
    lithuanian: "Litewski",
    latvian: "Łotewski",
    estonian: "Estoński",
    icelandic: "Islandzki",
    welsh: "Walijski",
    irish: "Irlandzki",
    scottishGaelic: "Szkocki gaelicki",
    basque: "Baskijski",
    maltese: "Maltański",
    albanian: "Albański",
    macedonian: "Macedoński",
    georgian: "Gruziński",
    armenian: "Ormiański",
    kazakh: "Kazachski",
    uzbek: "Uzbecki",
    mongolian: "Mongolski",
    nepali: "Nepalski",
    sinhala: "Syngaleski",
    burmese: "Birmański",
    khmer: "Khmerski",
    lao: "Laotański",
  },
  coachMarks: {
    startNodeTitle: "Rozpocznij rozmowę",
    startNodeBody: "Dotknij tej dymki wiadomości, aby rozpocząć dialog.",
    speakerTitle: "Posłuchaj wymowy",
    speakerBody: "Dotknij ikony głośnika, aby usłyszeć frazę na głos.",
    answerTitle: "Wybierz odpowiedź",
    answerBody: "Dotknij jednej z opcji, aby wybrać, co chcesz powiedzieć.",
    tabsTitle: "Odkryj więcej",
    tabsBody: "Przełączaj między dialogiem, ćwiczeniami i słownictwem.",
    skip: "Pomiń",
    next: "Dalej",
    done: "Gotowe",
  },
  passwordGate: {
    enterPassword: "Wprowadź hasło, aby kontynuować",
    placeholder: "Hasło",
  },
  dialogueFeedback: {
    title: "Co myślisz o tym dialogu?",
    bad: "Źle",
    meh: "Średnio",
    good: "Dobrze",
    thanks: "Dziękujemy za opinię!",
    tellUsWhy: "Dziękujemy! Możesz powiedzieć dlaczego?",
    commentPlaceholder: "Co mogłoby być lepsze?",
    skip: "Pomiń",
    send: "Wyślij",
  },
  dialog: {
    listening: "Słucham Cię...",
    stopRecording: "Zatrzymaj nagrywanie",
    practice: "Ćwiczenie",
    vocabulary: "Słownictwo",
    loadingVocabulary: "Ładowanie słownictwa...",
  },
  languageNames: {
    "en-US": "Angielski (USA)",
    "en-GB": "Angielski (Wielka Brytania)",
    "es-ES": "Hiszpański (Hiszpania)",
    "es-MX": "Hiszpański (Meksyk)",
    "pt-PT": "Portugalski (Portugalia)",
    "pt-BR": "Portugalski (Brazylia)",
    "fr-FR": "Francuski",
    "de-DE": "Niemiecki",
    "de-AT": "Niemiecki (Austria)",
    "de-CH": "Niemiecki (Szwajcaria)",
    "it-IT": "Włoski",
    "ja-JP": "Japoński",
    "zh-CN": "Chiński",
    "ko-KR": "Koreański",
    "he-IL": "Hebrajski",
    "ru-RU": "Rosyjski",
    "pl-PL": "Polski",
    "uk-UA": "Ukraiński",
    "fi-FI": "Fiński",
    "et-EE": "Estoński",
    "lv-LV": "Łotewski",
    "el-GR": "Grecki",
  },
};

// Ukrainian
const uk: Translations = {
  nav: {
    dialogues: "Діалоги",
    grammar: "Граматика",
    practice: "Практика",
    alphabet: "Алфавіт",
  },
  languageSelector: {
    title: "Мови",
    iSpeak: "Я розмовляю",
    iWantToLearn: "Я хочу вивчати",
    done: "Готово",
    back: "Назад",
    moreComingSoon: "Більше мов незабаром!",
  },
  topics: {
    title: "Теми Діалогів",
    subtitle: "Вибери розмову для практики",
    tagline: "Навчись говорити те, що тобі потрібно",
    heroTitle: "Будь готовий до будь-якої розмови",
    heroSubtitle: "Перестань кивати — почни говорити",
    createCustom: "Опрацювати нову ситуацію",
    chooseTopic: "Вибери тему",
    available: "доступно",
    words: "слів",
    expressions: "виразів",
    difficulty: {
      beginner: "Початківець",
      intermediate: "Середній",
      advanced: "Просунутий",
    },
    dialogNodes: "реплік",
    regenerateWarning: "Створено для іншої мови — натисни, щоб перестворити",
    deleteTitle: "Видалити Тему",
    deleteDescription: "Точно видалити цю тему? Цю дію не можна скасувати.",
    regenerateTitle: "Створити заново",
    regenerateDescription: "Буде створено новий діалог для цієї теми. Поточний діалог буде замінено.",
    regenerate: "Створити заново",
    edit: "Змінити",
    recent: "Останні",
    categories: "Категорії",
    byCategory: "Діалоги за категоріями",
    collapse: "Згорнути",
  },
  grammar: {
    title: "Правила Граматики",
    subtitle: "Як працює {language}",
    noRulesTitle: "Поки Немає Правил",
    noRulesDescription: "Створи тему на {language}, щоб побачити правила тут.",
    rulesCount: "{count} правил граматики",
    rulesSingular: "{count} правило граматики",
    tapToExpand: "Натисни для розгортання",
    examples: "Приклади з Діалогу",
    howToThink: "Як Це Розуміти",
    commonMistake: "Часта Помилка",
  },
  alphabet: {
    title: "Алфавіт і Звуки",
    subtitle: "Вимова {language}",
    comingSoonTitle: "Незабаром",
    comingSoonDescription: "Посібник з алфавіту для {language} ще не доступний.",
    examples: "Приклади",
    all: "Усі",
  },
  generator: {
    title: "Про що хочеш поговорити?",
    placeholder: "Опиши ситуацію...\n\nПриклади:\n• Замовити каву в кав'ярні\n• Запитати дорогу\n• Записатися до лікаря",
    proficiency: "Як ти хочеш звучати?",
    generate: "Почнімо",
    generating: "Готуємо все...",
    loadTopic: "Завантажити",
    back: "Назад",
    askAI: "Опиши свою ситуацію",
    responses: "відповідей",
    modelTierFast: "Швидкий",
    modelTierFastDesc: "Швидко та доступно",
    modelTierBalanced: "Збалансований",
    modelTierBalancedDesc: "Найкраще співвідношення ціни та якості",
    modelTierPremium: "Преміум",
    modelTierPremiumDesc: "Найвища якість",
    loadingModels: "Завантаження моделей...",
    llmConfigWarning: "Налаштуй постачальника LLM у налаштуваннях, щоб увімкнути генерацію AI",
    topicTitle: "Про що б ти хотів поговорити?",
    topicHintMic: "Натисни на мікрофон і скажи тему",
    topicHintKeyboard: "Введи сценарій або тему",
    exampleTapToUse: "Приклад — натисни для використання",
    enterText: "Введи текст",
    tapToStop: "Натисни для зупинки",
    processing: "Обробка...",
    tapAndTalk: "Натисни і говори",
    recording: "Запис...",
    useVoice: "Використовувати голос",
    attach: "Прикріпити",
    continue: "Продовжити",
    apiKeyRequired: "Потрібен API-ключ",
    apiKeyRequiredDesc: "Налаштуй постачальника LLM у налаштуваннях, щоб увімкнути генерацію AI",
    difficultyTitle: "Як ти хочеш звучати?",
    difficultySubtitle: "Вибери свій стиль мовлення",
    styleBeginner: "Початківець",
    styleBeginnerDesc: "Прості слова і фрази — як початківець",
    styleLocal: "Як місцевий",
    styleLocalDesc: "Жива мова, сленг, ідіоми — як носій",
    styleMix: "Мікс",
    styleMixDesc: "Обери один або кілька стилів",
    styleCustomPlaceholder: "Або напиши свій...",
    richnessTitle: "Скільки контенту?",
    richnessSubtitle: "Вибери кількість словника та діалогу",
    richnessQuick: "Швидко",
    richnessQuickDesc: "Короткий діалог, базовий словник",
    richnessStandard: "Стандарт",
    richnessStandardDesc: "Середній діалог, хороше охоплення словника",
    richnessComprehensive: "Повний",
    richnessComprehensiveDesc: "Довгий діалог, обширний словник",
    modelTitle: "Вибери рівень якості",
    modelSubtitle: "Краща якість — дорожче",
    cachedAvailable: "Кешована відповідь доступна — миттєве завантаження!",
    loadCached: "Завантажити кеш",
    previewPrompt: "Попередній перегляд промпту",
    fullPrompt: "Повний промпт",
    copied: "Скопійовано!",
    systemPrompt: "Системний промпт",
    userPrompt: "Користувацький промпт",
    gettingReady: "Створюємо урок...",
    connectingToAI: "Підключаємось до AI...",
    done: "Готово!",
    letsGo: "Почнімо",
    goBack: "Назад",
    playWhileWaiting: "Грай у {game1} або {game2} поки чекаєш!",
    play: "Грай у",
    or: "або",
    whileYouWait: "поки чекаєш",
    exitGame: "Вийти з гри",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Я замовив шини онлайн у шиномонтажі, і вони приїхали. Мені потрібно зателефонувати і записатися на встановлення цього тижня.",
      "Я у кав'ярні і хочу замовити флет вайт на вівсяному молоці та випічку. Також хочу запитати, чи є у них зерна моносорту.",
      "Я на вокзалі і мені потрібно дістатися до місця призначення. Хочу дізнатися, яка платформа, скільки коштує квиток туди-назад і чи можна оплатити карткою.",
      "Я на картингу з друзями. Ми хочемо забронювати 20-хвилинну сесію на 4 людини і дізнатися про групові знижки.",
      "Екран мого телефону розбитий, і я в майстерні з ремонту. Хочу дізнатися, скільки часу займе ремонт, скільки коштує і чи є гарантія.",
      "Я в аптеці і мені потрібно щось від сильного головного болю і від алергії. Хочу дізнатися, що ефективніше і чи потрібен рецепт.",
      "Я заселяюся в Airbnb, але ключа немає там, де сказав господар. Потрібно зателефонувати і з'ясувати, як потрапити всередину.",
      "Я в ресторані морепродуктів на пляжі і хочу замовити рибу на грилі, але хочу дізнатися, що свіже сьогодні, і попросити рекомендації.",
      "Я у школі серфінгу і хочу записатися на урок для початківців на завтра вранці. Потрібно запитати про оренду гідрокостюма і о котрій приходити.",
      "У моєї орендованої машини спустило колесо на шосе. Потрібно викликати техдопомогу і пояснити, де я і що сталося.",
    ],
  },
  pwa: {
    installPrompt: "Встановіть SaySay для кращого досвіду",
    install: "Встановити",
  },
  settings: {
    title: "Налаштування",
    theme: "Зовнішній вигляд",
    themeLight: "Світла",
    themeDark: "Темна",
    themeSystem: "Авто",
    resetAll: "Видалити Всі Дані",
    resetConfirm: "Це видалить всі налаштування і теми. Ви впевнені?",
    close: "Закрити",
    speechRecognition: "Розпізнавання мови",
    sttAzure: "Azure",
    sttAzureDesc: "Хмарне, висока якість",
    sttBrowser: "Браузер",
    sttBrowserDesc: "Вбудоване, працює офлайн",
    speechSpeed: "Швидкість мовлення",
    analytics: "Аналітика",
    shareUsageData: "Ділитися даними використання",
    analyticsDescription: "Допоможіть покращити SaySay, ділячись анонімними даними використання",
    knownLanguages: "Відомі мови",
    knownLanguagesDescription: "Виберіть мови, які ви вже знаєте. Це допомагає нам надавати кращі поради щодо вимови, використовуючи звуки з цих мов.",
    noneSelected: "Не вибрано",
    learning: "Навчання",
    showPronunciationHints: "Показати підказки вимови",
    showPronunciationHintsDescription: "Показує, як вимовляти фрази, використовуючи знайомі звуки",
  },
  learning: {
    back: "Назад",
    dialogues: "Діалоги",
    vocabulary: "Словник",
    grammar: "Граматика",
    practice: "Практика",
    cards: "Cards",
    listen: "Слухати",
    showTranslation: "Показати переклад",
    hideTranslation: "Сховати переклад",
    pronunciation: "Вимова",
    sayItLike: "Скажи як",
    allForms: "Усі Форми",
    example: "Приклад",
    inContext: "У Контексті",
    literalMeaning: "Буквальне Значення",
    wordBreakdown: "Розбір Слова",
    fromDialog: "З Діалогу",
    searchVocabulary: "Пошук словника...",
    foundResults: "Знайдено {count} результатів для \"{query}\"",
    progress: "Прогрес",
    mastered: "{count} / {total} засвоєно",
    words: "Слова",
    expressions: "Вирази",
    topical: "Корисно Знати",
    wordsDescription: "Основна лексика з усіма формами",
    expressionsDescription: "Фрази та ідіоми",
    topicalDescription: "Пов'язана лексика",
    noWordsMatch: "Слова не знайдено",
    noExpressionsMatch: "Вирази не знайдено",
    noTopicalMatch: "Пов'язану лексику не знайдено",
    forms: "Форми",
    buildingBlocks: "Базові слова",
    showBuildingBlocks: "Показати базові слова",
    buildingBlocksDescription: "Слова, потрібні для цього діалогу (артиклі, прийменники тощо)",
    hideByDefault: "Я вже знаю — сховати за замовчуванням",
    longPressHint: "Утримуй слово для пошуку",
    hideAll: "Сховати все",
    showAll: "Показати все",
    tapToReveal: "Натисни для показу перекладу",
    practiceMode: "Режим Практики",
    practiceDescription: "Проходь через реальні розмовні сценарії. Вибирай відповіді і дивись, як розвивається діалог.",
    howItWorks: "Як це працює:",
    practiceStep1: "Прочитай репліку",
    practiceStep2: "Вибери найкращу відповідь з варіантів",
    practiceStep3: "Продовжуй до кінця розмови",
    startPractice: "Почати Практику",
    greatJob: "Чудово!",
    keepPracticing: "Продовжуй практикуватися!",
    correctResponses: "{correct} з {total} правильних відповідей",
    yourConversation: "Твоя розмова:",
    tryAgain: "Спробувати Знову",
    newPath: "Новий Шлях",
    turn: "Хід {count}",
    score: "Рахунок: {correct}/{total}",
    chooseResponse: "Вибери відповідь:",
    continueConversation: "Продовжити Розмову",
    seeResults: "Дивитися Результати",
    conversationStarts: "Розмова починається",
    you: "Ти",
    them: "Вони",
    end: "Кінець",
    alsoSay: "Також скажіть",
    playing: "Відтворення...",
    askAI: "Допомога",
    response: "відповідь",
    responses: "відповідей",
    or: "або",
    systemResponse: "Система/Відповідь",
    yourOptions: "Твої варіанти",
    tapToExpand: "Натисни для розгортання. Утримуй слово для перекладу.",
    startAgain: "Почати спочатку",
    pickYourAnswer: "Обери відповідь",
    pickYourAnswerHint: "(натисни на один із варіантів нижче)",
    tapToAnswer: "Натисни на відповідь нижче, щоб продовжити",
    pickTheirAnswer: "Обери їхню відповідь",
    tapToStart: "Натисни, щоб почати",
    transcribing: "Розпізнаємо...",
    useVoice: "Голосом",
    saySomethingElse: "Я хочу сказати щось інше",
    said: "сказав(ла)",
    whatToSay: "Що ти хочеш сказати?",
    typeYourResponse: "Введи свою відповідь...",
    customPhraseHint: "Введи своєю мовою, і ми перекладемо природно",
    fixThisResponse: "Виправити цю відповідь",
    whatsWrong: "Що повинно бути замість цього?",
    fixResponseHint: "Опиши виправлення або вкажи правильну фразу",
    // Practice tab modes
    practiceTab: "Практика",
    practiceWords: "Слова",
    practiceSpelling: "Правопис",
    practiceKaraoke: "Караоке",
    practiceWordsDesc: "Вікторина з картинками",
    practiceSpellingDesc: "Вимовте слова вголос",
    practiceKaraokeDesc: "Вимовляйте фрази слово за словом",
    introduceWord: "Нове слово",
    composeWord: "Скласти слово",
    describeImage: "Опишіть, що на картинці",
    pickWordForSentence: "Оберіть слово для речення",
    iNeedAHint: "Мені потрібна підказка",
    iDontKnow: "Я не знаю це слово",
    tapHoldSayWord: "Натисніть, утримуйте і вимовте слово",
    tapHoldSayPhrase: "Натисніть, утримуйте і вимовте фразу",
    weHeard: "Ми почули:",
    skipWord: "Пропустити",
    nextWord: "Далі",
    practiceComplete: "Практику завершено!",
    wordsCorrectCount: "{correct} з {total} правильно",
    practiceAgain: "Повторити практику",
    done: "Готово",
    correct: "Правильно!",
    chooseModule: "Оберіть модуль",
    moduleHint: "Практикуйте невеликими групами для кращого запам'ятовування",
    wordsCount: "слів",
    markKnown: "Вже знаю",
    markTough: "Складне",
    markLiked: "Подобається",
    knownWordsHidden: "{count} відомих слів приховано",
    noWordsToReview: "Поки немає слів для повторення",
    noWordsToReviewDesc: "Створіть діалоги, щоб поповнити словниковий запас",
    generatingMore: "Генерація...",
  },
  common: {
    loading: "Завантаження...",
    error: "Сталася помилка",
    retry: "Повторити",
    cancel: "Скасувати",
    save: "Зберегти",
    delete: "Видалити",
    edit: "Редагувати",
    close: "Закрити",
    send: "Надіслати",
    copy: "Копіювати",
  },
  games: {
    score: "Рахунок",
    best: "Найкращий",
    hi: "РЕК",
    tapOn: "Натисни на",
    getReady: "Приготуйся...",
    tapToStart: "Натисни, щоб почати!",
    tapToJump: "Натисни, щоб стрибнути!",
    gameOver: "ГРА ЗАКІНЧЕНА",
    tapToRestart: "Натисни, щоб почати знову",
    speedSlow: "Повільно",
    speedSteady: "Стабільно",
    speedNormal: "Нормально",
    speedQuick: "Швидко",
    speedFast: "Стрімко",
    speedBlazing: "Блискавично",
    speedOnFire: "У вогні!",
    speedRocket: "Ракета",
    speedLegendary: "Легендарно",
    cheers: ["Клас!", "Чудово!", "Неймовірно!", "Ого!", "Прекрасно!", "Є!", "Так!", "Бум!", "Ідеально!", "Вражаюче!"],
  },
  errors: {
    microphoneAccessDenied: "Доступ до мікрофона заборонено",
    recognitionError: "Помилка розпізнавання мови",
    recordingFailed: "Не вдалося почати запис",
    noSpeechDetected: "Мова не виявлена. Спробуй знову.",
    llmConfigRequired: "Налаштуй ключ API LLM у налаштуваннях, щоб використовувати цю функцію.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Ласкаво просимо до SaySay",
    welcomeSubtitle: "Будь готовий до будь-якої розмови",
    getStarted: "Почати",
    // Feature slides
    featureTopicsPain: "Підручники не про твоє життя",
    featureTopicsSolution: "Практикуй те, що тобі реально потрібно",
    featureTopicsDesc: "Опиши будь-яку ситуацію — замовити каву, сходити до лікаря, поговорити з сусідами — і отримай діалог під себе.",
    featureVoicePain: "А як це взагалі вимовляється?",
    featureVoiceSolution: "Слухай кожне слово",
    featureVoiceDesc: "Натисни на будь-яку фразу, щоб почути вимову носія мови. Відповідай голосом без рук.",
    featureAiPain: "Шаблонний підхід не працює",
    featureAiSolution: "ШІ збирає твій словник",
    featureAiDesc: "Кожен діалог містить слова та вирази, які ти реально будеш використовувати. Вчи лексику, яка знадобиться в житті.",
    continue: "Далі",
    inputNativeLangTitle: "Якою мовою ти говориш?",
    inputNativeLangSubtitle: "Ми навчатимемо тебе цією мовою",
    inputTargetLangTitle: "Яку мову хочеш вивчати?",
    inputTargetLangSubtitle: "Обери мову, яку хочеш практикувати",
    // Input: Name
    inputNameTitle: "Як тебе називати?",
    inputNameSubtitle: "Можна змінити пізніше",
    inputNamePlaceholder: "Твоє ім'я",
    // Input: Languages
    inputLangTitle: "Знаєш інші мови?",
    inputLangSubtitle: "Ми використаємо схожості, щоб ти вчився швидше",
    inputLangSelect: "Обери мови, які знаєш окрім української",
    inputLangFindMore: "Знайти ще",
    inputLangSearch: "Пошук мови...",
    skipStep: "Пропустити",
    // Input: Style
    inputStyleTitle: "Як хочеш звучати?",
    inputStyleSubtitle: "Можна змінити пізніше",
    speakingStyleBeginner: "Початківець",
    speakingStyleBeginnerDesc: "Прості слова, базові фрази — як той, хто вчиться",
    speakingStyleLocal: "Як місцевий",
    speakingStyleLocalDesc: "Жива мова, сленг, ідіоми — щоб зійти за свого",
    speakingStyleMix: "Змішати стилі",
    speakingStyleMixDesc: "Обери один або кілька стилів",
    styleYourOption: "Свій варіант...",
    styleTypePlaceholder: "Напиши свій стиль...",
    // Input: Gender
    inputGenderTitle: "Хто ти в діалогах?",
    inputGenderSubtitle: "Необов'язково — можна змінити пізніше",
    genderMale: "Чоловік",
    genderFemale: "Жінка",
    genderOther: "Інше / Небінарний",
    genderPreferNot: "Не хочу відповідати",
    // Navigation
    back: "Назад",
    skipAll: "Пропустити все",
    // Ready screen
    startLearning: "Почати вчитися",
    youreAllSet: "Все готово!",
    youreAllSetWithName: "Все готово, {name}!",
    startFirstConversation: "Почни перший діалог і відчуй, як природно можна вчити мову.",
    analyticsOptIn: "Допоможи покращити SaySay — анонімна статистика використання (без особистих даних)",
    // First conversation transition screen
    firstConversationTitle: "Чудово! Тепер давай попрактикуємо твою першу розмову.",
    firstConversationSubtitle: "Обери тему, і ми створимо справжній діалог для практики.",
    firstConversationButton: "Почали!",
    // Preview UI elements
    previewTopicTitle: "Про що хочеш поговорити?",
    previewTopicSubtitle: "Введи сценарій або тему",
    previewExample: "Приклад — торкнись, щоб використати",
    previewExampleText: "Замовити каву та запитати про місцеві рекомендації",
    previewTypeOwn: "Або введи свою тему...",
    previewVoiceText: "Я б хотів каву...",
    previewDialogTranslation: "Доброго ранку! Що бажаєте?",
  },
  knownLanguages: {
    spanish: "Іспанська",
    french: "Французька",
    german: "Німецька",
    italian: "Італійська",
    portuguese: "Португальська",
    russian: "Російська",
    chinese: "Китайська",
    japanese: "Японська",
    korean: "Корейська",
    arabic: "Арабська",
    hindi: "Гінді",
    dutch: "Нідерландська",
    polish: "Польська",
    swedish: "Шведська",
    turkish: "Турецька",
    greek: "Грецька",
    czech: "Чеська",
    romanian: "Румунська",
    hungarian: "Угорська",
    finnish: "Фінська",
    norwegian: "Норвезька",
    danish: "Данська",
    ukrainian: "Українська",
    thai: "Тайська",
    vietnamese: "В'єтнамська",
    indonesian: "Індонезійська",
    malay: "Малайська",
    tagalog: "Тагальська",
    hebrew: "Іврит",
    persian: "Перська",
    bengali: "Бенгальська",
    punjabi: "Пенджабська",
    tamil: "Тамільська",
    urdu: "Урду",
    swahili: "Суахілі",
    afrikaans: "Африкаанс",
    catalan: "Каталанська",
    croatian: "Хорватська",
    serbian: "Сербська",
    slovak: "Словацька",
    slovenian: "Словенська",
    bulgarian: "Болгарська",
    lithuanian: "Литовська",
    latvian: "Латвійська",
    estonian: "Естонська",
    icelandic: "Ісландська",
    welsh: "Валлійська",
    irish: "Ірландська",
    scottishGaelic: "Шотландська гельська",
    basque: "Баскська",
    maltese: "Мальтійська",
    albanian: "Албанська",
    macedonian: "Македонська",
    georgian: "Грузинська",
    armenian: "Вірменська",
    kazakh: "Казахська",
    uzbek: "Узбецька",
    mongolian: "Монгольська",
    nepali: "Непальська",
    sinhala: "Сингальська",
    burmese: "Бірманська",
    khmer: "Кхмерська",
    lao: "Лаоська",
  },
  coachMarks: {
    startNodeTitle: "Почати розмову",
    startNodeBody: "Натисніть на цю бульбашку повідомлення, щоб почати діалог.",
    speakerTitle: "Послухати вимову",
    speakerBody: "Натисніть на значок динаміка, щоб почути фразу вголос.",
    answerTitle: "Оберіть відповідь",
    answerBody: "Натисніть на один із варіантів, щоб обрати, що ви хочете сказати.",
    tabsTitle: "Дізнатися більше",
    tabsBody: "Перемикайтеся між діалогом, вправами та словником.",
    skip: "Пропустити",
    next: "Далі",
    done: "Готово",
  },
  passwordGate: {
    enterPassword: "Введи пароль для продовження",
    placeholder: "Пароль",
  },
  dialogueFeedback: {
    title: "Що ви думаєте про цей діалог?",
    bad: "Погано",
    meh: "Так собі",
    good: "Добре",
    thanks: "Дякуємо за відгук!",
    tellUsWhy: "Дякуємо! Чи можете розповісти чому?",
    commentPlaceholder: "Що можна покращити?",
    skip: "Пропустити",
    send: "Надіслати",
  },
  dialog: {
    listening: "Слухаю тебе...",
    stopRecording: "Зупинити запис",
    practice: "Практика",
    vocabulary: "Словник",
    loadingVocabulary: "Завантаження словника...",
  },
  languageNames: {
    "en-US": "Англійська (США)",
    "en-GB": "Англійська (Велика Британія)",
    "es-ES": "Іспанська (Іспанія)",
    "es-MX": "Іспанська (Мексика)",
    "pt-PT": "Португальська (Португалія)",
    "pt-BR": "Португальська (Бразилія)",
    "fr-FR": "Французька",
    "de-DE": "Німецька",
    "de-AT": "Німецька (Австрія)",
    "de-CH": "Німецька (Швейцарія)",
    "it-IT": "Італійська",
    "ja-JP": "Японська",
    "zh-CN": "Китайська",
    "ko-KR": "Корейська",
    "he-IL": "Іврит",
    "ru-RU": "Російська",
    "pl-PL": "Польська",
    "uk-UA": "Українська",
    "fi-FI": "Фінська",
    "et-EE": "Естонська",
    "lv-LV": "Латвійська",
    "el-GR": "Грецька",
  },
};

// Arabic
const ar: Translations = {
  nav: {
    dialogues: "حوارات",
    grammar: "قواعد",
    practice: "تمرين",
    alphabet: "أبجدية",
  },
  languageSelector: {
    title: "اللغات",
    iSpeak: "أتحدث",
    iWantToLearn: "أريد أن أتعلم",
    done: "تم",
    back: "رجوع",
    moreComingSoon: "المزيد من اللغات قريبًا!",
  },
  topics: {
    title: "مواضيع الحوار",
    subtitle: "اختر محادثة للتدريب",
    tagline: "تعلم أن تقول ما تحتاجه",
    heroTitle: "كن مستعدًا لأي محادثة",
    heroSubtitle: "توقف عن الإيماء — ابدأ بالكلام",
    createCustom: "محادثة جديدة",
    chooseTopic: "اختر موضوعًا",
    available: "متاح",
    words: "كلمات",
    expressions: "تعبيرات",
    difficulty: {
      beginner: "مبتدئ",
      intermediate: "متوسط",
      advanced: "متقدم",
    },
    dialogNodes: "جولات",
    regenerateWarning: "تم إنشاؤه للغة أخرى — اضغط لإعادة الإنشاء",
    deleteTitle: "حذف الموضوع",
    deleteDescription: "هل أنت متأكد أنك تريد حذف هذا الموضوع؟ لا يمكن التراجع عن هذا الإجراء.",
    regenerateTitle: "إعادة إنشاء الحوار",
    regenerateDescription: "سيتم إنشاء حوار جديد لهذا الموضوع. سيتم استبدال الحوار الحالي.",
    regenerate: "إعادة إنشاء",
    edit: "تعديل",
    recent: "الأخيرة",
    categories: "الفئات",
    byCategory: "الحوارات حسب الفئة",
    collapse: "طي",
  },
  grammar: {
    title: "قواعد النحو",
    subtitle: "كيف تعمل {language}",
    noRulesTitle: "لا توجد قواعد بعد",
    noRulesDescription: "أنشئ موضوعًا بـ{language} لرؤية القواعد هنا.",
    rulesCount: "{count} قواعد نحوية",
    rulesSingular: "قاعدة نحوية {count}",
    tapToExpand: "انقر للتوسيع",
    examples: "أمثلة من الحوار",
    howToThink: "كيف تفكر في الأمر",
    commonMistake: "خطأ شائع",
  },
  alphabet: {
    title: "الأبجدية والأصوات",
    subtitle: "نطق {language}",
    comingSoonTitle: "قريبًا",
    comingSoonDescription: "دليل الأبجدية لـ{language} غير متوفر بعد.",
    examples: "أمثلة",
    all: "الكل",
  },
  generator: {
    title: "عن ماذا تريد أن تتحدث؟",
    placeholder: "صف موقفًا...\n\nأمثلة:\n• طلب قهوة في مقهى\n• السؤال عن الاتجاهات\n• حجز موعد طبيب",
    proficiency: "كيف تريد أن تبدو؟",
    generate: "هيا بنا",
    generating: "جاري التحضير...",
    loadTopic: "تحميل",
    back: "رجوع",
    askAI: "صف موقفك",
    responses: "ردود",
    modelTierFast: "سريع",
    modelTierFastDesc: "سريع وبأسعار معقولة",
    modelTierBalanced: "متوازن",
    modelTierBalancedDesc: "أفضل قيمة",
    modelTierPremium: "متميز",
    modelTierPremiumDesc: "أعلى جودة",
    loadingModels: "جاري تحميل النماذج...",
    llmConfigWarning: "قم بتكوين موفر LLM الخاص بك في الإعدادات لتمكين توليد AI",
    topicTitle: "عن ماذا تريد أن تتحدث؟",
    topicHintMic: "اضغط على الميكروفون وقل موضوعك",
    topicHintKeyboard: "اكتب سيناريو أو موضوع",
    exampleTapToUse: "مثال — اضغط للاستخدام",
    enterText: "أدخل نص",
    tapToStop: "اضغط للإيقاف",
    processing: "جاري المعالجة...",
    tapAndTalk: "اضغط وتحدث",
    recording: "جاري التسجيل...",
    useVoice: "استخدم الصوت",
    attach: "إرفاق",
    continue: "متابعة",
    apiKeyRequired: "مطلوب مفتاح API",
    apiKeyRequiredDesc: "قم بتكوين موفر LLM الخاص بك في الإعدادات لتمكين توليد AI",
    difficultyTitle: "كيف تريد أن تبدو؟",
    difficultySubtitle: "اختر أسلوب حديثك",
    styleBeginner: "مبتدئ",
    styleBeginnerDesc: "كلمات بسيطة — كمبتدئ",
    styleLocal: "مثل المحلي",
    styleLocalDesc: "كلام طبيعي، عامية — كأهل البلد",
    styleMix: "امزج",
    styleMixDesc: "اختر واحد أو أكثر",
    styleCustomPlaceholder: "أو اكتب خاصتك...",
    richnessTitle: "كم المحتوى؟",
    richnessSubtitle: "اختر كمية المفردات والحوار",
    richnessQuick: "سريع",
    richnessQuickDesc: "حوار قصير، مفردات أساسية",
    richnessStandard: "قياسي",
    richnessStandardDesc: "حوار متوسط، تغطية جيدة للمفردات",
    richnessComprehensive: "شامل",
    richnessComprehensiveDesc: "حوار طويل، مفردات واسعة",
    modelTitle: "اختر مستوى الجودة",
    modelSubtitle: "جودة أعلى = محتوى أفضل، تكلفة أعلى",
    cachedAvailable: "الرد المخزن متاح — تحميل فوري!",
    loadCached: "تحميل المخزن",
    previewPrompt: "معاينة الموجه",
    fullPrompt: "الموجه الكامل",
    copied: "تم النسخ!",
    systemPrompt: "موجه النظام",
    userPrompt: "موجه المستخدم",
    gettingReady: "جاري التحضير...",
    connectingToAI: "الاتصال بـ AI...",
    done: "تم!",
    letsGo: "هيا بنا",
    goBack: "رجوع",
    playWhileWaiting: "العب {game1} أو {game2} أثناء الانتظار!",
    play: "العب",
    or: "أو",
    whileYouWait: "أثناء الانتظار",
    exitGame: "الخروج من اللعبة",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "طلبت إطارات سيارة عبر الإنترنت ووصلت. أحتاج الاتصال وحجز موعد لتركيبها هذا الأسبوع.",
      "أنا في مقهى متخصص وأريد طلب فلات وايت بحليب الشوفان ومعجنات. أريد أيضاً أن أسأل إذا عندهم حبوب قهوة أحادية المصدر.",
      "أنا في محطة القطار وأحتاج الوصول لوجهتي. أريد معرفة أي رصيف، وكم سعر تذكرة الذهاب والعودة، وهل يمكن الدفع بالبطاقة.",
      "أنا في حلبة كارتينج مع أصدقائي. نريد حجز جلسة 20 دقيقة لـ 4 أشخاص والسؤال عن خصومات المجموعات.",
      "شاشة هاتفي مكسورة وأنا في محل صيانة. أريد أن أسأل كم يستغرق الإصلاح، وكم التكلفة، وهل هناك ضمان.",
      "أنا في الصيدلية وأحتاج شيء لصداع شديد ودواء للحساسية. أريد معرفة أيهما أقوى وهل أحتاج وصفة طبية.",
      "أنا أسجل الدخول في Airbnb والمفتاح ليس في المكان الذي قاله المضيف. أحتاج الاتصال ومعرفة كيف أدخل.",
      "أنا في مطعم مأكولات بحرية على الشاطئ وأريد طلب سمك مشوي، لكن أريد معرفة ما هو الطازج اليوم وطلب توصيات.",
      "أنا في مدرسة ركوب الأمواج وأريد حجز درس للمبتدئين غداً صباحاً. أحتاج أن أسأل عن استئجار البدلة وموعد الوصول.",
      "سيارتي المستأجرة إطارها فارغ على الطريق السريع. أحتاج الاتصال بالمساعدة على الطريق وشرح أين أنا وماذا حصل.",
    ],
  },
  pwa: {
    installPrompt: "ثبّت SaySay للحصول على تجربة أفضل",
    install: "تثبيت",
  },
  settings: {
    title: "الإعدادات",
    theme: "المظهر",
    themeLight: "فاتح",
    themeDark: "داكن",
    themeSystem: "تلقائي",
    resetAll: "حذف جميع البيانات",
    resetConfirm: "سيؤدي هذا إلى حذف جميع الإعدادات والمواضيع. هل أنت متأكد؟",
    close: "إغلاق",
    speechRecognition: "التعرف على الكلام",
    sttAzure: "Azure",
    sttAzureDesc: "سحابي، جودة عالية",
    sttBrowser: "المتصفح",
    sttBrowserDesc: "مدمج، يعمل بدون اتصال",
    speechSpeed: "سرعة الكلام",
    analytics: "التحليلات",
    shareUsageData: "مشاركة بيانات الاستخدام",
    analyticsDescription: "ساعد في تحسين SaySay من خلال مشاركة بيانات الاستخدام المجهولة",
    knownLanguages: "اللغات المعروفة",
    knownLanguagesDescription: "اختر اللغات التي تعرفها بالفعل. هذا يساعدنا على تقديم نصائح أفضل للنطق باستخدام أصوات من هذه اللغات.",
    noneSelected: "لم يتم الاختيار",
    learning: "التعلم",
    showPronunciationHints: "عرض تلميحات النطق",
    showPronunciationHintsDescription: "يعرض كيفية نطق العبارات باستخدام أصوات تعرفها",
  },
  learning: {
    back: "رجوع",
    dialogues: "حوارات",
    vocabulary: "مفردات",
    grammar: "قواعد",
    practice: "تدريب",
    cards: "Cards",
    listen: "استمع",
    showTranslation: "عرض الترجمة",
    hideTranslation: "إخفاء الترجمة",
    pronunciation: "النطق",
    sayItLike: "قلها مثل",
    allForms: "جميع الأشكال",
    example: "مثال",
    inContext: "في السياق",
    literalMeaning: "المعنى الحرفي",
    wordBreakdown: "تحليل الكلمة",
    fromDialog: "من الحوار",
    searchVocabulary: "البحث في المفردات...",
    foundResults: "تم العثور على {count} نتيجة لـ \"{query}\"",
    progress: "التقدم",
    mastered: "{count} / {total} تم إتقانها",
    words: "كلمات",
    expressions: "تعبيرات",
    topical: "من الجيد معرفته",
    wordsDescription: "المفردات الأساسية مع جميع الأشكال",
    expressionsDescription: "العبارات والتعابير",
    topicalDescription: "مفردات ذات صلة",
    noWordsMatch: "لا توجد كلمات مطابقة",
    noExpressionsMatch: "لا توجد تعبيرات مطابقة",
    noTopicalMatch: "لا توجد مفردات ذات صلة مطابقة",
    forms: "أشكال",
    buildingBlocks: "اللبنات الأساسية",
    showBuildingBlocks: "إظهار اللبنات الأساسية",
    buildingBlocksDescription: "كلمات شائعة تحتاجها لبناء هذا الحوار (أدوات التعريف، حروف الجر، إلخ)",
    hideByDefault: "أعرفها بالفعل — إخفاء افتراضيًا",
    longPressHint: "اضغط مطولاً على أي كلمة للبحث عنها",
    hideAll: "إخفاء الكل",
    showAll: "إظهار الكل",
    tapToReveal: "اضغط لإظهار الترجمة",
    practiceMode: "وضع التدريب",
    practiceDescription: "تنقل عبر سيناريوهات محادثة حقيقية. اختر الردود وشاهد كيف يتطور الحوار.",
    howItWorks: "كيف يعمل:",
    practiceStep1: "اقرأ تعليمات المحادثة",
    practiceStep2: "اختر أفضل رد من الخيارات",
    practiceStep3: "استمر حتى نهاية المحادثة",
    startPractice: "ابدأ التدريب",
    greatJob: "عمل رائع!",
    keepPracticing: "استمر في التدريب!",
    correctResponses: "{correct} من {total} إجابات صحيحة",
    yourConversation: "محادثتك:",
    tryAgain: "حاول مرة أخرى",
    newPath: "مسار جديد",
    turn: "الدور {count}",
    score: "النتيجة: {correct}/{total}",
    chooseResponse: "اختر ردك:",
    continueConversation: "متابعة المحادثة",
    seeResults: "عرض النتائج",
    conversationStarts: "تبدأ المحادثة",
    you: "أنت",
    them: "هم",
    end: "نهاية",
    alsoSay: "قل أيضاً",
    playing: "جاري التشغيل...",
    askAI: "مساعدة",
    response: "رد",
    responses: "ردود",
    or: "أو",
    systemResponse: "النظام/الرد",
    yourOptions: "خياراتك",
    tapToExpand: "اضغط للتوسيع. اضغط مطولاً على كلمة للترجمة.",
    startAgain: "ابدأ من جديد",
    pickYourAnswer: "اختر إجابتك",
    pickYourAnswerHint: "(انقر على إحدى الإجابات أدناه)",
    tapToAnswer: "اضغط على الإجابة أدناه للمتابعة",
    pickTheirAnswer: "اختر إجابتهم",
    tapToStart: "اضغط للبدء",
    transcribing: "جارٍ النسخ...",
    useVoice: "استخدم الصوت",
    saySomethingElse: "أريد أن أقول شيئاً آخر",
    said: "قال/قالت",
    whatToSay: "ماذا تريد أن تقول؟",
    typeYourResponse: "اكتب ردك...",
    customPhraseHint: "اكتب بلغتك وسنترجمها بشكل طبيعي",
    fixThisResponse: "تصحيح هذا الرد",
    whatsWrong: "ماذا يجب أن يقول بدلاً من ذلك؟",
    fixResponseHint: "صف التصحيح أو قدم العبارة الصحيحة",
    // Practice tab modes
    practiceTab: "تمرين",
    practiceWords: "كلمات",
    practiceSpelling: "إملاء",
    practiceKaraoke: "كاريوكي",
    practiceWordsDesc: "اختبار بالصور",
    practiceSpellingDesc: "انطق الكلمات بصوت عالٍ",
    practiceKaraokeDesc: "انطق العبارات كلمة بكلمة",
    introduceWord: "كلمة جديدة",
    composeWord: "تكوين كلمة",
    describeImage: "صف ما في الصورة",
    pickWordForSentence: "اختر كلمة للجملة",
    iNeedAHint: "أحتاج تلميحاً",
    iDontKnow: "لا أعرف هذه الكلمة",
    tapHoldSayWord: "انقر، اضغط مطولاً وقل الكلمة",
    tapHoldSayPhrase: "انقر، اضغط مطولاً وقل العبارة",
    weHeard: "سمعنا:",
    skipWord: "تخطي",
    nextWord: "التالي",
    practiceComplete: "اكتمل التمرين!",
    wordsCorrectCount: "{correct} من {total} صحيحة",
    practiceAgain: "تمرّن مرة أخرى",
    done: "تم",
    correct: "صحيح!",
    chooseModule: "اختر وحدة",
    moduleHint: "تدرب في مجموعات صغيرة لحفظ أفضل",
    wordsCount: "كلمات",
    markKnown: "أعرفها",
    markTough: "صعبة",
    markLiked: "أعجبني",
    knownWordsHidden: "{count} كلمات معروفة مخفية",
    noWordsToReview: "لا توجد كلمات للمراجعة بعد",
    noWordsToReviewDesc: "أنشئ بعض الحوارات لبناء مفرداتك",
    generatingMore: "...جارٍ الإنشاء",
  },
  common: {
    loading: "جاري التحميل...",
    error: "حدث خطأ",
    retry: "إعادة المحاولة",
    cancel: "إلغاء",
    save: "حفظ",
    delete: "حذف",
    edit: "تعديل",
    close: "إغلاق",
    send: "إرسال",
    copy: "نسخ",
  },
  games: {
    score: "النقاط",
    best: "الأفضل",
    hi: "أعلى",
    tapOn: "اضغط على",
    getReady: "استعد...",
    tapToStart: "اضغط للبدء!",
    tapToJump: "اضغط للقفز!",
    gameOver: "انتهت اللعبة",
    tapToRestart: "اضغط لإعادة البدء",
    speedSlow: "بطيء",
    speedSteady: "ثابت",
    speedNormal: "عادي",
    speedQuick: "سريع",
    speedFast: "خاطف",
    speedBlazing: "مشتعل",
    speedOnFire: "ملتهب!",
    speedRocket: "صاروخ",
    speedLegendary: "أسطوري",
    cheers: ["أحسنت!", "رائع!", "مذهل!", "واو!", "ممتاز!", "أصبت!", "نعم!", "بوم!", "مثالي!", "خرافي!"],
  },
  errors: {
    microphoneAccessDenied: "تم رفض الوصول إلى الميكروفون",
    recognitionError: "خطأ في التعرف على الكلام",
    recordingFailed: "فشل بدء التسجيل",
    noSpeechDetected: "لم يتم اكتشاف كلام. يرجى المحاولة مرة أخرى.",
    llmConfigRequired: "قم بتكوين مفتاح API الخاص بـ LLM في الإعدادات لاستخدام هذه الميزة.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "مرحباً بك في SaySay",
    welcomeSubtitle: "كن مستعداً لأي محادثة",
    getStarted: "لنبدأ",
    // Feature slides
    featureTopicsPain: "الكتب الدراسية لا تناسب حياتك",
    featureTopicsSolution: "تدرب على ما تحتاجه فعلاً",
    featureTopicsDesc: "صف أي موقف — طلب قهوة، زيارة الطبيب، الدردشة مع الجيران — واحصل على حوار مصمم لك.",
    featureVoicePain: "كيف تُنطق هذه الكلمة؟",
    featureVoiceSolution: "استمع لكل كلمة",
    featureVoiceDesc: "انقر على أي عبارة لسماع نطق الناطق الأصلي. استخدم صوتك للرد بدون يدين.",
    featureAiPain: "الحل الموحد لا يناسب الجميع",
    featureAiSolution: "الذكاء الاصطناعي يبني مفرداتك",
    featureAiDesc: "كل حوار يحتوي على كلمات وتعبيرات ستستخدمها فعلاً. تعلم مفردات تناسب حياتك.",
    continue: "متابعة",
    inputNativeLangTitle: "ما هي لغتك؟",
    inputNativeLangSubtitle: "سنعلّمك بهذه اللغة",
    inputTargetLangTitle: "ماذا تريد أن تتعلم؟",
    inputTargetLangSubtitle: "اختر اللغة التي تريد ممارستها",
    // Input: Name
    inputNameTitle: "ماذا نناديك؟",
    inputNameSubtitle: "يمكنك تغييره لاحقاً",
    inputNamePlaceholder: "اسمك",
    // Input: Languages
    inputLangTitle: "هل تعرف لغات أخرى؟",
    inputLangSubtitle: "سنستخدم التشابهات لمساعدتك على التعلم أسرع",
    inputLangSelect: "اختر اللغات التي تعرفها بالإضافة إلى العربية",
    inputLangFindMore: "ابحث عن المزيد",
    inputLangSearch: "ابحث عن لغة...",
    skipStep: "تخطي",
    // Input: Style
    inputStyleTitle: "كيف تريد أن تبدو؟",
    inputStyleSubtitle: "يمكنك تغييره لاحقاً",
    speakingStyleBeginner: "مبتدئ",
    speakingStyleBeginnerDesc: "كلمات بسيطة، عبارات أساسية — كمتعلم",
    speakingStyleLocal: "كأنك من أهل البلد",
    speakingStyleLocalDesc: "تعبيرات طبيعية، عامية، تعابير اصطلاحية — للاندماج",
    speakingStyleMix: "امزج الأنماط",
    speakingStyleMixDesc: "اختر نمطاً واحداً أو أكثر",
    styleYourOption: "خيارك...",
    styleTypePlaceholder: "اكتب نمطك...",
    // Input: Gender
    inputGenderTitle: "من أنت في الحوارات؟",
    inputGenderSubtitle: "اختياري — يمكنك تغييره لاحقاً",
    genderMale: "ذكر",
    genderFemale: "أنثى",
    genderOther: "آخر / غير ثنائي",
    genderPreferNot: "أفضل عدم القول",
    // Navigation
    back: "رجوع",
    skipAll: "تخطي الكل",
    // Ready screen
    startLearning: "ابدأ التعلم",
    youreAllSet: "أنت جاهز!",
    youreAllSetWithName: "أنت جاهز يا {name}!",
    startFirstConversation: "ابدأ محادثتك الأولى واكتشف كيف يمكن أن يكون تعلم اللغة طبيعياً.",
    analyticsOptIn: "ساعد في تحسين SaySay من خلال مشاركة بيانات استخدام مجهولة (لا نجمع معلومات شخصية)",
    // First conversation transition screen
    firstConversationTitle: "رائع! الآن لنتدرب على محادثتك الأولى.",
    firstConversationSubtitle: "اختر موضوعاً وسنقوم بإنشاء حوار حقيقي للتدرب عليه.",
    firstConversationButton: "!هيا بنا",
    // Preview UI elements
    previewTopicTitle: "عن ماذا تريد التحدث؟",
    previewTopicSubtitle: "اكتب سيناريو أو موضوع",
    previewExample: "مثال — انقر للاستخدام",
    previewExampleText: "طلب قهوة والسؤال عن توصيات محلية",
    previewTypeOwn: "أو اكتب ما تريد...",
    previewVoiceText: "أريد قهوة...",
    previewDialogTranslation: "صباح الخير! ماذا تريد؟",
  },
  knownLanguages: {
    spanish: "الإسبانية",
    french: "الفرنسية",
    german: "الألمانية",
    italian: "الإيطالية",
    portuguese: "البرتغالية",
    russian: "الروسية",
    chinese: "الصينية",
    japanese: "اليابانية",
    korean: "الكورية",
    arabic: "العربية",
    hindi: "الهندية",
    dutch: "الهولندية",
    polish: "البولندية",
    swedish: "السويدية",
    turkish: "التركية",
    greek: "اليونانية",
    czech: "التشيكية",
    romanian: "الرومانية",
    hungarian: "المجرية",
    finnish: "الفنلندية",
    norwegian: "النرويجية",
    danish: "الدنماركية",
    ukrainian: "الأوكرانية",
    thai: "التايلاندية",
    vietnamese: "الفيتنامية",
    indonesian: "الإندونيسية",
    malay: "الملايوية",
    tagalog: "التاغالوغية",
    hebrew: "العبرية",
    persian: "الفارسية",
    bengali: "البنغالية",
    punjabi: "البنجابية",
    tamil: "التاميلية",
    urdu: "الأردية",
    swahili: "السواحيلية",
    afrikaans: "الأفريكانية",
    catalan: "الكتالونية",
    croatian: "الكرواتية",
    serbian: "الصربية",
    slovak: "السلوفاكية",
    slovenian: "السلوفينية",
    bulgarian: "البلغارية",
    lithuanian: "الليتوانية",
    latvian: "اللاتفية",
    estonian: "الإستونية",
    icelandic: "الآيسلندية",
    welsh: "الويلزية",
    irish: "الأيرلندية",
    scottishGaelic: "الغيلية الاسكتلندية",
    basque: "الباسكية",
    maltese: "المالطية",
    albanian: "الألبانية",
    macedonian: "المقدونية",
    georgian: "الجورجية",
    armenian: "الأرمنية",
    kazakh: "الكازاخية",
    uzbek: "الأوزبكية",
    mongolian: "المنغولية",
    nepali: "النيبالية",
    sinhala: "السنهالية",
    burmese: "البورمية",
    khmer: "الخميرية",
    lao: "اللاوية",
  },
  coachMarks: {
    startNodeTitle: "ابدأ المحادثة",
    startNodeBody: "انقر على فقاعة الرسالة هذه لبدء الحوار.",
    speakerTitle: "استمع إلى النطق",
    speakerBody: "انقر على أيقونة مكبر الصوت لسماع العبارة بصوت عالٍ.",
    answerTitle: "اختر إجابتك",
    answerBody: "انقر على أحد الخيارات لاختيار ما تريد قوله.",
    tabsTitle: "اكتشف المزيد",
    tabsBody: "التبديل بين الحوار وتمارين الممارسة والمفردات.",
    skip: "تخطي",
    next: "التالي",
    done: "تم",
  },
  passwordGate: {
    enterPassword: "أدخل كلمة المرور للمتابعة",
    placeholder: "كلمة المرور",
  },
  dialogueFeedback: {
    title: "ما رأيك في هذا الحوار؟",
    bad: "سيئ",
    meh: "عادي",
    good: "جيد",
    thanks: "شكراً على ملاحظاتك!",
    tellUsWhy: "شكراً! هل يمكنك إخبارنا بالسبب؟",
    commentPlaceholder: "ما الذي يمكن تحسينه؟",
    skip: "تخطي",
    send: "إرسال",
  },
  dialog: {
    listening: "أستمع إليك...",
    stopRecording: "إيقاف التسجيل",
    practice: "Practice",
    vocabulary: "Vocabulary",
    loadingVocabulary: "Loading vocabulary...",
  },
  languageNames: {
    "en-US": "الإنجليزية (أمريكا)",
    "en-GB": "الإنجليزية (بريطانيا)",
    "es-ES": "الإسبانية (إسبانيا)",
    "es-MX": "الإسبانية (المكسيك)",
    "pt-PT": "البرتغالية (البرتغال)",
    "pt-BR": "البرتغالية (البرازيل)",
    "fr-FR": "الفرنسية",
    "de-DE": "الألمانية",
    "de-AT": "الألمانية (النمسا)",
    "de-CH": "الألمانية (سويسرا)",
    "it-IT": "الإيطالية",
    "ja-JP": "اليابانية",
    "zh-CN": "الصينية",
    "ko-KR": "الكورية",
    "he-IL": "العبرية",
    "ru-RU": "الروسية",
    "pl-PL": "البولندية",
    "uk-UA": "الأوكرانية",
    "fi-FI": "الفنلندية",
    "et-EE": "الإستونية",
    "lv-LV": "اللاتفية",
    "el-GR": "اليونانية",
  },
};

// Dutch
const nl: Translations = {
  nav: {
    dialogues: "Dialogen",
    grammar: "Grammatica",
    practice: "Oefening",
    alphabet: "Alfabet",
  },
  languageSelector: {
    title: "Talen",
    iSpeak: "Ik spreek",
    iWantToLearn: "Ik wil leren",
    done: "Klaar",
    back: "Terug",
    moreComingSoon: "Meer talen komen binnenkort!",
  },
  topics: {
    title: "Dialoogonderwerpen",
    subtitle: "Kies een gesprek om te oefenen",
    tagline: "Leer zeggen wat je nodig hebt",
    heroTitle: "Wees klaar voor elk gesprek",
    heroSubtitle: "Stop met knikken — begin te praten",
    createCustom: "Nieuw gesprek",
    chooseTopic: "Kies een Onderwerp",
    available: "beschikbaar",
    words: "woorden",
    expressions: "uitdrukkingen",
    difficulty: {
      beginner: "Beginner",
      intermediate: "Gemiddeld",
      advanced: "Gevorderd",
    },
    dialogNodes: "beurten",
    regenerateWarning: "Gemaakt voor een andere taal — tik om opnieuw te maken",
    deleteTitle: "Onderwerp Verwijderen",
    deleteDescription: "Weet je zeker dat je dit onderwerp wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.",
    regenerateTitle: "Dialoog opnieuw genereren",
    regenerateDescription: "Er wordt een nieuwe dialoog aangemaakt voor dit onderwerp. De huidige dialoog wordt vervangen.",
    regenerate: "Opnieuw genereren",
    edit: "Bewerken",
    recent: "Recent",
    categories: "Categorieën",
    byCategory: "Dialogen per categorie",
    collapse: "Inklappen",
  },
  grammar: {
    title: "Grammaticaregels",
    subtitle: "Hoe {language} werkt",
    noRulesTitle: "Nog Geen Regels",
    noRulesDescription: "Maak een onderwerp in {language} om grammaticaregels te zien.",
    rulesCount: "{count} grammaticaregels",
    rulesSingular: "{count} grammaticaregel",
    tapToExpand: "Tik om uit te vouwen",
    examples: "Voorbeelden uit Dialoog",
    howToThink: "Hoe Erover na te Denken",
    commonMistake: "Veelgemaakte Fout",
  },
  alphabet: {
    title: "Alfabet & Klanken",
    subtitle: "{language} uitspraak",
    comingSoonTitle: "Binnenkort",
    comingSoonDescription: "De alfabetgids voor {language} is nog niet beschikbaar.",
    examples: "Voorbeelden",
    all: "Alles",
  },
  generator: {
    title: "Waar wil je over praten?",
    placeholder: "Beschrijf een situatie...\n\nVoorbeelden:\n• Koffie bestellen in een café\n• De weg vragen\n• Doktersafspraak maken",
    proficiency: "Hoe wil je klinken?",
    generate: "Laten we beginnen",
    generating: "Alles wordt voorbereid...",
    loadTopic: "Laden",
    back: "Terug",
    askAI: "Beschrijf je situatie",
    responses: "reacties",
    modelTierFast: "Snel",
    modelTierFastDesc: "Snel en betaalbaar",
    modelTierBalanced: "Gebalanceerd",
    modelTierBalancedDesc: "Beste waarde",
    modelTierPremium: "Premium",
    modelTierPremiumDesc: "Hoogste kwaliteit",
    loadingModels: "Modellen laden...",
    llmConfigWarning: "Configureer je LLM-provider in instellingen om AI-generatie in te schakelen",
    topicTitle: "Waar wil je over praten?",
    topicHintMic: "Tik op de microfoon en spreek je onderwerp",
    topicHintKeyboard: "Typ een scenario of onderwerp",
    exampleTapToUse: "Voorbeeld — tik om te gebruiken",
    enterText: "Tekst invoeren",
    tapToStop: "Tik om te stoppen",
    processing: "Verwerken...",
    tapAndTalk: "Tik en praat",
    recording: "Opnemen...",
    useVoice: "Gebruik stem",
    attach: "Bijvoegen",
    continue: "Doorgaan",
    apiKeyRequired: "API-sleutel vereist",
    apiKeyRequiredDesc: "Configureer je LLM-provider in instellingen om AI-generatie in te schakelen",
    difficultyTitle: "Hoe wil je klinken?",
    difficultySubtitle: "Kies je spreekstijl",
    styleBeginner: "Beginner",
    styleBeginnerDesc: "Eenvoudige woorden — zoals een beginner",
    styleLocal: "Zoals een local",
    styleLocalDesc: "Natuurlijke spraak, slang — zoals een native",
    styleMix: "Mix",
    styleMixDesc: "Kies een of meer",
    styleCustomPlaceholder: "Of typ je eigen...",
    richnessTitle: "Hoeveel inhoud?",
    richnessSubtitle: "Kies de hoeveelheid vocabulaire en dialoog",
    richnessQuick: "Snel",
    richnessQuickDesc: "Korte dialoog, essentiële woordenschat",
    richnessStandard: "Standaard",
    richnessStandardDesc: "Gemiddelde dialoog, goede woordenschat dekking",
    richnessComprehensive: "Uitgebreid",
    richnessComprehensiveDesc: "Lange dialoog, uitgebreide woordenschat",
    modelTitle: "Kies kwaliteitsniveau",
    modelSubtitle: "Hogere kwaliteit = betere inhoud, hogere kosten",
    cachedAvailable: "Gecachte reactie beschikbaar — direct laden!",
    loadCached: "Cache laden",
    previewPrompt: "Prompt bekijken",
    fullPrompt: "Volledige prompt",
    copied: "Gekopieerd!",
    systemPrompt: "Systeem prompt",
    userPrompt: "Gebruiker prompt",
    gettingReady: "Alles wordt voorbereid...",
    connectingToAI: "Verbinden met AI...",
    done: "Klaar!",
    letsGo: "Laten we beginnen",
    goBack: "Terug",
    playWhileWaiting: "Speel {game1} of {game2} terwijl je wacht!",
    play: "Speel",
    or: "of",
    whileYouWait: "terwijl je wacht",
    exitGame: "Spel afsluiten",
    // Sample scenarios for inspiration
    sampleScenarios: [
      "Ik heb online banden besteld bij een bandenwinkel en ze zijn aangekomen. Ik moet bellen om een afspraak te maken om ze deze week te laten monteren.",
      "Ik ben in een specialty koffiebar en wil een flat white met havermelk en een gebakje bestellen. Ik wil ook vragen of ze single-origin bonen hebben.",
      "Ik ben op het station en moet naar mijn bestemming. Ik wil weten welk perron, hoeveel een retourtje kost en of ik met pin kan betalen.",
      "Ik ben bij een kartbaan met vrienden. We willen een sessie van 20 minuten voor 4 personen boeken en vragen naar groepskorting.",
      "Mijn telefoonscherm is gebarsten en ik ben bij een reparatiewinkel. Ik wil vragen hoe lang het duurt, hoeveel het kost en of ze garantie hebben.",
      "Ik ben bij de apotheek en heb iets nodig tegen erge hoofdpijn en ook een antihistamine. Ik wil weten welke het sterkst is en of ik een recept nodig heb.",
      "Ik check in bij een Airbnb en de sleutel is niet waar de host zei. Ik moet bellen om uit te zoeken hoe ik binnenkom.",
      "Ik ben in een visrestaurant aan het strand en wil gegrilde vis bestellen, maar wil weten wat er vandaag vers is en om aanbevelingen vragen.",
      "Ik ben bij een surfschool en wil een beginnerles boeken voor morgenochtend. Ik moet vragen naar wetsuithuur en hoe laat ik moet komen.",
      "Mijn huurauto heeft een lekke band op de snelweg. Ik moet de pechhulp bellen en uitleggen waar ik ben en wat er is gebeurd.",
    ],
  },
  pwa: {
    installPrompt: "Installeer SaySay voor een betere ervaring",
    install: "Installeren",
  },
  settings: {
    title: "Instellingen",
    theme: "Weergave",
    themeLight: "Licht",
    themeDark: "Donker",
    themeSystem: "Auto",
    resetAll: "Verwijder Alle Gegevens",
    resetConfirm: "Dit verwijdert alle instellingen en onderwerpen. Weet je het zeker?",
    close: "Sluiten",
    speechRecognition: "Spraakherkenning",
    sttAzure: "Azure",
    sttAzureDesc: "Cloudgebaseerd, hoge kwaliteit",
    sttBrowser: "Browser",
    sttBrowserDesc: "Ingebouwd, werkt offline",
    speechSpeed: "Spreeksnelheid",
    analytics: "Analytics",
    shareUsageData: "Gebruiksgegevens delen",
    analyticsDescription: "Help SaySay te verbeteren door anonieme gebruiksgegevens te delen",
    knownLanguages: "Bekende talen",
    knownLanguagesDescription: "Selecteer talen die je al kent. Dit helpt ons betere uitspraaktips te geven met klanken uit deze talen.",
    noneSelected: "Geen geselecteerd",
    learning: "Leren",
    showPronunciationHints: "Uitspraakhints tonen",
    showPronunciationHintsDescription: "Toont hoe je zinnen uitspreekt met klanken die je al kent",
  },
  learning: {
    back: "Terug",
    dialogues: "Dialogen",
    vocabulary: "Woordenschat",
    grammar: "Grammatica",
    practice: "Oefenen",
    cards: "Cards",
    listen: "Luister",
    showTranslation: "Toon vertaling",
    hideTranslation: "Verberg vertaling",
    pronunciation: "Uitspraak",
    sayItLike: "Zeg het als",
    allForms: "Alle Vormen",
    example: "Voorbeeld",
    inContext: "In Context",
    literalMeaning: "Letterlijke Betekenis",
    wordBreakdown: "Woordanalyse",
    fromDialog: "Uit Dialoog",
    searchVocabulary: "Woordenschat zoeken...",
    foundResults: "{count} resultaten gevonden voor \"{query}\"",
    progress: "Voortgang",
    mastered: "{count} / {total} beheerst",
    words: "Woorden",
    expressions: "Uitdrukkingen",
    topical: "Goed om te Weten",
    wordsDescription: "Kernwoordenschat met alle vormen",
    expressionsDescription: "Uitdrukkingen en idiomen",
    topicalDescription: "Gerelateerde woordenschat",
    noWordsMatch: "Geen woorden komen overeen",
    noExpressionsMatch: "Geen uitdrukkingen komen overeen",
    noTopicalMatch: "Geen gerelateerde woordenschat komt overeen",
    forms: "Vormen",
    buildingBlocks: "Bouwstenen",
    showBuildingBlocks: "Toon bouwstenen",
    buildingBlocksDescription: "Veelvoorkomende woorden die je nodig hebt voor deze dialoog (lidwoorden, voorzetsels, enz.)",
    hideByDefault: "Ik ken ze al — standaard verbergen",
    longPressHint: "Houd een woord ingedrukt om te zoeken",
    hideAll: "Alles verbergen",
    showAll: "Alles tonen",
    tapToReveal: "Tik om vertaling te tonen",
    practiceMode: "Oefenmodus",
    practiceDescription: "Navigeer door echte gespreksscenario's. Kies antwoorden en zie hoe de dialoog zich ontvouwt.",
    howItWorks: "Hoe het werkt:",
    practiceStep1: "Lees de gespreksprompt",
    practiceStep2: "Kies het beste antwoord uit de opties",
    practiceStep3: "Ga door tot het einde van het gesprek",
    startPractice: "Start Oefening",
    greatJob: "Goed gedaan!",
    keepPracticing: "Blijf oefenen!",
    correctResponses: "{correct} van {total} correcte antwoorden",
    yourConversation: "Je gesprek:",
    tryAgain: "Probeer Opnieuw",
    newPath: "Nieuw Pad",
    turn: "Beurt {count}",
    score: "Score: {correct}/{total}",
    chooseResponse: "Kies je antwoord:",
    continueConversation: "Gesprek Voortzetten",
    seeResults: "Resultaten Bekijken",
    conversationStarts: "Gesprek begint",
    you: "Jij",
    them: "Zij",
    end: "Einde",
    alsoSay: "Zeg ook",
    playing: "Speelt af...",
    askAI: "Hulp",
    response: "antwoord",
    responses: "antwoorden",
    or: "of",
    systemResponse: "Systeem/Antwoord",
    yourOptions: "Jouw opties",
    tapToExpand: "Tik om uit te vouwen. Houd een woord ingedrukt om te vertalen.",
    startAgain: "Opnieuw beginnen",
    pickYourAnswer: "Kies je antwoord",
    pickYourAnswerHint: "(tik op een van de opties hieronder)",
    tapToAnswer: "Tik op het antwoord hieronder om verder te gaan",
    pickTheirAnswer: "Kies hun antwoord",
    tapToStart: "Tik om te starten",
    transcribing: "Transcriberen...",
    useVoice: "Gebruik spraak",
    saySomethingElse: "Ik wil iets anders zeggen",
    said: "zei",
    whatToSay: "Wat wil je zeggen?",
    typeYourResponse: "Typ je antwoord...",
    customPhraseHint: "Typ in je taal en we vertalen het natuurlijk",
    fixThisResponse: "Corrigeer dit antwoord",
    whatsWrong: "Wat moet het in plaats daarvan zeggen?",
    fixResponseHint: "Beschrijf de correctie of geef de juiste zin",
    // Practice tab modes
    practiceTab: "Oefening",
    practiceWords: "Woorden",
    practiceSpelling: "Spelling",
    practiceKaraoke: "Karaoke",
    practiceWordsDesc: "Quiz met afbeeldingen",
    practiceSpellingDesc: "Zeg woorden hardop",
    practiceKaraokeDesc: "Spreek zinnen woord voor woord uit",
    introduceWord: "Nieuw woord",
    composeWord: "Een woord samenstellen",
    describeImage: "Beschrijf wat er op de afbeelding staat",
    pickWordForSentence: "Kies een woord voor de zin",
    iNeedAHint: "Ik heb een hint nodig",
    iDontKnow: "Ik ken dit woord niet",
    tapHoldSayWord: "Tik, houd vast en zeg het woord",
    tapHoldSayPhrase: "Tik, houd vast en zeg de zin",
    weHeard: "We hoorden:",
    skipWord: "Overslaan",
    nextWord: "Volgende",
    practiceComplete: "Oefening voltooid!",
    wordsCorrectCount: "{correct} van {total} correct",
    practiceAgain: "Opnieuw oefenen",
    done: "Klaar",
    correct: "Correct!",
    chooseModule: "Kies een module",
    moduleHint: "Oefen in kleine groepen voor beter onthouden",
    wordsCount: "woorden",
    markKnown: "Ken ik al",
    markTough: "Moeilijk",
    markLiked: "Leuk",
    knownWordsHidden: "{count} bekende woorden verborgen",
    noWordsToReview: "Nog geen woorden om te oefenen",
    noWordsToReviewDesc: "Maak enkele dialogen om je woordenschat op te bouwen",
    generatingMore: "Genereren...",
  },
  common: {
    loading: "Laden...",
    error: "Er is een fout opgetreden",
    retry: "Opnieuw",
    cancel: "Annuleren",
    save: "Opslaan",
    delete: "Verwijderen",
    edit: "Bewerken",
    close: "Sluiten",
    send: "Verzenden",
    copy: "Kopiëren",
  },
  games: {
    score: "Score",
    best: "Beste",
    hi: "HI",
    tapOn: "Tik op",
    getReady: "Maak je klaar...",
    tapToStart: "Tik om te starten!",
    tapToJump: "Tik om te springen!",
    gameOver: "GAME OVER",
    tapToRestart: "Tik om opnieuw te beginnen",
    speedSlow: "Langzaam",
    speedSteady: "Gestaag",
    speedNormal: "Normaal",
    speedQuick: "Snel",
    speedFast: "Vlot",
    speedBlazing: "Razend",
    speedOnFire: "In vuur!",
    speedRocket: "Raket",
    speedLegendary: "Legendarisch",
    cheers: ["Netjes!", "Geweldig!", "Ongelooflijk!", "Wauw!", "Prachtig!", "Raak!", "Ja!", "Boem!", "Perfect!", "Fantastisch!"],
  },
  errors: {
    microphoneAccessDenied: "Microfoontoegang geweigerd",
    recognitionError: "Spraakherkenningsfout",
    recordingFailed: "Opname starten mislukt",
    noSpeechDetected: "Geen spraak gedetecteerd. Probeer het opnieuw.",
    llmConfigRequired: "Configureer je LLM API-sleutel in instellingen om deze functie te gebruiken.",
  },
  onboarding: {
    // Welcome screen
    welcomeTitle: "Welkom bij SaySay",
    welcomeSubtitle: "Wees voorbereid op elk gesprek",
    getStarted: "Aan de slag",
    // Feature slides
    featureTopicsPain: "Lesboeken passen niet bij jouw leven",
    featureTopicsSolution: "Oefen wat je echt nodig hebt",
    featureTopicsDesc: "Beschrijf elke situatie — koffie bestellen, naar de dokter, kletsen met de buren — en krijg een dialoog op maat.",
    featureVoicePain: "Hoe spreek je dat eigenlijk uit?",
    featureVoiceSolution: "Luister naar elk woord",
    featureVoiceDesc: "Tik op een zin om de uitspraak van een native speaker te horen. Reageer handsfree met je stem.",
    featureAiPain: "One-size-fits-all werkt niet",
    featureAiSolution: "AI bouwt jouw woordenschat",
    featureAiDesc: "Elke dialoog bevat woorden en uitdrukkingen die je echt gaat gebruiken. Leer vocabulaire dat past bij jouw leven.",
    continue: "Doorgaan",
    inputNativeLangTitle: "Welke taal spreek je?",
    inputNativeLangSubtitle: "We geven je les in deze taal",
    inputTargetLangTitle: "Wat wil je leren?",
    inputTargetLangSubtitle: "Kies de taal die je wilt oefenen",
    // Input: Name
    inputNameTitle: "Hoe moeten we je noemen?",
    inputNameSubtitle: "Je kunt dit later wijzigen",
    inputNamePlaceholder: "Jouw naam",
    // Input: Languages
    inputLangTitle: "Spreek je andere talen?",
    inputLangSubtitle: "We gebruiken overeenkomsten om je sneller te laten leren",
    inputLangSelect: "Selecteer talen die je kent naast Nederlands",
    inputLangFindMore: "Meer vinden",
    inputLangSearch: "Zoek een taal...",
    skipStep: "Overslaan",
    // Input: Style
    inputStyleTitle: "Hoe wil je klinken?",
    inputStyleSubtitle: "Je kunt dit later wijzigen",
    speakingStyleBeginner: "Beginner",
    speakingStyleBeginnerDesc: "Eenvoudige woorden, basisfrases — als een leerling",
    speakingStyleLocal: "Als een local",
    speakingStyleLocalDesc: "Natuurlijke uitdrukkingen, slang, idiomen — opgaan in de menigte",
    speakingStyleMix: "Mix stijlen",
    speakingStyleMixDesc: "Kies een of meer stijlen",
    styleYourOption: "Jouw optie...",
    styleTypePlaceholder: "Typ je stijl...",
    // Input: Gender
    inputGenderTitle: "Wie ben je in dialogen?",
    inputGenderSubtitle: "Optioneel — je kunt dit later wijzigen",
    genderMale: "Man",
    genderFemale: "Vrouw",
    genderOther: "Anders / Non-binair",
    genderPreferNot: "Zeg ik liever niet",
    // Navigation
    back: "Terug",
    skipAll: "Alles overslaan",
    // Ready screen
    startLearning: "Begin met leren",
    youreAllSet: "Je bent klaar!",
    youreAllSetWithName: "Je bent klaar, {name}!",
    startFirstConversation: "Begin je eerste gesprek en ontdek hoe natuurlijk taal leren kan zijn.",
    analyticsOptIn: "Help SaySay verbeteren door anonieme gebruiksgegevens te delen (geen persoonlijke info verzameld)",
    // First conversation transition screen
    firstConversationTitle: "Top! Laten we nu je eerste gesprek oefenen.",
    firstConversationSubtitle: "Kies een onderwerp en we genereren een echt dialoog om mee te oefenen.",
    firstConversationButton: "Laten we gaan!",
    // Preview UI elements
    previewTopicTitle: "Waar wil je over praten?",
    previewTopicSubtitle: "Typ een scenario of onderwerp",
    previewExample: "Voorbeeld — tik om te gebruiken",
    previewExampleText: "Koffie bestellen en vragen naar lokale aanbevelingen",
    previewTypeOwn: "Of typ je eigen...",
    previewVoiceText: "Ik wil graag een koffie...",
    previewDialogTranslation: "Goedemorgen! Wat mag het zijn?",
  },
  knownLanguages: {
    spanish: "Spaans",
    french: "Frans",
    german: "Duits",
    italian: "Italiaans",
    portuguese: "Portugees",
    russian: "Russisch",
    chinese: "Chinees",
    japanese: "Japans",
    korean: "Koreaans",
    arabic: "Arabisch",
    hindi: "Hindi",
    dutch: "Nederlands",
    polish: "Pools",
    swedish: "Zweeds",
    turkish: "Turks",
    greek: "Grieks",
    czech: "Tsjechisch",
    romanian: "Roemeens",
    hungarian: "Hongaars",
    finnish: "Fins",
    norwegian: "Noors",
    danish: "Deens",
    ukrainian: "Oekraïens",
    thai: "Thais",
    vietnamese: "Vietnamees",
    indonesian: "Indonesisch",
    malay: "Maleis",
    tagalog: "Tagalog",
    hebrew: "Hebreeuws",
    persian: "Perzisch",
    bengali: "Bengaals",
    punjabi: "Punjabi",
    tamil: "Tamil",
    urdu: "Urdu",
    swahili: "Swahili",
    afrikaans: "Afrikaans",
    catalan: "Catalaans",
    croatian: "Kroatisch",
    serbian: "Servisch",
    slovak: "Slowaaks",
    slovenian: "Sloveens",
    bulgarian: "Bulgaars",
    lithuanian: "Litouws",
    latvian: "Lets",
    estonian: "Ests",
    icelandic: "IJslands",
    welsh: "Welsh",
    irish: "Iers",
    scottishGaelic: "Schots-Gaelisch",
    basque: "Baskisch",
    maltese: "Maltees",
    albanian: "Albanees",
    macedonian: "Macedonisch",
    georgian: "Georgisch",
    armenian: "Armeens",
    kazakh: "Kazachs",
    uzbek: "Oezbeeks",
    mongolian: "Mongools",
    nepali: "Nepalees",
    sinhala: "Singalees",
    burmese: "Birmaans",
    khmer: "Khmer",
    lao: "Laotiaans",
  },
  coachMarks: {
    startNodeTitle: "Start het gesprek",
    startNodeBody: "Tik op deze berichtballon om het dialoog te starten.",
    speakerTitle: "Luister naar de uitspraak",
    speakerBody: "Tik op het luidsprekericoon om de zin hardop te horen.",
    answerTitle: "Kies je antwoord",
    answerBody: "Tik op een van de opties om te kiezen wat je wilt zeggen.",
    tabsTitle: "Ontdek meer",
    tabsBody: "Wissel tussen het dialoog, oefeningen en woordenschat.",
    skip: "Overslaan",
    next: "Volgende",
    done: "Klaar",
  },
  passwordGate: {
    enterPassword: "Voer wachtwoord in om door te gaan",
    placeholder: "Wachtwoord",
  },
  dialogueFeedback: {
    title: "Wat vind je van deze dialoog?",
    bad: "Slecht",
    meh: "Matig",
    good: "Goed",
    thanks: "Bedankt voor je feedback!",
    tellUsWhy: "Bedankt! Kun je ons vertellen waarom?",
    commentPlaceholder: "Wat kan er beter?",
    skip: "Overslaan",
    send: "Versturen",
  },
  dialog: {
    listening: "Ik luister naar je...",
    stopRecording: "Stop opname",
    practice: "Practice",
    vocabulary: "Vocabulary",
    loadingVocabulary: "Loading vocabulary...",
  },
  languageNames: {
    "en-US": "Engels (VS)",
    "en-GB": "Engels (VK)",
    "es-ES": "Spaans (Spanje)",
    "es-MX": "Spaans (Mexico)",
    "pt-PT": "Portugees (Portugal)",
    "pt-BR": "Portugees (Brazilië)",
    "fr-FR": "Frans",
    "de-DE": "Duits",
    "de-AT": "Duits (Oostenrijk)",
    "de-CH": "Duits (Zwitserland)",
    "it-IT": "Italiaans",
    "ja-JP": "Japans",
    "zh-CN": "Chinees",
    "ko-KR": "Koreaans",
    "he-IL": "Hebreeuws",
    "ru-RU": "Russisch",
    "pl-PL": "Pools",
    "uk-UA": "Oekraïens",
    "fi-FI": "Fins",
    "et-EE": "Ests",
    "lv-LV": "Lets",
    "el-GR": "Grieks",
  },
};

// All translations map
const translations: Record<string, Translations> = {
  en,
  es,
  pt,
  fr,
  de,
  it,
  ru,
  ja,
  ko,
  zh,
  he,
  pl,
  uk,
  ar,
  nl,
};

// Get base language code
function getBaseCode(code: string): string {
  return code.split("-")[0]!;
}

// Get translations for a language code
export function getTranslations(langCode: string): Translations {
  const baseCode = getBaseCode(langCode);
  return translations[baseCode] || en;
}

// Helper to interpolate variables in strings
export function t(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? `{${key}}`));
}
